import React, { useState, useEffect, useLayoutEffect, useRef, useContext, useCallback, useMemo } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner, Form, Dropdown, Badge, Placeholder, Modal, OverlayTrigger, Tooltip, Collapse, ButtonGroup } from 'react-bootstrap';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getAccount, getTransactions, updateTickerPrices, getTickers, getAccountTickers, updateAccount, getAllTransactions, getTickerShields, recalculateAccountShields, getTrades, getAllAccountsWithAggregate } from '../services/api';
import { subscribeToTickerUpdates, subscribeToAccountTickerUpdates } from '../services/socketService';
import { useTickerUpdates } from '../hooks/useTickerUpdates';
import { useStableCallback } from '../hooks/useStableCallback';
import tickerPollingService from '../services/tickerPollingService';
import { ThemeContext } from '../contexts/ThemeContext';
import TickerTable from '../components/TickerTable.js';
import ColumnVisibilityDropdown from '../components/ColumnVisibilityDropdown';
import TransactionModal from '../components/TransactionModal';
import PieChartModal from '../components/PieChartModal';
import TreeMapContainer from '../components/TreeMapContainer';
import BalanceHistoryModal from '../components/BalanceHistoryModal';
import PerformanceChartModal from '../components/PerformanceChartModal';
import StandardSizeModal from '../components/StandardSizeModal';
import EdgeHistoryModal from '../components/EdgeHistoryModal';
import TickerTransactionHistoryModal from '../components/TickerTransactionHistoryModal';
import PlanningModal from '../components/PlanningModal';
import { formatBalanceDisplay, normalizeNearZero } from '../utils/formatBalance';
import { FaSyncAlt } from 'react-icons/fa';
import OrderDialog from '../components/OrderDialog';
import { createOrder, getAccountOrders, updateOrder, commitOrder, deleteOrder, fulfillOrder, getTradingStatistics, addTickerNote, createTransaction } from '../services/api';
import { getCurrentLocalDate, formatISODate, parseDateForSort } from '../utils/dateUtils';
import { initializeColumnConfig, saveAsDefaultColumnConfig, getDefaultColumnConfig } from '../utils/columnPreferences';
import { createLogger } from '../utils/debugLogger';
import { showStrategyPresetModal } from '../utils/strategyPresetModalManager';
import { applyThemeOverridesToTickers } from '../utils/themeOverrides';
import { applyBaseOverridesToTickers } from '../utils/baseOverrides';
import { isAccountTickersRefreshRequired, clearAccountTickersRefreshFlag } from '../utils/accountTickerRefreshFlag';

const logger = createLogger(
  'accountDetailsPage',
  'Account details page ticker refreshes and trade diagnostics',
  false,
  'DEBUG'
);

const MIN_POSITION_SHARES = 0.0001;
const DEFAULT_TRADING_STATS_MONTHS = 3;
const NEXT_PLAN_OVERRIDES_KEY_PREFIX = 'accountNextPlanOverrides';
const NEXT_PLAN_OVERRIDE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

// Static -- hoisted so it doesn't defeat TickerTable's React.memo with a new
// object reference every render (FRONTEND-005).
const TICKER_TABLE_COLUMN_WIDTH_OVERRIDES = { theme: '40px', base: '40px' };

const getNextPlanOverridesStorageKey = (accountId) => {
  if (accountId === undefined || accountId === null) {
    return null;
  }
  return `${NEXT_PLAN_OVERRIDES_KEY_PREFIX}-${accountId}`;
};

const buildNextPlanOverrideKeys = (tickerId, symbol) => {
  const keys = [];
  if (tickerId !== undefined && tickerId !== null) {
    keys.push(String(tickerId));
  }
  if (symbol) {
    keys.push(`symbol:${symbol}`);
  }
  return keys;
};

const ACCOUNT_DETAIL_COLUMNS = [
  'symbol',
  'strategy',
  'stop',
  'actions',
  'notes',
  'name',
  'rating',
  'entry',
  'tsize',
  'next',
  'source',
  'category',
  'theme',
  'base',
  'shares',
  'cost_basis',
  'total_cost',
  'current_price',
  'pre_close',
  'current_value',
  'high',
  'today_gain_amount',
  'today_gain_percentage',
  'total_gain',
  'total_gain_pct',
  'unrealized_gain',
  'trade_gain',
  'trade_gain_pct',
  'days_held',
  'position_size',
  'armor',
  'risk_gain',
  'trisk_gain',
  'risk_gain_amount'
];

const ACCOUNT_DETAIL_TOGGLE_COLUMNS = ACCOUNT_DETAIL_COLUMNS.filter(column => column !== 'symbol');

const ACCOUNT_COLUMN_LABELS = {
  cost_basis: 'Cost',
  total_cost: 'TCost',
  current_price: 'Price',
  current_value: 'Value',
  pre_close: 'PClose',
  days_held: 'D',
  today_gain_amount: 'Tdy$',
  total_gain: 'TGain',
  total_gain_pct: 'TGain%',
  unrealized_gain: 'Unrlzd$',
  trade_gain: 'Trade$',
  trade_gain_pct: 'Trade%',
  entry: 'Entry',
  position_size: 'Size',
  risk_gain: 'Risk%',
  trisk_gain: 'TRisk%',
  risk_gain_amount: 'Risk$',
  today_gain_percentage: 'Tdy%',
  theme: 'Thm',
  base: 'Base'
};

const DEFAULT_ACCOUNT_VISIBLE_COLUMNS = {
  strategy: true,
  stop: true,
  notes: true,
  rating: true,
  entry: true,
  tsize: true,
  next: true,
  source: false,
  name: false,
  category: false,
  shares: true,
  cost_basis: false,
  total_cost: false,
  pre_close: false,
  current_price: false,
  current_value: true,
  high: true,
  today_gain_amount: false,
  today_gain_percentage: true,
  total_gain: false,
  total_gain_pct: true,
  unrealized_gain: true,
  trade_gain: true,
  trade_gain_pct: true,
  days_held: true,
  position_size: true,
  armor: true,
  theme: true,
  base: true,
  risk_gain: true,
  trisk_gain: true,
  risk_gain_amount: true,
  actions: true
};

const getDefaultVisibleColumns = () => ({ ...DEFAULT_ACCOUNT_VISIBLE_COLUMNS });
const getDefaultColumnOrder = () => [...ACCOUNT_DETAIL_COLUMNS];

const alignCategoryDependentColumns = (order = []) => {
  if (!Array.isArray(order)) {
    return [];
  }

  const nextOrder = order.filter(column => typeof column === 'string');
  const repositionColumn = (column, anchor) => {
    const columnIndex = nextOrder.indexOf(column);
    if (columnIndex === -1) {
      return;
    }

    const [columnValue] = nextOrder.splice(columnIndex, 1);
    const anchorIndex = nextOrder.indexOf(anchor);

    if (anchorIndex === -1) {
      nextOrder.push(columnValue);
      return;
    }

    nextOrder.splice(anchorIndex + 1, 0, columnValue);
  };

  repositionColumn('theme', 'category');
  repositionColumn('base', nextOrder.includes('theme') ? 'theme' : 'category');

  return nextOrder;
};

const normalizeAccountVisibleColumns = (columns = {}) => {
  const normalized = {};
  ACCOUNT_DETAIL_TOGGLE_COLUMNS.forEach(column => {
    if (typeof columns[column] === 'boolean') {
      normalized[column] = columns[column];
    } else {
      normalized[column] = DEFAULT_ACCOUNT_VISIBLE_COLUMNS[column] ?? false;
    }
  });
  return normalized;
};

const normalizeAccountColumnOrder = (order = []) => {
  const normalized = [];
  const seen = new Set();

  if (Array.isArray(order)) {
    order.forEach(column => {
      if (ACCOUNT_DETAIL_COLUMNS.includes(column) && !seen.has(column)) {
        normalized.push(column);
        seen.add(column);
      }
    });
  }

  ACCOUNT_DETAIL_COLUMNS.forEach(column => {
    if (!seen.has(column)) {
      normalized.push(column);
    }
  });

  return alignCategoryDependentColumns(normalized);
};

const formatAccountColumnLabel = (column) => {
  if (ACCOUNT_COLUMN_LABELS[column]) {
    return ACCOUNT_COLUMN_LABELS[column];
  }
  return column
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Custom styles for the component
const customStyles = `
    /* Enhanced card styling for maximum contrast and prominence */
    .account-details-card {
        background-color: var(--card-bg) !important;
        border: 2px solid var(--border-color) !important;
        border-radius: 8px !important;
        box-shadow: 0 4px 16px var(--shadow-color) !important;
        transition: all 0.3s ease-in-out !important;
    }
    
    .account-details-card:hover {
        transform: translateY(-2px) !important;
        box-shadow: 0 8px 24px var(--shadow-color) !important;
    }
    
    [data-theme="light"] .account-details-card {
        background-color: #ffffff !important;
        border-color: #d1d5db !important;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1) !important;
    }
    
    [data-theme="light"] .account-details-card:hover {
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
    }
    
    [data-theme="dark"] .account-details-card {
        background-color: #1f2937 !important;
        border-color: #4b5563 !important;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3) !important;
    }
    
    [data-theme="dark"] .account-details-card:hover {
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4) !important;
    }

    /* Column dropdown toggle enhancements */
    .column-toggle-switch .form-check-input {
        width: 2.25em !important;
        height: 1.25em !important;
        margin-right: 0.4rem;
        cursor: pointer;
        transition: all 0.2s ease-in-out;
    }

    .column-toggle-switch .form-check-input:checked {
        background-color: #0d6efd !important;
        border-color: #0d6efd !important;
        box-shadow: 0 0 6px rgba(13, 110, 253, 0.65) !important;
    }

    .column-toggle-switch .form-check-input:focus {
        border-color: #0d6efd !important;
        box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25) !important;
    }

    /* Enhanced table container styling */
    .account-tickers-table-container {
        background-color: var(--card-bg) !important;
        border: 2px solid var(--border-color) !important;
        border-radius: 8px !important;
        box-shadow: 0 4px 16px var(--shadow-color) !important;
        overflow: hidden !important;
        margin-bottom: 1rem !important;
    }
    
    [data-theme="light"] .account-tickers-table-container {
        background-color: #ffffff !important;
        border-color: #d1d5db !important;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1) !important;
    }
    
    [data-theme="dark"] .account-tickers-table-container {
        background-color: #1f2937 !important;
        border-color: #4b5563 !important;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3) !important;
    }
    
    /* Enhanced table styling */
    .account-tickers-table {
        background-color: var(--card-bg) !important;
        border: none !important;
        margin-bottom: 0 !important;
    }
    
    /* Remove gap between last row and bottom of table container */
    .account-tickers-table-container .table {
        margin-bottom: 0 !important;
    }
    
    .account-tickers-table-container .ticker-table {
        margin-bottom: 0 !important;
    }
    
    /* Enhanced table header styling - override TickerTable.css */
    .ticker-table.account-tickers-table thead th,
    .account-tickers-table thead th,
    .account-tickers-table-header {
        background-color: var(--bg-secondary) !important;
        border-bottom: 2px solid var(--border-color) !important;
        font-weight: 600 !important;
        color: var(--text-primary) !important;
        padding: 6px 6px !important;
        transition: background-color 0.2s ease-in-out;
    }
    
    .ticker-table.account-tickers-table thead th:hover,
    .account-tickers-table thead th:hover,
    .account-tickers-table-header:hover {
        background-color: var(--hover-bg) !important;
        cursor: pointer;
    }
    
    [data-theme="light"] .ticker-table.account-tickers-table thead th,
    [data-theme="light"] .account-tickers-table thead th,
    [data-theme="light"] .account-tickers-table-header {
        background-color: #f8f9fa !important;
        border-bottom: 2px solid #d1d5db !important;
        color: #374151 !important;
    }
    
    [data-theme="light"] .ticker-table.account-tickers-table thead th:hover,
    [data-theme="light"] .account-tickers-table thead th:hover,
    [data-theme="light"] .account-tickers-table-header:hover {
        background-color: #e9ecef !important;
    }
    
    [data-theme="dark"] .ticker-table.account-tickers-table thead th,
    [data-theme="dark"] .account-tickers-table thead th,
    [data-theme="dark"] .account-tickers-table-header {
        background-color: #374151 !important;
        border-bottom: 2px solid #4b5563 !important;
        color: #f9fafb !important;
    }
    
    [data-theme="dark"] .ticker-table.account-tickers-table thead th:hover,
    [data-theme="dark"] .account-tickers-table thead th:hover,
    [data-theme="dark"] .account-tickers-table-header:hover {
        background-color: #4b5563 !important;
    }
    
    
    /* Enhanced table row styling - more specific selectors to override TickerTable styles */
    .account-tickers-table-container .ticker-table.tickers-table tbody tr,
    .account-tickers-table.ticker-table.tickers-table tbody tr {
        border-bottom: 1px solid var(--border-color) !important;
        transition: all 0.3s ease-in-out !important;
        font-size: 0.95rem !important;
    }
    
    .account-tickers-table-container .ticker-table.tickers-table tbody tr:hover,
    .account-tickers-table.ticker-table.tickers-table tbody tr:hover {}
    
    [data-theme="light"] .account-tickers-table-container .ticker-table.tickers-table tbody tr:hover,
    [data-theme="light"] .account-tickers-table.ticker-table.tickers-table tbody tr:hover {}
    
    [data-theme="dark"] .account-tickers-table-container .ticker-table.tickers-table tbody tr:hover,
    [data-theme="dark"] .account-tickers-table.ticker-table.tickers-table tbody tr:hover {}
    
    /* Enhanced table cell styling - more specific selectors to override TickerTable styles */
    .account-tickers-table-container .ticker-table.tickers-table tbody td,
    .account-tickers-table.ticker-table.tickers-table tbody td {
        padding: 4px 6px !important;
        border-bottom: 1px solid var(--border-color) !important;
        vertical-align: middle !important;
        transition: all 0.3s ease-in-out !important;
    }
    
    .account-tickers-table-container .ticker-table.tickers-table tbody tr:hover td,
    .account-tickers-table.ticker-table.tickers-table tbody tr:hover td {}
    
    [data-theme="light"] .account-tickers-table-container .ticker-table.tickers-table tbody td,
    [data-theme="light"] .account-tickers-table.ticker-table.tickers-table tbody td {
        border-bottom: 1px solid #e5e7eb !important;
    }
    
    [data-theme="light"] .account-tickers-table-container .ticker-table.tickers-table tbody tr:hover td,
    [data-theme="light"] .account-tickers-table.ticker-table.tickers-table tbody tr:hover td {}
    
    [data-theme="dark"] .account-tickers-table-container .ticker-table.tickers-table tbody td,
    [data-theme="dark"] .account-tickers-table.ticker-table.tickers-table tbody td {
        border-bottom: 1px solid #374151 !important;
    }
    
    [data-theme="dark"] .account-tickers-table-container .ticker-table.tickers-table tbody tr:hover td,
    [data-theme="dark"] .account-tickers-table.ticker-table.tickers-table tbody tr:hover td {}
    
    /* CSS Variables for theme support */
    :root {
        --card-bg: #ffffff;
        --border-color: #d1d5db;
        --shadow-color: rgba(0, 0, 0, 0.1);
        --text-primary: #374151;
        --bg-secondary: #f8f9fa;
        --hover-bg: #f1f5f9;
    }
    
    [data-theme="dark"] {
        --card-bg: #1f2937;
        --border-color: #4b5563;
        --shadow-color: rgba(0, 0, 0, 0.3);
        --text-primary: #f9fafb;
        --bg-secondary: #374151;
        --hover-bg: #4b5563;
    }

    /* Simple spin animation for refresh icon */
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    .spin {
      animation: spin 1s linear infinite;
      display: inline-block;
    }
    
    /* Input field styling with weather theme support */
    .form-control,
    .form-select,
    input,
    textarea {
      background-color: var(--input-bg, #ffffff) !important;
      border-color: var(--border-color, #ced4da) !important;
      color: var(--input-text, #495057) !important;
      transition: background-color 0.3s ease, border-color 0.3s ease;
    }
    
    .form-control:focus,
    .form-select:focus,
    input:focus,
    textarea:focus {
      background-color: var(--input-bg, #ffffff) !important;
      border-color: #86b7fe !important;
      color: var(--input-text, #495057) !important;
      box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
    }

    /* Weather theme input backgrounds - Light mode */
    [data-weather-theme="clear"] .form-control,
    [data-weather-theme="clear"] .form-select,
    [data-weather-theme="clear"] input,
    [data-weather-theme="clear"] textarea,
    [data-weather-theme="clear"] select {
      background-color: rgba(255, 248, 220, 0.9) !important;
      border-color: rgba(243, 156, 18, 0.3) !important;
      color: #8b4513 !important;
    }

    [data-weather-theme="clouds"] .form-control,
    [data-weather-theme="clouds"] .form-select,
    [data-weather-theme="clouds"] input,
    [data-weather-theme="clouds"] textarea,
    [data-weather-theme="clouds"] select {
      background-color: rgba(232, 232, 232, 0.9) !important;
      border-color: rgba(149, 165, 166, 0.3) !important;
      color: #555555 !important;
    }

    [data-weather-theme="rain"] .form-control,
    [data-weather-theme="rain"] .form-select,
    [data-weather-theme="rain"] input,
    [data-weather-theme="rain"] textarea,
    [data-weather-theme="rain"] select {
      background-color: rgba(224, 242, 241, 0.9) !important;
      border-color: rgba(38, 166, 154, 0.3) !important;
      color: #00695c !important;
    }

    [data-weather-theme="snow"] .form-control,
    [data-weather-theme="snow"] .form-select,
    [data-weather-theme="snow"] input,
    [data-weather-theme="snow"] textarea,
    [data-weather-theme="snow"] select {
      background-color: rgba(240, 248, 255, 0.9) !important;
      border-color: rgba(100, 181, 246, 0.3) !important;
      color: #1565c0 !important;
    }

    [data-weather-theme="thunderstorm"] .form-control,
    [data-weather-theme="thunderstorm"] .form-select,
    [data-weather-theme="thunderstorm"] input,
    [data-weather-theme="thunderstorm"] textarea,
    [data-weather-theme="thunderstorm"] select {
      background-color: rgba(243, 229, 245, 0.9) !important;
      border-color: rgba(171, 71, 188, 0.3) !important;
      color: #6a1b9a !important;
    }

    [data-weather-theme="spring"] .form-control,
    [data-weather-theme="spring"] .form-select,
    [data-weather-theme="spring"] input,
    [data-weather-theme="spring"] textarea,
    [data-weather-theme="spring"] select {
      background-color: rgba(232, 245, 232, 0.9) !important;
      border-color: rgba(76, 175, 80, 0.3) !important;
      color: #2e7d32 !important;
    }

    [data-weather-theme="summer"] .form-control,
    [data-weather-theme="summer"] .form-select,
    [data-weather-theme="summer"] input,
    [data-weather-theme="summer"] textarea,
    [data-weather-theme="summer"] select {
      background-color: rgba(224, 247, 250, 0.9) !important;
      border-color: rgba(0, 172, 193, 0.3) !important;
      color: #006064 !important;
    }

    [data-weather-theme="autumn"] .form-control,
    [data-weather-theme="autumn"] .form-select,
    [data-weather-theme="autumn"] input,
    [data-weather-theme="autumn"] textarea,
    [data-weather-theme="autumn"] select {
      background-color: rgba(251, 233, 231, 0.9) !important;
      border-color: rgba(255, 152, 0, 0.3) !important;
      color: #e65100 !important;
    }

    [data-weather-theme="winter"] .form-control,
    [data-weather-theme="winter"] .form-select,
    [data-weather-theme="winter"] input,
    [data-weather-theme="winter"] textarea,
    [data-weather-theme="winter"] select {
      background-color: rgba(232, 234, 246, 0.9) !important;
      border-color: rgba(121, 134, 203, 0.3) !important;
      color: #303f9f !important;
    }

    /* Dark mode weather theme inputs */
    [data-theme="dark"][data-weather-theme="clear"] .form-control,
    [data-theme="dark"][data-weather-theme="clear"] .form-select,
    [data-theme="dark"][data-weather-theme="clear"] input,
    [data-theme="dark"][data-weather-theme="clear"] textarea {
      background-color: rgba(26, 26, 10, 0.8) !important;
      border-color: rgba(255, 255, 255, 0.2) !important;
      color: #f5f5dc !important;
    }

    [data-theme="dark"][data-weather-theme="spring"] .form-control,
    [data-theme="dark"][data-weather-theme="spring"] .form-select,
    [data-theme="dark"][data-weather-theme="spring"] input,
    [data-theme="dark"][data-weather-theme="spring"] textarea {
      background-color: rgba(13, 46, 13, 0.8) !important;
      border-color: rgba(255, 255, 255, 0.2) !important;
      color: #e8f5e8 !important;
    }

    [data-theme="dark"][data-weather-theme="summer"] .form-control,
    [data-theme="dark"][data-weather-theme="summer"] .form-select,
    [data-theme="dark"][data-weather-theme="summer"] input,
    [data-theme="dark"][data-weather-theme="summer"] textarea {
      background-color: rgba(13, 46, 46, 0.8) !important;
      border-color: rgba(255, 255, 255, 0.2) !important;
      color: #e0f7fa !important;
    }

    [data-theme="dark"][data-weather-theme="autumn"] .form-control,
    [data-theme="dark"][data-weather-theme="autumn"] .form-select,
    [data-theme="dark"][data-weather-theme="autumn"] input,
    [data-theme="dark"][data-weather-theme="autumn"] textarea {
      background-color: rgba(46, 29, 13, 0.8) !important;
      border-color: rgba(255, 255, 255, 0.2) !important;
      color: #fbe9e7 !important;
    }

    [data-theme="dark"][data-weather-theme="winter"] .form-control,
    [data-theme="dark"][data-weather-theme="winter"] .form-select,
    [data-theme="dark"][data-weather-theme="winter"] input,
    [data-theme="dark"][data-weather-theme="winter"] textarea {
      background-color: rgba(13, 13, 46, 0.8) !important;
      border-color: rgba(255, 255, 255, 0.2) !important;
      color: #e8eaf6 !important;
    }

    /* Standard dark mode (no weather theme) */
    [data-theme="dark"] .form-control,
    [data-theme="dark"] .form-select,
    [data-theme="dark"] input,
    [data-theme="dark"] textarea {
      background-color: var(--input-bg, #2a2a2a) !important;
      border-color: var(--border-color, #4a5568) !important;
      color: var(--input-text, #d0d0d0) !important;
    }
    
    [data-theme="dark"] .form-control:focus,
    [data-theme="dark"] .form-select:focus,
    [data-theme="dark"] input:focus,
    [data-theme="dark"] textarea:focus {
      background-color: var(--input-bg, #2a2a2a) !important;
      border-color: #6ea8fe !important;
      color: var(--input-text, #d0d0d0) !important;
      box-shadow: 0 0 0 0.25rem rgba(110, 168, 254, 0.25);
    }

    /* Weather theme styling for Form.Select option elements */
    /* Light mode weather themes for select options */
    [data-weather-theme="clear"] .form-select option {
      background-color: rgba(255, 248, 220, 0.95) !important;
      color: #8b4513 !important;
    }

    [data-weather-theme="clouds"] .form-select option {
      background-color: rgba(232, 232, 232, 0.95) !important;
      color: #555555 !important;
    }

    [data-weather-theme="rain"] .form-select option {
      background-color: rgba(224, 242, 241, 0.95) !important;
      color: #00695c !important;
    }

    [data-weather-theme="snow"] .form-select option {
      background-color: rgba(240, 248, 255, 0.95) !important;
      color: #1565c0 !important;
    }

    [data-weather-theme="thunderstorm"] .form-select option {
      background-color: rgba(243, 229, 245, 0.95) !important;
      color: #6a1b9a !important;
    }

    [data-weather-theme="spring"] .form-select option {
      background-color: rgba(232, 245, 232, 0.95) !important;
      color: #2e7d32 !important;
    }

    [data-weather-theme="summer"] .form-select option {
      background-color: rgba(224, 247, 250, 0.95) !important;
      color: #006064 !important;
    }

    [data-weather-theme="autumn"] .form-select option {
      background-color: rgba(251, 233, 231, 0.95) !important;
      color: #e65100 !important;
    }

    [data-weather-theme="winter"] .form-select option {
      background-color: rgba(232, 234, 246, 0.95) !important;
      color: #303f9f !important;
    }

    /* Dark mode weather themes for select options */
    [data-theme="dark"][data-weather-theme="clear"] .form-select option {
      background-color: rgba(26, 26, 10, 0.9) !important;
      color: #f5f5dc !important;
    }

    [data-theme="dark"][data-weather-theme="spring"] .form-select option {
      background-color: rgba(13, 46, 13, 0.9) !important;
      color: #e8f5e8 !important;
    }

    [data-theme="dark"][data-weather-theme="summer"] .form-select option {
      background-color: rgba(13, 46, 46, 0.9) !important;
      color: #e0f7fa !important;
    }

    [data-theme="dark"][data-weather-theme="autumn"] .form-select option {
      background-color: rgba(46, 29, 13, 0.9) !important;
      color: #fbe9e7 !important;
    }

    [data-theme="dark"][data-weather-theme="winter"] .form-select option {
      background-color: rgba(13, 13, 46, 0.9) !important;
      color: #e8eaf6 !important;
    }

    [data-theme="dark"][data-weather-theme="clouds"] .form-select option {
      background-color: rgba(26, 26, 26, 0.9) !important;
      color: #f0f0f0 !important;
    }

    [data-theme="dark"][data-weather-theme="rain"] .form-select option {
      background-color: rgba(10, 26, 42, 0.9) !important;
      color: #e0f2f1 !important;
    }

    [data-theme="dark"][data-weather-theme="snow"] .form-select option {
      background-color: rgba(26, 26, 42, 0.9) !important;
      color: #f0f8ff !important;
    }

    [data-theme="dark"][data-weather-theme="thunderstorm"] .form-select option {
      background-color: rgba(10, 10, 26, 0.9) !important;
      color: #f3e5f5 !important;
    }
`;

function AccountDetails() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { weatherThemeEnabled, selectedWeatherTheme, weatherCondition, effectiveTheme } = useContext(ThemeContext);
  
  // Get weather theme colors based on current weather/seasonal theme
  const getWeatherThemeColors = () => {
    // Determine which theme to use
    const currentTheme = weatherThemeEnabled 
      ? (selectedWeatherTheme || weatherCondition || 'summer')
      : 'summer'; // Default fallback
    
    // Check if we're in dark mode for muted colors
    const isDarkMode = effectiveTheme === 'dark';
    
    // Map weather themes to header colors
    switch (currentTheme) {
      case 'spring':
        return isDarkMode ? {
          background: '#1a2e1a', // Much darker green
          border: '#2e4a2e',     // Darker green border  
          text: '#8bc34a'        // Muted green text
        } : {
          background: '#d4ebd4', // Light green
          border: '#4caf50',     // Green border  
          text: '#2e7d32'        // Dark green text
        };
      case 'summer':
        return isDarkMode ? {
          background: '#1a2e2e', // Much darker teal
          border: '#2e4a4a',     // Darker teal border
          text: '#4dd0e1'        // Muted cyan text
        } : {
          background: '#b2ebf2', // Light teal/cyan
          border: '#00acc1',     // Teal border
          text: '#00695c'        // Dark teal text
        };
      case 'autumn':
        return isDarkMode ? {
          background: '#2e1e13', // Much darker brown
          border: '#4a3426',     // Darker orange-brown border
          text: '#ff9800'        // Muted orange text
        } : {
          background: '#ffccbc', // Light orange
          border: '#ff9800',     // Orange border
          text: '#e65100'        // Dark orange text
        };
      case 'winter':
        return isDarkMode ? {
          background: '#1a1a2e', // Much darker blue
          border: '#2e2e4a',     // Darker blue border
          text: '#7986cb'        // Muted blue text
        } : {
          background: '#c5cae9', // Light blue/purple
          border: '#7986cb',     // Blue-purple border
          text: '#303f9f'        // Dark blue text
        };
      case 'clear':
        return isDarkMode ? {
          background: '#2e2e1a', // Much darker yellow-brown
          border: '#4a4a2e',     // Darker yellow border
          text: '#ffb74d'        // Muted yellow text
        } : {
          background: '#ffeaa7', // Light yellow
          border: '#f39c12',     // Yellow-orange border
          text: '#d68910'        // Dark yellow text
        };
      case 'clouds':
        return isDarkMode ? {
          background: '#262626', // Much darker gray
          border: '#3a3a3a',     // Darker gray border
          text: '#9e9e9e'        // Muted gray text
        } : {
          background: '#d0d0d0', // Light gray
          border: '#95a5a6',     // Gray border
          text: '#5d6d7e'        // Dark gray text
        };
      case 'rain':
        return isDarkMode ? {
          background: '#132e2a', // Much darker blue-green
          border: '#264a42',     // Darker teal border
          text: '#4db6ac'        // Muted teal text
        } : {
          background: '#b2dfdb', // Light blue-green
          border: '#26a69a',     // Teal border
          text: '#00695c'        // Dark teal text
        };
      case 'snow':
        return isDarkMode ? {
          background: '#1a2633', // Much darker ice blue
          border: '#2e3d4a',     // Darker blue border
          text: '#81c784'        // Muted light blue text
        } : {
          background: '#e6f3ff', // Very light blue
          border: '#64b5f6',     // Light blue border
          text: '#1976d2'        // Blue text
        };
      case 'thunderstorm':
        return isDarkMode ? {
          background: '#1e1a2e', // Much darker purple
          border: '#332e4a',     // Darker purple border
          text: '#ba68c8'        // Muted purple text
        } : {
          background: '#e1bee7', // Light purple
          border: '#ab47bc',     // Purple border
          text: '#6a1b9a'        // Dark purple text
        };
      default:
        return isDarkMode ? {
          background: '#1a2e2e', // Default to dark summer
          border: '#2e4a4a',
          text: '#4dd0e1'
        } : {
          background: '#b2ebf2', // Default to light summer
          border: '#00acc1',
          text: '#00695c'
        };
    }
  };
  
  // Get weather theme colors for use in styles
  const weatherColors = getWeatherThemeColors();
  
  
  const [account, setAccount] = useState(null);
  // Server-computed totals for this account from the same
  // getAllAccountsWithAggregate() endpoint NavigationPanel uses -- kept
  // separate from `account` (rather than merged in) so this doesn't get
  // confused with getAccount()'s own today_gain_amount, which uses a
  // different, inconsistent formula (see getTodayTotalGain below). Refreshed
  // on the same poll-tick trigger NavigationPanel reacts to, so the stats
  // cards get the same atomic-snapshot freshness guarantee the nav panel
  // has, instead of drifting from the incrementally-merged `tickers` array.
  const [accountAggregate, setAccountAggregate] = useState(null);
  const [tickers, setTickers] = useState([]);
  // Tracks which account the `tickers` array above actually belongs to. The
  // effect below (search "Reset state when switching accounts") also clears
  // `tickers` on account switch, but effects run *after* commit -- for one
  // paint, this render still shows the outgoing account's `tickers` state
  // together with the new account's `key={accountId}` on <TickerTable>,
  // which forces a remount that mounts fresh using that same stale array.
  // Resetting here, during render, is React's documented pattern for
  // "adjusting state when a prop changes" (see useState docs): it lets us
  // notice the mismatch and clear `tickers` in the same render pass, before
  // anything is ever painted, instead of one commit late.
  const [tickersAccountId, setTickersAccountId] = useState(accountId);
  if (accountId !== tickersAccountId) {
    setTickersAccountId(accountId);
    setTickers([]);
  }
  const [, setTransactions] = useState([]);
  const [postAnalysisTodoCount, setPostAnalysisTodoCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showCashModal, setShowCashModal] = useState(false);
  const [showPieChartModal, setShowPieChartModal] = useState(false);
  const treemapVisibilityKey = accountId ? `treemapShowAccountDetails-${accountId}` : 'treemapShowAccountDetails';
  const treemapPageKey = accountId ? `accountDetails-${accountId}` : 'accountDetails';
  const [showTreeMapModal, setShowTreeMapModal] = useState(() => {
    const stored = localStorage.getItem(treemapVisibilityKey);
    return stored === 'true';
  });
  const [accountExpectancy, setAccountExpectancy] = useState(null);
  const [accountExpectancyWeighted, setAccountExpectancyWeighted] = useState(null);
  const [tradingStats, setTradingStats] = useState(null);
  const [showBalanceHistoryModal, setShowBalanceHistoryModal] = useState(false);
  const [showPerformanceModal, setShowPerformanceModal] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [availableTickers, setAvailableTickers] = useState([]);
  const [showStandardSize, setShowStandardSize] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(() => {
    const savedInterval = localStorage.getItem('refreshInterval');
    return savedInterval ? Number(savedInterval) : 60;
  });
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(() => {
    const savedSetting = localStorage.getItem('autoRefreshEnabled');
    return savedSetting !== null ? JSON.parse(savedSetting) : true;
  });
  useEffect(() => {
    const stored = localStorage.getItem(treemapVisibilityKey);
    setShowTreeMapModal(stored === 'true');
  }, [treemapVisibilityKey]);

  useEffect(() => {
    localStorage.setItem(treemapVisibilityKey, showTreeMapModal ? 'true' : 'false');
  }, [showTreeMapModal, treemapVisibilityKey]);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [marketInfo, setMarketInfo] = useState(null);
  const [historySaveMessage, setHistorySaveMessage] = useState('');
  const [transactionInitialData, setTransactionInitialData] = useState(null);
  const [standardSizeSuggestion, setStandardSizeSuggestion] = useState(false);
  const [showTickerHistoryModal, setShowTickerHistoryModal] = useState(false);
  const [selectedTickerSymbol, setSelectedTickerSymbol] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [orderDialogInitial, setOrderDialogInitial] = useState(null);
  const [orderDialogLoading, setOrderDialogLoading] = useState(false);
  const [orderDialogTicker, setOrderDialogTicker] = useState(null);
  const [orderDialogError, setOrderDialogError] = useState('');
  const [quickTradeStatus, setQuickTradeStatus] = useState(null);
  const [quickTradeState, setQuickTradeState] = useState(null);
  
  // Throttle account summary refreshes to avoid excessive API calls
  const lastAccountRefreshRef = useRef(0);
  const latestDetailsRequestRef = useRef(0);
  const latestTransactionsRequestRef = useRef(0);
  const tickerSnapshotRequestCounterRef = useRef(0);
  const latestTickerSnapshotAppliedRef = useRef(0);
  const currentAccountIdRef = useRef(accountId);
  const tickersAccountIdRef = useRef(accountId);
  const tickersRef = useRef([]);
  const RECENT_TICKER_REMOVAL_WINDOW_MS = 120000;
  // How long we retain a removal record purely for bookkeeping (map GC), decoupled
  // from RECENT_TICKER_REMOVAL_WINDOW_MS above. That 120s window is one of several
  // signals wasTickerRecentlyRemoved uses to decide staleness -- the causal
  // (request-id) check below doesn't decay with time, so the record needs to
  // outlive any realistic delay on an in-flight poll request (backgrounded tab
  // throttling, slow network) for that check to still have something to compare
  // against when the stale response finally arrives.
  const TICKER_REMOVAL_RECORD_TTL_MS = 30 * 60 * 1000;
  const recentlyRemovedTickersRef = useRef(new Map());
  const tickerRiskCacheRef = useRef(new Map());
  const nextPlanOverridesRef = useRef(new Map());
  const pendingFullRefreshRef = useRef(false);
  const recentShieldEditsRef = useRef(new Map()); // Map<tickerId, timestamp>
  const recentRatingEditsRef = useRef(new Map()); // Map<tickerId, timestamp> - protect recently edited ratings
  const recentCategoryEditsRef = useRef(new Map()); // Map<tickerId, timestamp> - protect recently edited categories (e.g. CategorySelect) from a stale in-flight refresh landing after the edit

  useEffect(() => {
    const handleDefaultShieldPresetChange = (event) => {
      const updatedAccountId = String(event?.detail?.accountId ?? '');
      const nextPreset = event?.detail?.defaultNewPositionShieldPreset;

      if (!updatedAccountId || String(currentAccountIdRef.current) !== updatedAccountId || !nextPreset) {
        return;
      }

      setAccount((prev) => (
        prev
          ? { ...prev, default_new_position_shield_preset: nextPreset }
          : prev
      ));
    };

    window.addEventListener('accountDefaultNewPositionShieldPresetChanged', handleDefaultShieldPresetChange);
    return () => {
      window.removeEventListener('accountDefaultNewPositionShieldPresetChanged', handleDefaultShieldPresetChange);
    };
  }, []);

  const getNextTickerSnapshotRequestId = useCallback(() => {
    tickerSnapshotRequestCounterRef.current += 1;
    return tickerSnapshotRequestCounterRef.current;
  }, []);

  const normalizeRemovalTimestamp = (value) => {
    if (!value) return null;
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }
    if (value instanceof Date) {
      const ms = value.getTime();
      return Number.isFinite(ms) ? ms : null;
    }
    if (typeof value === 'string') {
      const parsed = Date.parse(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  useEffect(() => {
    tickersRef.current = tickers;
  }, [tickers]);

  const updateTickerRiskCache = useCallback((tickerId, riskValues = {}, options = {}) => {
    if (!tickerId) {
      return;
    }

    const cache = tickerRiskCacheRef.current;
    const existing = cache.get(tickerId) || {};
    const now = Date.now();
    const source = options.source || 'unknown';
    const bypassOverride = options.bypassOverride === true;
    let entry = { ...existing };
    let changed = false;

    const overrideActive = !bypassOverride && source === 'server' && existing.overrideUntil && existing.overrideUntil > now;
    const hasLocalOverride = Boolean(existing.localOverride && existing.localOverride.values);
    const LOCAL_OVERRIDE_HOLD_MS = 120000;
    const RECENT_LOCAL_UPDATE_PROTECTION_MS = 20000;

    const existingPending = existing.pendingLocalValues || {};
    const existingLastServer = existing.lastServerValues || {};
    const pendingLocalValues = { ...existingPending };
    const lastServerValues = { ...existingLastServer };
    let pendingChanged = false;
    let lastServerChanged = false;

    const hasRecentLocalUpdate = (
      source === 'server' &&
      existing.lastUpdatedSource === 'local' &&
      typeof existing.lastUpdatedAt === 'number' &&
      (now - existing.lastUpdatedAt) <= RECENT_LOCAL_UPDATE_PROTECTION_MS
    );

    const shouldPreserveRecentLocalValue = (field, incomingValue) => {
      if (!hasRecentLocalUpdate) {
        return false;
      }

      const cachedValue = existing[field];
      const overrideValue = existing?.localOverride?.values?.[field];
      const referenceValue = overrideValue !== undefined ? overrideValue : cachedValue;

      if (referenceValue === undefined) {
        return false;
      }

      if (Object.is(referenceValue, incomingValue)) {
        return false;
      }

      return incomingValue === null || incomingValue === undefined;
    };

    const shouldBlockServerUpdate = (field, incomingValue) => {
      if (bypassOverride) {
        return false;
      }

      if (source === 'server') {
        const isNullish = incomingValue === null || incomingValue === undefined;

        // Always accept concrete server values immediately.
        if (!isNullish) {
          return false;
        }

        const hasPendingField = Object.prototype.hasOwnProperty.call(pendingLocalValues, field);
        if (hasPendingField) {
          const pendingValue = pendingLocalValues[field];
          const pendingIsConcrete = pendingValue !== null && pendingValue !== undefined;
          if (pendingIsConcrete && !Object.is(pendingValue, incomingValue)) {
            return true;
          }
        }

        if (!hasPendingField && isNullish) {
          if (Object.prototype.hasOwnProperty.call(lastServerValues, field)) {
            const lastServerValue = lastServerValues[field];
            if (lastServerValue !== null && lastServerValue !== undefined) {
              return true;
            }
          } else if (existing[field] !== null && existing[field] !== undefined) {
            return true;
          }
        }
      }

      if (source !== 'server' || !hasLocalOverride) {
        return shouldPreserveRecentLocalValue(field, incomingValue);
      }

      if (!Object.prototype.hasOwnProperty.call(existing.localOverride.values, field)) {
        return shouldPreserveRecentLocalValue(field, incomingValue);
      }

      const pendingValue = existing.localOverride.values[field];

      if (pendingValue === undefined) {
        return shouldPreserveRecentLocalValue(field, incomingValue);
      }

      if (!Object.is(pendingValue, incomingValue)) {
        return true;
      }

      return shouldPreserveRecentLocalValue(field, incomingValue);
    };

    const assignField = (field) => {
      if (!Object.prototype.hasOwnProperty.call(riskValues, field)) {
        return;
      }

      const newValue = riskValues[field];
      const incomingIsNullish = newValue === null || newValue === undefined;
      if (overrideActive && incomingIsNullish && existing[field] !== undefined && !Object.is(existing[field], newValue)) {
        return;
      }

      if (shouldBlockServerUpdate(field, newValue)) {
        return;
      }

      if (!Object.is(existing[field], newValue)) {
        entry[field] = newValue;
        changed = true;
      }

      if (source === 'server') {
        if (Object.prototype.hasOwnProperty.call(pendingLocalValues, field) && Object.is(pendingLocalValues[field], newValue)) {
          delete pendingLocalValues[field];
          pendingChanged = true;
        }

        if (!Object.prototype.hasOwnProperty.call(lastServerValues, field) && newValue !== undefined) {
          lastServerValues[field] = newValue;
          lastServerChanged = true;
        } else if (Object.prototype.hasOwnProperty.call(lastServerValues, field) && !Object.is(lastServerValues[field], newValue)) {
          if (newValue === undefined) {
            delete lastServerValues[field];
          } else {
            lastServerValues[field] = newValue;
          }
          lastServerChanged = true;
        }
      }
    };

    assignField('shield_risk_percentage');
    assignField('shield_risk_amount');
    assignField('armor');

    if (source === 'local') {
      const overrideValues = {};
      const pendingUpdates = {};
      ['shield_risk_percentage', 'shield_risk_amount', 'armor'].forEach((field) => {
        if (Object.prototype.hasOwnProperty.call(riskValues, field)) {
          const value = riskValues[field];
          if (value !== undefined) {
            overrideValues[field] = value;
            pendingUpdates[field] = value;
          }
        }
      });

      if (Object.keys(overrideValues).length > 0) {
        entry.localOverride = {
          createdAt: now,
          values: overrideValues
        };
        entry.overrideUntil = now + LOCAL_OVERRIDE_HOLD_MS;
        changed = true;
      }

      if (Object.keys(pendingUpdates).length > 0) {
        Object.keys(pendingUpdates).forEach((field) => {
          pendingLocalValues[field] = pendingUpdates[field];
        });
        pendingChanged = true;
      }
    } else {
      if (entry.overrideUntil && (entry.overrideUntil <= now || bypassOverride)) {
        delete entry.overrideUntil;
        changed = true;
      }

      if (source === 'server' && hasLocalOverride && !bypassOverride) {
        const remainingOverrideValues = { ...existing.localOverride.values };
        let overrideChanged = false;

        Object.keys(existing.localOverride.values).forEach((field) => {
          if (!Object.prototype.hasOwnProperty.call(riskValues, field)) {
            return;
          }

          const incomingValue = riskValues[field];
          if (Object.is(existing.localOverride.values[field], incomingValue)) {
            delete remainingOverrideValues[field];
            overrideChanged = true;
          }
        });

        if (Object.keys(remainingOverrideValues).length === 0) {
          delete entry.localOverride;
          if (entry.overrideUntil) {
            delete entry.overrideUntil;
          }
          changed = true;
        } else if (overrideChanged) {
          entry.localOverride = {
            ...existing.localOverride,
            values: remainingOverrideValues
          };
          changed = true;
        }
      } else if (bypassOverride && entry.localOverride) {
        delete entry.localOverride;
        if (entry.overrideUntil) {
          delete entry.overrideUntil;
        }
        changed = true;
      }

      if (source === 'server' && bypassOverride) {
        ['shield_risk_percentage', 'shield_risk_amount', 'armor'].forEach((field) => {
          if (Object.prototype.hasOwnProperty.call(pendingLocalValues, field)) {
            delete pendingLocalValues[field];
            pendingChanged = true;
          }
        });
      }
    }

    if (pendingChanged) {
      const keys = Object.keys(pendingLocalValues);
      if (keys.length > 0) {
        entry.pendingLocalValues = pendingLocalValues;
      } else if (entry.pendingLocalValues) {
        delete entry.pendingLocalValues;
      }
      changed = true;
    }

    if (lastServerChanged) {
      const keys = Object.keys(lastServerValues);
      if (keys.length > 0) {
        entry.lastServerValues = lastServerValues;
      } else if (entry.lastServerValues) {
        delete entry.lastServerValues;
      }
      changed = true;
    }

    if (changed) {
      entry.lastUpdatedAt = now;
      entry.lastUpdatedSource = source;
      cache.set(tickerId, entry);
    }
  }, []);

  const applyRiskCacheToTicker = useCallback((ticker) => {
    if (!ticker || !ticker.ticker_id) {
      return ticker;
    }

    const cacheEntry = tickerRiskCacheRef.current.get(ticker.ticker_id);
    if (!cacheEntry) {
      return ticker;
    }

    const overrideValues = cacheEntry?.localOverride?.values || {};

    const resolveCachedValue = (field) => {
      if (Object.prototype.hasOwnProperty.call(overrideValues, field)) {
        return overrideValues[field];
      }

      if (Object.prototype.hasOwnProperty.call(cacheEntry, field)) {
        return cacheEntry[field];
      }

      return undefined;
    };

    let merged = ticker;

    ['shield_risk_percentage', 'shield_risk_amount', 'armor'].forEach((field) => {
      const cachedValue = resolveCachedValue(field);
      if (cachedValue !== undefined && !Object.is(ticker[field], cachedValue)) {
        if (merged === ticker) {
          merged = { ...ticker };
        }
        merged[field] = cachedValue;
      }
    });

    return merged;
  }, []);

  const persistNextPlanOverridesToStorage = useCallback((accountIdentifier, overridesMap) => {
    const storageKey = getNextPlanOverridesStorageKey(accountIdentifier);
    if (!storageKey || typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    try {
      if (!overridesMap || overridesMap.size === 0) {
        window.localStorage.removeItem(storageKey);
        return;
      }
      const serialized = {};
      overridesMap.forEach((value, key) => {
        serialized[key] = value;
      });
      window.localStorage.setItem(storageKey, JSON.stringify(serialized));
    } catch (error) {
      console.error('Failed to persist next plan overrides', error);
    }
  }, []);

  const loadNextPlanOverridesFromStorage = useCallback((accountIdentifier) => {
    const storageKey = getNextPlanOverridesStorageKey(accountIdentifier);
    const map = new Map();
    if (!storageKey || typeof window === 'undefined' || !window.localStorage) {
      return map;
    }
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) {
        return map;
      }
      const parsed = JSON.parse(raw);
      const now = Date.now();
      Object.entries(parsed).forEach(([key, value]) => {
        if (!value) {
          return;
        }
        const normalizedAction = typeof value.next_action === 'string' ? value.next_action : 'HOLD';
        const normalizedSize = typeof value.next_size_pct === 'number' ? value.next_size_pct : null;
        const updatedAt = value.updatedAt || value.savedAt || now;
        if (NEXT_PLAN_OVERRIDE_TTL_MS > 0 && updatedAt && (now - updatedAt) > NEXT_PLAN_OVERRIDE_TTL_MS) {
          return;
        }
        map.set(String(key), {
          next_action: normalizedAction,
          next_size_pct: normalizedSize,
          updatedAt
        });
      });
    } catch (error) {
      console.error('Failed to load next plan overrides', error);
      try {
        if (storageKey) {
          window.localStorage.removeItem(storageKey);
        }
      } catch (cleanupError) {
        console.error('Failed to clear next plan overrides entry', cleanupError);
      }
    }
    return map;
  }, []);

  const ensureNextPlanOverridesMap = useCallback((accountIdentifier) => {
    if (accountIdentifier === undefined || accountIdentifier === null) {
      return null;
    }
    const accountKey = String(accountIdentifier);
    let overridesMap = nextPlanOverridesRef.current.get(accountKey);
    if (!overridesMap) {
      overridesMap = loadNextPlanOverridesFromStorage(accountIdentifier);
      nextPlanOverridesRef.current.set(accountKey, overridesMap);
    }
    return overridesMap;
  }, [loadNextPlanOverridesFromStorage]);

  const recordNextPlanOverride = useCallback((accountIdentifier, overridePayload = {}) => {
    if (accountIdentifier === undefined || accountIdentifier === null) {
      return;
    }
    const accountKey = String(accountIdentifier);
    const overridesMap = ensureNextPlanOverridesMap(accountIdentifier) || new Map();
    const [primaryKey, secondaryKey] = buildNextPlanOverrideKeys(overridePayload.tickerId, overridePayload.symbol);
    const key = primaryKey || secondaryKey;
    if (!key) {
      return;
    }
    const payload = {
      next_action: overridePayload.next_action || 'HOLD',
      next_size_pct: typeof overridePayload.next_size_pct === 'number' ? overridePayload.next_size_pct : null,
      updatedAt: Date.now()
    };
    overridesMap.set(key, payload);
    if (secondaryKey && key !== secondaryKey) {
      overridesMap.delete(secondaryKey);
    }
    nextPlanOverridesRef.current.set(accountKey, overridesMap);
    persistNextPlanOverridesToStorage(accountIdentifier, overridesMap);
  }, [ensureNextPlanOverridesMap, persistNextPlanOverridesToStorage]);

  const applyNextPlanOverrideIfPending = useCallback((ticker, accountIdentifier) => {
    if (!ticker || accountIdentifier === undefined || accountIdentifier === null) {
      return ticker;
    }
    const overridesMap = ensureNextPlanOverridesMap(accountIdentifier);
    if (!overridesMap || overridesMap.size === 0) {
      return ticker;
    }
    const [idKey, symbolKey] = buildNextPlanOverrideKeys(ticker.ticker_id, ticker.symbol);
    let overrideKey = null;
    let overrideValues = null;

    if (idKey && overridesMap.has(idKey)) {
      overrideKey = idKey;
      overrideValues = overridesMap.get(idKey);
    } else if (symbolKey && overridesMap.has(symbolKey)) {
      overrideKey = symbolKey;
      overrideValues = overridesMap.get(symbolKey);
      if (idKey) {
        overridesMap.set(idKey, overrideValues);
        overridesMap.delete(symbolKey);
        overrideKey = idKey;
      }
    }

    if (!overrideValues) {
      return ticker;
    }

    const normalizedTickerAction = (ticker.next_action || 'HOLD').toUpperCase();
    const normalizedOverrideAction = (overrideValues.next_action || 'HOLD').toUpperCase();
    const normalizedTickerSize = typeof ticker.next_size_pct === 'number' ? ticker.next_size_pct : null;
    const normalizedOverrideSize = typeof overrideValues.next_size_pct === 'number' ? overrideValues.next_size_pct : null;

    if (normalizedTickerAction === normalizedOverrideAction && normalizedTickerSize === normalizedOverrideSize) {
      overridesMap.delete(overrideKey);
      nextPlanOverridesRef.current.set(String(accountIdentifier), overridesMap);
      persistNextPlanOverridesToStorage(accountIdentifier, overridesMap);
      return ticker;
    }

    return {
      ...ticker,
      next_action: overrideValues.next_action,
      next_size_pct: overrideValues.next_size_pct
    };
  }, [ensureNextPlanOverridesMap, persistNextPlanOverridesToStorage]);

  const buildTickerRemovalKey = (accountIdentifier, tickerIdentifier) => {
    if (tickerIdentifier === null || tickerIdentifier === undefined) {
      return null;
    }

    const normalizedAccountId = accountIdentifier ?? currentAccountIdRef.current ?? accountId;
    if (normalizedAccountId === null || normalizedAccountId === undefined) {
      return null;
    }

    return `${String(normalizedAccountId)}::${String(tickerIdentifier).toUpperCase()}`;
  };

  const cleanupRecentTickerRemovals = (now = Date.now()) => {
    const removalMap = recentlyRemovedTickersRef.current;
    if (!removalMap) return;

    for (const [key, entry] of removalMap.entries()) {
      const removedAt = typeof entry === 'number' ? entry : entry?.removedAt;
      if (!removedAt || now - removedAt > TICKER_REMOVAL_RECORD_TTL_MS) {
        removalMap.delete(key);
      }
    }
  };

  const markTickerRecentlyRemoved = (tickerIdentifier, accountIdentifier = currentAccountIdRef.current, lastUpdated = null) => {
    const key = buildTickerRemovalKey(accountIdentifier, tickerIdentifier);
    if (!key) return;

    const now = Date.now();
    const normalizedLastUpdated = normalizeRemovalTimestamp(lastUpdated);
    recentlyRemovedTickersRef.current.set(key, {
      removedAt: now,
      lastUpdatedAt: normalizedLastUpdated,
      // Snapshot the outgoing-request counter at the moment of removal so a
      // full-refresh response can be judged stale by *causal order* -- "was this
      // request issued before we knew the ticker was gone" -- rather than by how
      // long it took to arrive. See wasTickerRecentlyRemoved.
      removedAtRequestId: tickerSnapshotRequestCounterRef.current
    });
    cleanupRecentTickerRemovals(now);
  };

  const removeTickerFromCache = () => {
    // Cache persistence is disabled for Account Details to guarantee fresh-only renders.
  };

  const wasTickerRecentlyRemoved = (tickerIdentifier, accountIdentifier = currentAccountIdRef.current, incomingLastUpdated = null, incomingSnapshotRequestId = null) => {
    const key = buildTickerRemovalKey(accountIdentifier, tickerIdentifier);
    if (!key) return false;

    const removalMap = recentlyRemovedTickersRef.current;
    const entry = removalMap.get(key);
    if (entry === undefined) {
      return false;
    }

    const removedAt = typeof entry === 'number' ? entry : entry?.removedAt;
    const lastUpdatedAt = typeof entry === 'object' ? entry?.lastUpdatedAt : null;
    const removedAtRequestId = typeof entry === 'object' ? entry?.removedAtRequestId : null;
    const incomingLastUpdatedMs = normalizeRemovalTimestamp(incomingLastUpdated);
    const now = Date.now();

    // Causal-order check: a response tagged with a request id issued at or before
    // the moment we recorded this removal was necessarily fetched before (or at
    // the same time as) the removal, no matter how long it took to arrive back.
    // This is what actually protects against a poll request that was already in
    // flight when the sale happened but takes minutes to resolve -- the 120s
    // window below can't, since it decays with wall-clock time regardless of when
    // the stale request was actually issued.
    if (Number.isFinite(incomingSnapshotRequestId) && Number.isFinite(removedAtRequestId)) {
      if (incomingSnapshotRequestId <= removedAtRequestId) {
        return true;
      }
      // This snapshot was requested strictly after we recorded the removal, so it
      // necessarily reflects backend state at or after that removal (e.g. the
      // ticker was bought back). Trust that proof instead of falling through to
      // the wall-clock heuristic below, which would otherwise keep rejecting a
      // perfectly fresh re-buy for up to RECENT_TICKER_REMOVAL_WINDOW_MS just
      // because it landed soon after the sell.
      removalMap.delete(key);
      return false;
    }

    if (removedAt && incomingLastUpdatedMs !== null) {
      const staleCutoff = lastUpdatedAt || removedAt;
      if (incomingLastUpdatedMs <= staleCutoff) {
        return true;
      }
      // Same reasoning as above: a last_updated newer than the removal record
      // proves this snapshot reflects a later state, so don't let the
      // wall-clock fallback below override that proof.
      removalMap.delete(key);
      return false;
    }

    if (removedAt && now - removedAt <= RECENT_TICKER_REMOVAL_WINDOW_MS) {
      return true;
    }

    removalMap.delete(key);
    return false;
  };
  // currentAccountIdRef is read live (via .current, not through React state)
  // by every async guard in this file -- fetch responses, the polling merge,
  // and the account-scoped WebSocket subscription below all compare their
  // payload's account against it to decide whether to apply or discard stale
  // data. A plain useEffect only updates it *after* the browser has painted
  // the new accountId, leaving a real (if narrow) window where a message
  // that arrives from the socket in that gap -- sockets aren't paused by
  // React's render/commit cycle -- would still see the outgoing account's id
  // here and be wrongly accepted as current. useLayoutEffect runs
  // synchronously right after DOM mutation, before paint and before the
  // event loop can hand control to anything else (socket callbacks
  // included), so the ref is never observably stale to an outside caller.
  useLayoutEffect(() => {
    currentAccountIdRef.current = accountId;
    tickersAccountIdRef.current = accountId;
  }, [accountId]);

  useEffect(() => {
    if (!quickTradeStatus) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      setQuickTradeStatus(null);
    }, 6000);

    return () => clearTimeout(timeout);
  }, [quickTradeStatus]);

  const [showCancelledModal, setShowCancelledModal] = useState(false);
  const [cancelledOrderObj, setCancelledOrderObj] = useState(null);
  const [shieldsRefreshing, setShieldsRefreshing] = useState(false);
  const [fulfilledOrderObj, setFulfilledOrderObj] = useState(null);
  const [orderSortColumn, setOrderSortColumn] = useState('action');
  const [orderSortDirection, setOrderSortDirection] = useState('asc');
  const [sortedOrders, setSortedOrders] = useState([]);
  const [adjustingOrderId, setAdjustingOrderId] = useState(null);
  
  // Edge history modal state
  const [showEdgeHistoryModal, setShowEdgeHistoryModal] = useState(false);
  const [edgeHistoryTimePeriod, setEdgeHistoryTimePeriod] = useState('1month');
  
  // Planning modal state
  const [showPlanningModal, setShowPlanningModal] = useState(false);
  const [showSaveDefaultModal, setShowSaveDefaultModal] = useState(false);
  
  // Account transactions state
  const [accountTransactions, setAccountTransactions] = useState([]);
  const [accountTransactionsLoading, setAccountTransactionsLoading] = useState(false);
  const [showTransactionsToday, setShowTransactionsToday] = useState(() => {
    const saved = localStorage.getItem(`account-${accountId}-transactions-today-visible`);
    return saved !== null ? saved === 'true' : true;
  });
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('SELL');

  const filteredAccountTransactions = useMemo(() => {
    if (transactionTypeFilter === 'BUY') {
      return accountTransactions.filter(tx => String(tx.transaction_type).toUpperCase() === 'BUY');
    }
    if (transactionTypeFilter === 'SELL') {
      return accountTransactions.filter(tx => String(tx.transaction_type).toUpperCase() === 'SELL');
    }
    return accountTransactions;
  }, [accountTransactions, transactionTypeFilter]);
  
  // Sorting for Account Transactions ("Transactions Today")
  const [accountTxSortColumn, setAccountTxSortColumn] = useState('date');
  const [accountTxSortDirection, setAccountTxSortDirection] = useState('desc');

  const handleAccountTxSort = (column) => {
    setAccountTxSortDirection(prev => (accountTxSortColumn === column ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setAccountTxSortColumn(column);
  };

  const getAccountTxSortValue = (tx, column) => {
    switch (column) {
      case 'date': {
        if (typeof tx.normalizedDateMs === 'number') {
          return tx.normalizedDateMs;
        }
        const candidates = dedupeDatesByTime(parseFlexibleDateCandidates(tx?.transaction_date || tx?.raw_date));
        const chosen = chooseBestDate(candidates);
        return chosen ? chosen.getTime() : null;
      }
      case 'type':
        return tx.transaction_type || '';
      case 'symbol':
        return tx.transaction_type === 'CASH' ? '' : (tx.ticker_symbol || '');
      case 'shares':
        if (tx.numericShares != null) return tx.numericShares;
        const sharesVal = Number(tx.shares);
        return Number.isFinite(sharesVal) ? sharesVal : 0;
      case 'price':
        if (tx.transaction_type === 'CASH') return null;
        if (tx.numericPrice != null) return tx.numericPrice;
        const priceVal = Number(tx.price_per_share);
        return Number.isFinite(priceVal) ? priceVal : 0;
      case 'total': {
        const sharesValue = tx.numericShares != null ? tx.numericShares : Number(tx.shares) || 0;
        const priceValue = tx.numericPrice != null ? tx.numericPrice : Number(tx.price_per_share) || 0;
        if (tx.transaction_type === 'CASH') return sharesValue;
        return sharesValue * priceValue;
      }
      case 'gain':
      case 'gain_pct': {
        if (tx.transaction_type !== 'SELL') return null;
        // Prefer backend-provided realized values if available
        if (column === 'gain' && tx.realized_gain_amount != null) return Number(tx.realized_gain_amount);
        if (column === 'gain_pct' && tx.realized_gain_percent != null) return Number(tx.realized_gain_percent);
        // Fallback to local computation
        const t = tickers.find(tt => tt.symbol === tx.ticker_symbol);
        const cb = t && t.cost_basis != null ? Number(t.cost_basis) : null;
        if (cb == null) return null;
        const price = tx.numericPrice != null ? tx.numericPrice : Number(tx.price_per_share) || 0;
        const shares = tx.numericShares != null ? tx.numericShares : Number(tx.shares) || 0;
        const perShare = price - cb;
        if (column === 'gain') return perShare * shares;
        return cb > 0 ? (perShare / cb) * 100 : null;
      }
      case 'notes':
        return (tx.notes || '').toLowerCase();
      default:
        return null;
    }
  };

  const getSortedAccountTransactions = () => {
    const arr = [...filteredAccountTransactions];
    arr.sort((a, b) => {
      const va = getAccountTxSortValue(a, accountTxSortColumn);
      const vb = getAccountTxSortValue(b, accountTxSortColumn);
      if (va == null && vb == null) return 0;
      if (va == null) return 1; // nulls last
      if (vb == null) return -1;
      if (typeof va === 'string' && typeof vb === 'string') {
        const res = va.localeCompare(vb);
        return accountTxSortDirection === 'asc' ? res : -res;
      }
      const res = va - vb;
      return accountTxSortDirection === 'asc' ? res : -res;
    });
    return arr;
  };

  const renderAccountTxSortIndicator = (col) => {
    if (accountTxSortColumn !== col) return null;
    return <span className="ms-1">{accountTxSortDirection === 'asc' ? '▲' : '▼'}</span>;
  };

  useEffect(() => {
    const savedVisibility = localStorage.getItem(`account-${accountId}-transactions-today-visible`);
    setShowTransactionsToday(savedVisibility !== null ? savedVisibility === 'true' : false);
  }, [accountId]);

  useEffect(() => {
    if (!accountId) return;
    localStorage.setItem(`account-${accountId}-transactions-today-visible`, showTransactionsToday ? 'true' : 'false');
  }, [accountId, showTransactionsToday]);

  const toggleTransactionsTodayVisibility = () => {
    setShowTransactionsToday(prev => !prev);
  };

  // Add sort configuration state with localStorage persistence
  const [sortConfig, setSortConfig] = useState(() => {
    const savedSortConfig = localStorage.getItem(`sortConfig-account-${accountId}`);
    return savedSortConfig ? JSON.parse(savedSortConfig) : { key: 'next', direction: 'asc' };
  });
  
  // Default column order is now handled dynamically by the column preferences system
  
  // Column visibility state - will be initialized by useEffect
  const [visibleColumns, setVisibleColumns] = useState(getDefaultVisibleColumns);
  
  // Column order state - will be initialized by useEffect
  const [columnOrder, setColumnOrder] = useState(getDefaultColumnOrder);
  
  // Initialize column configuration from user defaults
  useEffect(() => {
    const initializeColumns = async () => {
      try {
        const config = await initializeColumnConfig('account_details', accountId);
        const normalizedColumns = normalizeAccountVisibleColumns(config.visibleColumns);
        const normalizedOrder = normalizeAccountColumnOrder(config.columnOrder);
        setVisibleColumns(normalizedColumns);
        safelyUpdateColumnOrder(normalizedOrder);
      } catch (error) {
        logger.error('Failed to initialize column configuration', error);
        // Fall back to localStorage if available
        const savedColumns = localStorage.getItem(`visibleColumns-account-${accountId}`);
        const savedOrder = localStorage.getItem(`columnOrder-account-${accountId}`);

        if (savedColumns && savedOrder) {
          try {
            const parsedColumns = JSON.parse(savedColumns);
            const parsedOrder = JSON.parse(savedOrder);
            setVisibleColumns(normalizeAccountVisibleColumns(parsedColumns));
            safelyUpdateColumnOrder(normalizeAccountColumnOrder(parsedOrder));
          } catch (parseError) {
            logger.error('Failed to parse localStorage column settings');
            // Fall back to hardcoded defaults as last resort
            setVisibleColumns(getDefaultVisibleColumns());
            setColumnOrder(getDefaultColumnOrder());
          }
        }
      }
    };
    
    if (accountId) {
      initializeColumns();
    }
  }, [accountId]);
  
  // Helper function to handle reset to user's saved defaults
  const handleResetToDefaults = async () => {
    logger.debug("Reset button clicked - this will reset to user's saved defaults");
    try {
      // Get user's saved defaults (or system defaults if none saved)
      const config = await getDefaultColumnConfig('account_details');
      logger.debug('Reset config received', config);
      const normalizedColumns = normalizeAccountVisibleColumns(config.visibleColumns);
      const normalizedOrder = normalizeAccountColumnOrder(config.columnOrder);
      setVisibleColumns(normalizedColumns);
      safelyUpdateColumnOrder(normalizedOrder);
    } catch (error) {
      logger.error("Failed to reset to user's default column configuration", error);
      // Fall back to hardcoded system defaults as last resort
      setVisibleColumns(getDefaultVisibleColumns());
      setColumnOrder(getDefaultColumnOrder());
    }
  };
  
  // Helper function to save current configuration as user defaults
  const handleSaveAsDefaults = async () => {
    logger.debug('handleSaveAsDefaults called');
    logger.debug('Current visibleColumns', visibleColumns);
    logger.debug('Current columnOrder', columnOrder);

    // Check if states are properly initialized
    if (!visibleColumns || Object.keys(visibleColumns).length === 0) {
      alert('❌ No column configuration found to save.\n\nPlease wait for the page to fully load and try again.');
      return;
    }

    if (!columnOrder || columnOrder.length === 0) {
      alert('❌ No column order found to save.\n\nPlease wait for the page to fully load and try again.');
      return;
    }

    // Show confirmation modal
    setShowSaveDefaultModal(true);
  };

  const confirmSaveAsDefaults = async () => {
    setShowSaveDefaultModal(false);

    try {
      logger.debug('Saving column preferences', { visibleColumns, columnOrder });
      await saveAsDefaultColumnConfig('account_details', visibleColumns, columnOrder);

      logger.info('Current column configuration saved as user defaults');
    } catch (error) {
      logger.error('Failed to save current configuration as defaults', error);

      // Show error message
      alert('❌ Failed to save column preferences as default.\n\nPlease check your connection and try again.\n\nError: ' + error.message);
    }
  };
  
  // Save column visibility settings when they change
  useEffect(() => {
    if (accountId) {
      localStorage.setItem(`visibleColumns-account-${accountId}`, JSON.stringify(visibleColumns));
    }
  }, [visibleColumns, accountId]);
  
  // Save column order when it changes
  useEffect(() => {
    if (accountId) {
      const orderToSave = [...columnOrder];
      localStorage.setItem(`columnOrder-account-${accountId}`, JSON.stringify(orderToSave));
    }
  }, [columnOrder, accountId]);

  // Safely update column order ensuring symbol is always at position 0 and actions at position 1
  const safelyUpdateColumnOrder = (newOrder) => {
    // Ensure symbol and strategy are always included at the right positions
    let safeOrder = [...newOrder];

    // Handle symbol column (always at position 0)
    if (!safeOrder.includes('symbol')) {
      safeOrder = ['symbol', ...safeOrder];
    } else if (safeOrder[0] !== 'symbol') {
      const filteredOrder = safeOrder.filter(col => col !== 'symbol');
      safeOrder = ['symbol', ...filteredOrder];
    }

    // Ensure strategy column immediately follows symbol
    if (!safeOrder.includes('strategy')) {
      const withoutSymbol = safeOrder.filter(col => col !== 'symbol');
      safeOrder = ['symbol', 'strategy', ...withoutSymbol];
    } else if (safeOrder[1] !== 'strategy') {
      const remaining = safeOrder.filter(col => col !== 'symbol' && col !== 'strategy');
      safeOrder = ['symbol', 'strategy', ...remaining];
    }

    // Add total_cost to order if not present
    if (!safeOrder.includes('total_cost')) {
      // Add after cost_basis if it exists
      const costBasisIndex = safeOrder.indexOf('cost_basis');
      if (costBasisIndex !== -1) {
        safeOrder = [
          ...safeOrder.slice(0, costBasisIndex + 1),
          'total_cost',
          ...safeOrder.slice(costBasisIndex + 1)
        ];
      } else {
        // Otherwise, add after shares
        const sharesIndex = safeOrder.indexOf('shares');
        if (sharesIndex !== -1) {
          safeOrder = [
            ...safeOrder.slice(0, sharesIndex + 1),
            'total_cost',
            ...safeOrder.slice(sharesIndex + 1)
          ];
        } else {
          // If all else fails, add at position 5 or at the end if shorter
          const insertPos = Math.min(5, safeOrder.length);
          safeOrder = [
            ...safeOrder.slice(0, insertPos),
            'total_cost',
            ...safeOrder.slice(insertPos)
          ];
        }
      }
    }
    
    setColumnOrder(safeOrder);
  };
  
  // Calculate total balance from cash and tickers
  const calculateTotalBalance = () => {
    if (!account) {
      return 0;
    }
    const tickersTotal = tickers.reduce((total, ticker) => total + (ticker.current_value || 0), 0);
    return (account.cash || 0) + tickersTotal;
  };
  
  // Calculate total cost by summing all tickers' cost
  // Prefer ticker.total_cost when provided, otherwise fallback to shares * cost_basis
  const calculateTotalCost = () => {
    if (!account) {
      return 0;
    }
    return tickers.reduce((sum, ticker) => {
      const explicitTotalCost = ticker.total_cost;
      if (explicitTotalCost !== undefined && explicitTotalCost !== null) {
        return sum + Number(explicitTotalCost || 0);
      }
      const shares = Number(ticker.shares || 0);
      const costBasis = Number(ticker.cost_basis || 0);
      return sum + shares * costBasis;
    }, 0);
  };
 
  // Helper: parse a date-like input into multiple possible Date objects (handles naive ISO strings)
  const parseFlexibleDateCandidates = (value, { includeUtcFallback = true } = {}) => {
    if (value === null || value === undefined) return [];

    if (value instanceof Date) {
      const cloned = new Date(value.getTime());
      Object.defineProperty(cloned, '__hasExplicitTimezone', { value: true, enumerable: false });
      Object.defineProperty(cloned, '__isUtcFallback', { value: false, enumerable: false });
      return [cloned];
    }

    const asString = typeof value === 'string' ? value : String(value);
    const trimmed = asString.trim();
    if (!trimmed) return [];

    const candidateDetails = new Map();
    const addCandidate = (candidate, { isUtcFallback = false } = {}) => {
      if (!candidate || candidateDetails.has(candidate)) return;
      const hasExplicitTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(candidate);
      candidateDetails.set(candidate, { hasExplicitTimezone, isUtcFallback });
    };

    addCandidate(trimmed);

    if (trimmed.includes(' ')) {
      addCandidate(trimmed.replace(' ', 'T'));
    }

    const isoLikePattern = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2})(?::(\d{2})(?::(\d{2})(?:\.(\d{1,6}))?)?)?)?$/;
    if (isoLikePattern.test(trimmed)) {
      const isoLike = trimmed.includes(' ') ? trimmed.replace(' ', 'T') : trimmed;
      addCandidate(isoLike);

      const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(isoLike);
      if (!hasTimezone && includeUtcFallback) {
        addCandidate(`${isoLike}Z`, { isUtcFallback: true });
      }
    }

    const results = [];
    candidateDetails.forEach((details, candidate) => {
      const date = new Date(candidate);
      if (!Number.isNaN(date.getTime())) {
        Object.defineProperty(date, '__hasExplicitTimezone', { value: details.hasExplicitTimezone, enumerable: false });
        Object.defineProperty(date, '__isUtcFallback', { value: details.isUtcFallback, enumerable: false });
        results.push(date);
      }
    });

    return results;
  };

  const dedupeDatesByTime = (dates) => {
    const bestByTime = new Map();
    const scoreDate = (date) => {
      if (!date || Number.isNaN(date.getTime())) return -Infinity;
      const timezoneScore = date.__hasExplicitTimezone ? 2 : 0;
      const fallbackScore = date.__isUtcFallback ? 1 : 0;
      return timezoneScore + fallbackScore;
    };

    dates.forEach((date) => {
      const time = date.getTime();
      if (Number.isNaN(time)) return;
      const score = scoreDate(date);
      const existing = bestByTime.get(time);
      if (!existing || score > existing.score) {
        bestByTime.set(time, { date, score });
      }
    });

    const result = [];
    dates.forEach((date) => {
      const time = date.getTime();
      const entry = bestByTime.get(time);
      if (entry && entry.date === date) {
        result.push(date);
        bestByTime.delete(time);
      }
    });

    return result;
  };

  const chooseBestDate = (dates, preferredIsoDay = null) => {
    if (!dates || dates.length === 0) return null;
    const scoreTimezone = (date) => (date && date.__hasExplicitTimezone ? 1 : 0);
    const scoreUtcFallback = (date) => (date && date.__isUtcFallback ? 1 : 0);

    const sorted = [...dates].sort((a, b) => {
      const tzDiff = scoreTimezone(b) - scoreTimezone(a);
      if (tzDiff !== 0) return tzDiff;

      const utcFallbackDiff = scoreUtcFallback(b) - scoreUtcFallback(a);
      if (utcFallbackDiff !== 0) return utcFallbackDiff;

      return b.getTime() - a.getTime();
    });
    if (preferredIsoDay) {
      const match = sorted.find((date) => formatISODate(date) === preferredIsoDay);
      if (match) return match;
    }
    return sorted[0];
  };

  // Format date for display (prefers ISO raw_date if available)
  const formatTransactionDate = (dateInput) => {
    if (!dateInput) return '';

    try {
      const candidates = dedupeDatesByTime(parseFlexibleDateCandidates(dateInput));
      const date = chooseBestDate(candidates);
      if (!date) {
        return typeof dateInput === 'string' ? dateInput : '';
      }

      const datePart = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });

      const timePart = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });

      return `${datePart} ${timePart}`;
    } catch (e) {
      return typeof dateInput === 'string' ? dateInput : '';
    }
  };

  // Format currency for display
  const formatTransactionCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numeric);
  };

  // Fetch account transactions for today
  const fetchAccountTransactions = async () => {
    if (!accountId) return;
    const requestId = ++latestTransactionsRequestRef.current;
    const requestedAccountId = accountId;
    const isStale = () => latestTransactionsRequestRef.current !== requestId || accountId !== requestedAccountId;

    const todayIso = getCurrentLocalDate();

    const shiftIsoDay = (iso, delta) => {
      if (!iso) return '';
      const base = new Date(`${iso}T00:00:00`);
      if (Number.isNaN(base.getTime())) return iso;
      base.setDate(base.getDate() + delta);
      return formatISODate(base);
    };

    const previousDayIso = shiftIsoDay(todayIso, -1);
    const nextDayIso = shiftIsoDay(todayIso, 1);

    const normalizeTransaction = (tx) => {
      const rawDateValue = tx?.raw_date || tx?.transaction_date || null;
      const candidateDates = dedupeDatesByTime([
        ...parseFlexibleDateCandidates(tx?.raw_date),
        ...parseFlexibleDateCandidates(tx?.transaction_date)
      ]);
      const candidateLocalDays = candidateDates.map((date) => formatISODate(date));
      const chosenDate = chooseBestDate(candidateDates, todayIso);
      // created_at carries the real second-level timestamp; use it for display/sort if available
      const createdAtDate = tx?.created_at ? (parseFlexibleDateCandidates(tx.created_at)[0] ?? null) : null;

      const numericShares = tx?.shares != null && Number.isFinite(Number(tx.shares)) ? Number(tx.shares) : null;
      const numericPrice = tx?.price_per_share != null && Number.isFinite(Number(tx.price_per_share))
        ? Number(tx.price_per_share)
        : null;

      const displayDate = createdAtDate ?? chosenDate;
      return {
        ...tx,
        raw_date: rawDateValue,
        normalizedDate: displayDate ? displayDate.toISOString() : null,
        normalizedDateMs: displayDate ? displayDate.getTime() : null,
        normalizedLocalDay: chosenDate ? formatISODate(chosenDate) : null,
        candidateLocalDays,
        numericShares,
        numericPrice
      };
    };

    const stripTransientFields = (tx) => {
      const { candidateLocalDays, ...rest } = tx;
      return rest;
    };

    setAccountTransactionsLoading(true);
    try {
      const filters = {
        accountId: accountId,
        // Expand the server query by a day on both sides so entries that land on an adjacent
        // UTC calendar day (e.g. cash deposits recorded just after midnight UTC) are still
        // returned for local "today" once we normalise them client-side.
        startDate: previousDayIso,
        endDate: nextDayIso
      };

      const PAGE_LIMIT = 100;
      let list = [];

      const fetchPage = async (pageNumber) => {
        const response = await getAllTransactions(pageNumber, PAGE_LIMIT, filters);
        if (isStale()) {
          return null;
        }
        const pageTransactions = (response?.data?.transactions || [])
          .map(normalizeTransaction)
          .filter((tx) => Array.isArray(tx.candidateLocalDays) && tx.candidateLocalDays.includes(todayIso))
          .map(stripTransientFields);
        return {
          transactions: pageTransactions,
          totalPages: response?.data?.totalPages || 1
        };
      };

      const firstPage = await fetchPage(1);
      if (!firstPage) return;

      list = firstPage.transactions;
      const totalPages = firstPage.totalPages;

      if (!isStale() && totalPages > 1) {
        for (let page = 2; page <= totalPages; page += 1) {
          const nextPage = await fetchPage(page);
          if (!nextPage) return;
          if (nextPage.transactions?.length) {
            list = list.concat(nextPage.transactions);
          }
        }
      }

      // Fallback: if API filter returns empty, try unfiltered account transactions and filter client-side
      if ((!Array.isArray(list) || list.length === 0) && !isStale()) {
        try {
          const res2 = await getTransactions(accountId);
          if (isStale()) return;
          const allTx = Array.isArray(res2.data) ? res2.data : [];
          list = allTx
            .map(normalizeTransaction)
            .filter((tx) => Array.isArray(tx.candidateLocalDays) && tx.candidateLocalDays.includes(todayIso))
            .map(stripTransientFields);
        } catch (e) {
          // keep list as empty
        }
      }

      if (isStale()) return;
      setAccountTransactions(list);
    } catch (err) {
      logger.error('Error fetching account transactions', err);
      if (isStale()) return;
      setAccountTransactions([]);
    } finally {
      if (isStale()) return;
      setAccountTransactionsLoading(false);
    }
  };

  // Use the backend-calculated bottom line value
  // The backend calculates this correctly as: Cash + Sum of all shield values (shield_price × protected_shares)
  const calculateBottomLine = () => {
    if (!account) {
      return 0;
    }
    
    // Use the backend-calculated bottom line value
    return account.bottom_line || 0;
  };
  
  // Calculate the default standard size as 1/10 of the total account value, minimum $1000
  const calculateDefaultStandardSize = () => {
    // If backend provides a default_standard_size, use it
    if (account && account.default_standard_size) {
      return account.default_standard_size;
    }
    
    // If no account data, return default minimum
    if (!account) {
      return 1000;
    }
    
    // Otherwise calculate it on the frontend with minimum $1000
    const totalBalance = calculateTotalBalance();
    // Calculate 1/10 of the account value
    const baseValue = totalBalance / 10;
    // Round to the nearest $1,000 increment with a minimum of $1,000
    return Math.max(1000, Math.round(baseValue / 1000) * 1000);
  };

  const parseNumericValue = (value) => {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    if (typeof value === 'string') {
      const sanitized = value.replace(/[^0-9.-]/g, '');
      if (!sanitized || sanitized === '-' || sanitized === '.' || !/[0-9]/.test(sanitized)) {
        return null;
      }

      const parsed = Number(sanitized);
      return Number.isFinite(parsed) ? parsed : null;
    }

    if (typeof value === 'bigint') {
      return Number(value);
    }

    return null;
  };

  const standardSizeValue = (account?.auto_standard_size || !account?.standard_size)
    ? calculateDefaultStandardSize()
    : account.standard_size;
  const standardSizeNumber = parseNumericValue(standardSizeValue);
  const standardSizeDisplay = Number.isFinite(standardSizeNumber)
    ? `$${Math.round(standardSizeNumber).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
    : '$0';
  const standardSizeLabel = `X=${standardSizeDisplay}`;

  
  // Calculate today's total gain across all holdings
  const getTodayTotalGain = () => {
    // If account data is not available or we don't have previous_day_balance, return default values
    if (!account || account.previous_day_balance === undefined || loading) {
      return { amount: 0, percentage: 0, totalGainAmount: 0, totalGainPercentage: 0 };
    }

    // Prefer the server-computed totals from the same
    // getAllAccountsWithAggregate() endpoint NavigationPanel uses (see
    // syncAccountAggregate) when we have a fresh one for this account: it's
    // one atomic snapshot computed from the DB's current prices, so it
    // can't drift the way summing the incrementally-merged `tickers` array
    // can between poll ticks. Falls through to the client-side recompute
    // below when we don't have one yet (e.g. right after mount, before the
    // first poll tick lands) or the background fetch failed.
    if (accountAggregate && String(accountAggregate.account_id) === String(accountId)) {
      return {
        amount: accountAggregate.today_gain_amount ?? 0,
        percentage: accountAggregate.today_gain_percentage ?? 0,
        usingPreviousBalance: true,
        totalGainAmount: accountAggregate.total_gain_amount ?? 0,
        totalGainPercentage: accountAggregate.total_gain_percentage ?? 0
      };
    }

    // Calculate today's gain using previous day balance instead of ticker gains
    const calculateTodayGain = () => {
      // Current account value
      const currentValue = calculateTotalBalance();
      
      // Previous day account balance (from backend)
      const previousDayBalance = account.previous_day_balance || 0;
      
      // Calculate gain amount as current value minus previous day balance
      const gainAmount = currentValue - previousDayBalance;
      
      // Calculate percentage based on previous day balance
      const gainPercentage = previousDayBalance > 0 ? (gainAmount / previousDayBalance) * 100 : 0;
      
      return {
        amount: gainAmount,
        percentage: gainPercentage,
        usingPreviousBalance: true
      };
    };
    
    // Calculate total gain amount directly from tickers if not provided by backend
    const calculateTotalGainAmount = () => {
      if (account.total_gain_amount !== undefined) return account.total_gain_amount;
      
      // Fallback calculation from tickers
      return tickers.reduce((total, ticker) => 
        total + (ticker.current_price ? ticker.total_gain : 0), 0);
    };
    
    // Calculate total gain percentage if not provided by backend
    const calculateTotalGainPercentage = () => {
      // Force frontend calculation
      const totalGainAmount = calculateTotalGainAmount();

      // For the Total Gain card, use the total balance (including cash)
      const totalBalance = calculateTotalBalance();
      
      // Use total balance for percentage calculation instead of just the cost basis
      const calculatedPercentage = totalBalance > 0 ? (totalGainAmount / totalBalance) * 100 : 0;
      return calculatedPercentage;
    };
    
    // Get today's gain values using the new calculation method
    const todayGain = calculateTodayGain();
    
    // Return values with fallbacks for missing backend values
    return { 
      amount: todayGain.amount, 
      percentage: todayGain.percentage,
      usingPreviousBalance: todayGain.usingPreviousBalance,
      totalGainAmount: calculateTotalGainAmount(),
      totalGainPercentage: calculateTotalGainPercentage()
    };
  };

  // Calculate holdings total gain to match TickerTable TOTAL row (Total $ and Total %)
  const holdingsTotalGain = useMemo(() => {
    if (!Array.isArray(tickers) || tickers.length === 0) {
      return { amount: 0, percentage: 0 };
    }
    // Total gain amount: sum of ticker.total_gain; fallback to current_value - (shares * cost_basis)
    const totalGainAmount = tickers.reduce((sum, t) => {
      const cost = t.cost_basis ? (t.shares * t.cost_basis) : 0;
      const currentVal = t.current_value != null ? t.current_value : cost;
      const gain = (t.total_gain != null ? t.total_gain : (currentVal - cost));
      return sum + (gain || 0);
    }, 0);
    // Total cost basis across tickers (excludes cash)
    const totalCostBasis = tickers.reduce((sum, t) => {
      return sum + (t.cost_basis ? (t.shares * t.cost_basis) : 0);
    }, 0);
    const percentage = totalCostBasis > 0 ? (totalGainAmount / totalCostBasis) * 100 : 0;
    return { amount: totalGainAmount, percentage };
  }, [tickers]);
  
  useEffect(() => {
    if (!accountId) return;
    
    // Clear any existing errors when component loads or accountId changes
    setError('');
    
    // Reset state when switching accounts to prevent race conditions
    setAccount(null);
    setTickers([]);
    tickerRiskCacheRef.current.clear();
    setTransactions([]);
    setRefreshLoading(false);
    recentlyRemovedTickersRef.current.clear();

    setLoading(true);
    setTableLoading(true);

    // Fetch account details on component mount
    fetchAccountDetails(true);
    // Fetch account transactions for today
    fetchAccountTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  // Handle quick sell trigger from navigation state (when navigated from AggregatedTickers)
  useEffect(() => {
    const quickSellTicker = location.state?.quickSellTicker;

    if (quickSellTicker && quickSellTicker.symbol && tickers.length > 0 && !loading) {
      // Find the ticker by symbol
      const ticker = tickers.find(t =>
        (t.symbol || t.ticker) === quickSellTicker.symbol
      );

      if (ticker) {
        // Clear the navigation state immediately to prevent repeated triggers
        navigate(location.pathname, { replace: true, state: {} });

        // Trigger quick sell after a short delay to ensure state is cleared
        setTimeout(() => {
          handleQuickSell(ticker);
        }, 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickers, location.state, location.pathname, navigate, loading]);

  // Helper function to get calendar month period label
  const getCalendarMonthLabel = () => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1); // 1st day of current month
    
    const startMonth = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endMonth = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    
    return `${startMonth} - ${endMonth}`;
  };

  // Flexible function to fetch trading stats for N months (used by PlanningModal)
  const fetchTradingStatsForMonths = useCallback(async (months) => {
    if (!accountId) {
      return null;
    }

    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth() - (months - 1), 1);

    const resp = await getTradingStatistics({
      account_id: accountId,
      start_date: formatISODate(startDate),
      end_date: formatISODate(now)
    });

    return resp.data;
  }, [accountId]);

  // Load account-level expectancy (trading edge) for current month only
  const fetchTradingStatsForCurrentMonth = useCallback(async () => {
    if (!accountId) {
      return null;
    }

    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1); // First day of current month

    const resp = await getTradingStatistics({
      account_id: accountId,
      start_date: formatISODate(startDate),
      end_date: formatISODate(now)
    });

    return resp.data;
  }, [accountId]);

  useEffect(() => {
    const loadExpectancy = async () => {
      try {
        const stats = await fetchTradingStatsForCurrentMonth();
        if (!stats) {
          setAccountExpectancy(null);
          setAccountExpectancyWeighted(null);
          setTradingStats(null);
          return;
        }

        setAccountExpectancy(typeof stats?.expectancy === 'number' ? stats.expectancy : null);
        setAccountExpectancyWeighted(
          typeof stats?.expectancy_position_weighted === 'number'
            ? stats.expectancy_position_weighted
            : null
        );
        setTradingStats(stats);

        logger.debug('AccountDetails: Received trading statistics', {
          expectancy: stats?.expectancy,
          expectancy_position_weighted: stats?.expectancy_position_weighted,
          account_id: accountId
        });
      } catch (e) {
        setAccountExpectancy(null);
        setAccountExpectancyWeighted(null);
        setTradingStats(null);
      }
    };

    if (accountId) {
      loadExpectancy();
    }
  }, [accountId, fetchTradingStatsForCurrentMonth]);

  // Check if standard size needs to be set
  useEffect(() => {
    if (account && account.standard_size === null && tickers.length > 0) {
      setStandardSizeSuggestion(true);
    } else {
      setStandardSizeSuggestion(false);
    }
  }, [account, tickers]);

  // Handle standard size updates
  const handleStandardSizeUpdate = (newStandardSize, newAutoStandardSize) => {
    // Update the account state with the new standard size and auto mode
    setAccount(prev => ({
      ...prev,
      standard_size: newStandardSize,
      auto_standard_size: newAutoStandardSize
    }));
    
    // Hide the suggestion since standard size is now set
    setStandardSizeSuggestion(false);
  };

  // Use combined ticker updates (WebSocket + Polling)
  useTickerUpdates({
    enablePolling: true,
    onUpdate: (data) => {
      logger.debug('AccountDetails: Received ticker update', {
        hasUpdatedTickers: Boolean(data?.updated_tickers?.length > 0),
        updateCount: data?.updated_tickers?.length || 0,
        localTickersCount: tickers.length,
        source: data?.source || 'unknown',
        hasLastUpdateTime: Boolean(data?.latest_update_time),
        isTimestampEvent: data?.message === 'Timestamp update event',
        pollingCycleComplete: data?.pollingCycleComplete,
        accountId: accountId,
        currentAccountId: currentAccountIdRef.current
      });
      
      // Update market status if included in the data
      if (data.market_closed !== undefined && data.market_info) {
        setMarketInfo({
          market_closed: data.market_closed,
          market_info: data.market_info
        });
        // Update polling service to skip polls when market is closed
        tickerPollingService.setMarketClosed(data.market_closed);
      }
      
      // Always update the timestamp regardless of whether there are ticker updates
      // This ensures that the frontend stays in sync with backend updates
      let validTimestamp = null;
      
      // First check latest_update_time
      if (data.latest_update_time) {
        try {
          const updateTime = new Date(data.latest_update_time);
          if (!isNaN(updateTime) && updateTime <= new Date()) {
            validTimestamp = updateTime;
            logger.debug('AccountDetails: Setting lastRefreshTime from latest_update_time', { updateTime });
          }
        } catch (e) {
          logger.error('AccountDetails: Error parsing latest_update_time', e);
        }
      }
      
      // Next try data.time
      if (!validTimestamp && data.time) {
        try {
          const updateTime = new Date(data.time);
          if (!isNaN(updateTime) && updateTime <= new Date()) {
            validTimestamp = updateTime;
            logger.debug('AccountDetails: Setting lastRefreshTime from data.time', { updateTime });
          }
        } catch (e) {
          logger.error('AccountDetails: Error parsing data.time', e);
        }
      }
      
      // If we found a valid timestamp, update the state
      if (validTimestamp) {
        setLastRefreshTime(validTimestamp);
      }
      
      // Simple approach: if we have ticker updates from polling, just call handleRefreshPrices
      // This uses the same proven logic as the manual "Refresh Prices" button
      const hasTickerUpdates = Boolean(data?.updated_tickers?.length > 0);
      const shouldFullRefresh = pendingFullRefreshRef.current;

      logger.debug('AccountDetails: Polling cycle completed', {
        hasTickerUpdates,
        shouldFullRefresh,
        pollingCycleComplete: data?.pollingCycleComplete,
        willRefreshPrices: data?.pollingCycleComplete || hasTickerUpdates
      });

      // If full refresh is pending (e.g., after a transaction), fetch complete ticker list
      if (shouldFullRefresh && tickers.length > 0) {
        logger.debug('AccountDetails: Performing full refresh after transaction');
        pendingFullRefreshRef.current = false; // Clear flag

        const requestedAccountId = currentAccountIdRef.current;
        const snapshotRequestId = getNextTickerSnapshotRequestId();
        getAccountTickers(requestedAccountId)
          .then(response => {
            if (String(currentAccountIdRef.current) !== String(requestedAccountId)) {
              logger.debug('AccountDetails: Ignoring full refresh for stale account', {
                requestedAccountId,
                currentAccountId: currentAccountIdRef.current
              });
              return; // Stale request
            }

            const freshTickers = response.data || [];
            updateTickersWithFilteredData(freshTickers, requestedAccountId, {
              source: 'polling-full-refresh',
              snapshotRequestId
            });

            logger.debug('AccountDetails: Full refresh completed', {
              tickersCount: freshTickers.length
            });
          })
          .catch(error => {
            if (String(currentAccountIdRef.current) !== String(requestedAccountId)) {
              logger.debug('AccountDetails: Ignoring full refresh error for stale account');
              return;
            }
            logger.error('AccountDetails: Full refresh failed', error);
            pendingFullRefreshRef.current = false; // Clear flag even on error
          });

        return; // Skip normal price update flow
      }

      // The values shown on screen come from the dedicated polling
      // subscription below (search for "Account details page use
      // subscription"), which merges data.updated_tickers from this same
      // poll tick directly into state as soon as it arrives -- never
      // gated behind the nudge below, so this page never re-lags behind
      // the navigation panel the way it used to.
      //
      // That merge only ever shows whatever's already in the DB, though,
      // and the backend's own background thread only rewrites prices
      // there every few minutes (GLOBAL_TICKER_UPDATE_INTERVAL). Without
      // something nudging it more often, this poll tick "flashes" (a
      // fresh render every ~60s) while showing the same stale price for
      // several cycles in a row -- exactly the DB staleness a manual
      // "Refresh Prices" click (or a hard page reload racing the
      // background thread) papers over. So: fire a background refresh
      // request here too, but -- unlike before -- don't await it or apply
      // its response; it exists purely to make the backend fetch+write a
      // fresh price (still governed by its own per-ticker cooldown, so
      // this is cheap when prices haven't actually moved) for the *next*
      // poll tick to pick up.
      if (hasTickerUpdates && tickers.length > 0) {
        updateTickerPrices(true).catch(error => {
          logger.debug('AccountDetails: Background price refresh nudge failed (non-critical)', error);
        });
      }

      // Keep the stats cards (Today's Gain, Total Gain) in lockstep with
      // NavigationPanel: it reacts to this exact same "tick had ticker
      // updates" trigger by refetching getAllAccountsWithAggregate()
      // unconditionally, getting one atomic server-computed number every
      // time. Do the same here rather than only ever recomputing from the
      // incrementally-merged `tickers` array, which can drift for a cycle
      // or two whenever a tick's payload doesn't carry a price for every
      // held ticker.
      if (hasTickerUpdates) {
        syncAccountAggregate();
      }

      const shouldRefreshSummary = data?.pollingCycleComplete;
      if (shouldRefreshSummary) {
        // After each polling cycle, refresh the account summary to keep high-level figures current
        maybeRefreshAccountSummary();
      }
    },
    onStatusChange: (status) => {
      logger.debug('AccountDetails: Update status changed', status);
    }
  });

  // Listen for account ticker updates (risk value changes) via WebSocket
  useEffect(() => {
    logger.debug('Setting up account ticker update subscription in AccountDetails');

    const numericAccountId = Number(accountId);
    if (!Number.isFinite(numericAccountId)) {
      logger.warn('AccountDetails: Invalid accountId for account ticker subscription', { accountId });
      return () => {};
    }

    const unsubscribe = subscribeToAccountTickerUpdates({ accountId: numericAccountId }, (data) => {
      logger.debug('AccountDetails: Received account ticker update via WebSocket', data);

      // Handle risk value updates from trailing stop changes
      if (data && data.type === 'risk_update' && data.account_id === numericAccountId) {
        logger.debug(`AccountDetails: Processing risk update for ticker ${data.ticker_id}`);
        
        // Update the risk values in the local tickers state
        // Ignore updates if user switched accounts
        if (String(data?.account_id) !== String(currentAccountIdRef.current)) {
          return;
        }
        tickersAccountIdRef.current = data.account_id;
        updateTickerRiskCache(data.ticker_id, {
          shield_risk_percentage: data.shield_risk_percentage,
          shield_risk_amount: data.shield_risk_amount
        }, { source: 'server' });
        setTickers(prevTickers => (
          prevTickers.map(ticker => {
            if (ticker.ticker_id === data.ticker_id) {
              logger.debug(`AccountDetails: Updating risk values for ${ticker.symbol}`, {
                old_risk_pct: ticker.shield_risk_percentage,
                new_risk_pct: data.shield_risk_percentage,
                old_risk_amount: ticker.shield_risk_amount,
                new_risk_amount: data.shield_risk_amount
              });

              const updatedTicker = {
                ...ticker,
                shield_risk_percentage: data.shield_risk_percentage,
                shield_risk_amount: data.shield_risk_amount
              };
              return applyRiskCacheToTicker(updatedTicker);
            }
            return ticker;
          })
        ));
        
        // Also refresh the account summary to ensure all calculations are up to date
        maybeRefreshAccountSummary();
      } else {
        logger.debug('AccountDetails: Received account ticker update but conditions not met', {
          hasData: !!data,
          type: data?.type,
          accountId: data?.account_id,
          expectedAccountId: parseInt(accountId),
          isMatch: data && data.type === 'risk_update' && data.account_id === parseInt(accountId)
        });
      }
    });

    // Test the socket connection
    logger.debug('AccountDetails: Socket subscription setup complete, checking connection...');
    const socket = require('../services/socketService').getSocket();
    logger.debug('AccountDetails: Socket connection status', {
      connected: socket?.connected,
      id: socket?.id
    });

    return () => {
      logger.debug('Cleaning up account ticker update subscription in AccountDetails');
      unsubscribe();
    };
    // applyRiskCacheToTicker/maybeRefreshAccountSummary/updateTickerRiskCache intentionally
    // omitted: this WebSocket subscription is scoped to accountId and re-subscribing on every
    // render of these large, frequently-changing helpers would be far more disruptive than
    // reading their current closures from inside the long-lived handler.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  // Update local refresh settings when localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      const savedInterval = localStorage.getItem('refreshInterval');
      const savedEnabled = localStorage.getItem('autoRefreshEnabled');
      
      if (savedInterval) {
        setRefreshInterval(Number(savedInterval));
      }
      
      if (savedEnabled !== null) {
        setAutoRefreshEnabled(JSON.parse(savedEnabled));
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Save sort configuration when it changes
  useEffect(() => {
    if (accountId && sortConfig) {
      localStorage.setItem(`sortConfig-account-${accountId}`, JSON.stringify(sortConfig));
    }
  }, [sortConfig, accountId]);

  useEffect(() => {
    if (!accountId) {
      return;
    }
    ensureNextPlanOverridesMap(accountId);
  }, [accountId, ensureNextPlanOverridesMap]);

  const fetchAccountDetails = async (showErrors = true, options = {}) => {
    const { skipInitialTableLoading = false } = options;
    const requestId = ++latestDetailsRequestRef.current;
    const tickerSnapshotRequestId = getNextTickerSnapshotRequestId();
    const requestedAccountId = accountId;
    const isStale = () => latestDetailsRequestRef.current !== requestId || accountId !== requestedAccountId;
    if (!skipInitialTableLoading) {
      setTableLoading(true);
    }
    if (showErrors) {
      setError('');
    }
    
    try {
      // First, get account data (this is fast and needed for basic info)
      const response = await getAccount(accountId);
      const accountData = response.data;
      if (isStale()) return;
      setAccount(accountData);
      
      logger.debug('==== DAYS DEBUG: ACCOUNT RESPONSE DATA ====');
      logger.debug('Account data from API', accountData);
      logger.debug('First ticker days_held check', accountData.tickers?.[0]?.days_held);
      logger.debug('Days_held values in tickers', (accountData.tickers || []).map(t => ({
        symbol: t.symbol,
        days_held: t.days_held,
        hasOwnProperty: t.hasOwnProperty?.('days_held')
      })));
      logger.debug('==== END DAYS DEBUG ====');

      const refreshFlagSet = isAccountTickersRefreshRequired(requestedAccountId);

      logger.debug('[AccountDetails] Fetching tickers from database', {
        accountId: requestedAccountId
      });
      const tickersResponse = await getAccountTickers(accountId);
      if (isStale()) return;
      const tickersData = Array.isArray(tickersResponse?.data) ? tickersResponse.data : [];
      if (refreshFlagSet) {
        clearAccountTickersRefreshFlag(requestedAccountId);
      }

      // Clear strategy badge cache AND shields cache when fetching fresh data from DB
      // This ensures stale cached strategy values don't persist after database updates
      const strategyCacheKey = `strategyBadgesCache-${requestedAccountId}`;
      const shieldsCacheKey = `tickerShieldsCache-${requestedAccountId}`;
      try {
        localStorage.removeItem(strategyCacheKey);
        localStorage.removeItem(shieldsCacheKey);
        logger.debug('[AccountDetails] Cleared strategy and shields caches for fresh data load', {
          strategyCacheKey,
          shieldsCacheKey
        });
      } catch (error) {
        logger.warn('[AccountDetails] Failed to clear strategy/shields cache', error);
      }
      if (isStale()) return;
      
      // After loading tickers, recalculate risk values to ensure they're current
      try {
        logger.debug('Recalculating risk values for account...');
        const riskResponse = await recalculateAccountShields(accountId);
        if (isStale()) return;
        logger.debug('Risk values recalculation result', riskResponse.data);
        const recalcResults = Array.isArray(riskResponse?.data?.results)
          ? riskResponse.data.results
          : [];
        if (recalcResults.length > 0) {
          recalcResults.forEach(result => {
            if (!result || !result.ticker_id) {
              return;
            }
            updateTickerRiskCache(result.ticker_id, {
              shield_risk_percentage: result.shield_risk_percentage,
              shield_risk_amount: result.shield_risk_amount
            }, { source: 'server' });
          });
        }
      } catch (riskError) {
        logger.warn('Failed to recalculate risk values on page load', riskError);
        // Don't fail the page load if risk recalculation fails
      }
      
      // Find the most recent ticker timestamp to use for lastRefreshTime
      let mostRecentDate = null;
      if (tickersData && tickersData.length > 0) {
        for (const ticker of tickersData) {
          if (ticker.last_updated) {
            try {
              const tickerDate = new Date(ticker.last_updated);
              if (!isNaN(tickerDate) && tickerDate <= new Date()) {
                if (!mostRecentDate || tickerDate > mostRecentDate) {
                  mostRecentDate = tickerDate;
                }
              }
            } catch (e) {
              logger.error(`Error parsing ticker timestamp for ${ticker.symbol}`, e);
            }
          }
        }
      }
      
      // Set lastRefreshTime based on ticker data or response
      if (isStale()) return;
      if (mostRecentDate) {
        logger.debug('Setting lastRefreshTime from ticker timestamp', { mostRecentDate });
        setLastRefreshTime(mostRecentDate);
      } else if (accountData.latest_update_time) {
        logger.debug('Setting lastRefreshTime from accountData', {
          latestUpdateTime: accountData.latest_update_time
        });
        try {
          const updateTime = new Date(accountData.latest_update_time);
          if (!isNaN(updateTime) && updateTime <= new Date()) {
            setLastRefreshTime(updateTime);
          }
        } catch (e) {
          logger.error('Error parsing timestamp from account data', e);
        }
      }
      
      // Check for market info from available sources
      if (isStale()) return;
      if (accountData.market_closed !== undefined) {
        logger.debug('Setting marketInfo from account data', {
          market_closed: accountData.market_closed,
          market_info: accountData.market_info
        });
        setMarketInfo({
          market_closed: accountData.market_closed,
          market_info: accountData.market_info || {}
        });
      }
      
      // Filter out tickers with negligible shares (tiny remainders from "sell all")
      const filteredTickers = tickersData.filter(ticker => {
        // Keep tickers without share information (like cash entries)
        if (ticker.shares === undefined) return true;

        // Filter out tickers with extremely small share values (less than MIN_POSITION_SHARES)
        return ticker.shares >= MIN_POSITION_SHARES;
      });

      // Track any tickers that were filtered out locally so stale snapshots don't reintroduce them
      tickersData.forEach(ticker => {
        if (ticker?.shares !== undefined && ticker.shares < MIN_POSITION_SHARES) {
          const tickerLastUpdated = ticker.last_updated || ticker.updated_at || null;
          markTickerRecentlyRemoved(ticker.symbol || ticker.ticker_id, requestedAccountId, tickerLastUpdated);
          removeTickerFromCache(requestedAccountId, ticker.symbol || ticker.ticker_id);
        }
      });

      const normalizedTickers = filteredTickers.map(ticker => {
        const tradeId = ticker.current_trade_id;
        const tradeGain = ticker.current_trade_gain_pct;

        let normalizedTradeId = null;
        if (tradeId !== null && tradeId !== undefined) {
          const parsedId = Number(tradeId);
          normalizedTradeId = Number.isFinite(parsedId) ? parsedId : tradeId;
        }

        let normalizedTradeGain = null;
        if (tradeGain !== null && tradeGain !== undefined) {
          const parsedGain = Number(tradeGain);
          normalizedTradeGain = Number.isFinite(parsedGain) ? parsedGain : tradeGain;
        }

        return {
          ...ticker,
          current_trade_id: normalizedTradeId,
          current_trade_gain_pct: normalizedTradeGain
        };
      });

      if (isStale()) return;
      updateTickersWithFilteredData(normalizedTickers, requestedAccountId, {
        source: 'server',
        snapshotRequestId: tickerSnapshotRequestId
      });

      // Set loading to false here so the table shows immediately
      setLoading(false);
      setTableLoading(false);
      
      // Now load additional data in parallel (non-blocking)
      const loadBackgroundData = async () => {
        try {
          // Warm accountAggregate right away (fire-and-forget) so the stats
          // cards have a NavigationPanel-matching snapshot ready before the
          // first poll tick lands, rather than only recomputing client-side
          // from tickers/account until then. Not required for correctness --
          // that client computation is accurate immediately after this same
          // fetchAccountDetails call, since account/tickers were just
          // fetched together -- just closes the gap sooner.
          syncAccountAggregate();

          // Load all background data in parallel
          const [transactionsResponse, availableTickersResponse, closedTradesResponse] = await Promise.all([
            getTransactions(accountId),
            getTickers(),
            getTrades({ account_id: accountId, status: 'CLOSED', per_page: 1000 })
          ]);

          if (isStale()) return;
          setTransactions(transactionsResponse.data);
          setAvailableTickers(availableTickersResponse.data);

          // Calculate Post() count: closed trades with empty post analysis
          try {
            const closedTrades = closedTradesResponse?.data?.trades || [];
            const missingCount = closedTrades.reduce((acc, tr) => {
              const analysis = (tr.post_analysis_analysis || '').trim();
              const lessons = (tr.post_analysis_lessons || '').trim();
              const rating = tr.post_analysis_rating;
              const missing = !analysis && !lessons && (rating === null || rating === undefined);
              return acc + (missing ? 1 : 0);
            }, 0);
            if (isStale()) return;
            setPostAnalysisTodoCount(missingCount);
          } catch (e) {
            logger.warn('Failed to compute post analysis count', e);
          }
          
          // Debug transaction data (non-critical, don't let it affect main error state)
          try {
            const debugResponse = await fetch(`/api/accounts/${accountId}/debug-transactions`);
            if (!debugResponse.ok) {
              logger.warn(`Debug transactions request failed with status: ${debugResponse.status}`);
            } else {
              const textResponse = await debugResponse.text();
              try {
                const debugData = JSON.parse(textResponse);
                logger.debug('==== TRANSACTION DEBUG DATA ====');
                logger.debug('Transaction debug data', debugData);
                logger.debug('==== END TRANSACTION DEBUG ====');
              } catch (jsonError) {
                logger.error('Error parsing debug transactions response as JSON', jsonError);
                logger.error('Server response text', textResponse.substring(0, 200) + '...');
              }
            }
          } catch (err) {
            logger.error('Error fetching debug transactions', err);
          }
          
          // If there are tickers but days_held is missing, add a fallback calculation
          if (filteredTickers.length > 0 && filteredTickers.some(t => t.days_held === undefined)) {
            const updatedTickers = filteredTickers.map(ticker => {
              if (ticker.days_held === undefined) {
                logger.debug(`Missing days_held for ticker ${ticker.symbol}, setting to 0`);
                return { ...ticker, days_held: 0 };
              }
              return ticker;
            });
            if (isStale()) return;
            tickersAccountIdRef.current = accountId;
            // Route through the merge helper instead of replacing state outright:
            // updatedTickers here is a snapshot from this async load's start, so a
            // plain setTickers(...) would clobber any rating/shield edit the user
            // made in the meantime (e.g. right after landing on the page, before
            // this days_held backfill resolves). updateTickersWithFilteredData
            // merges against the latest state and already re-applies the risk
            // cache per ticker, so the manual updateTickerRiskCache loop above is
            // no longer needed here.
            //
            // Pass the SAME snapshotRequestId this fetchAccountDetails call already
            // generated for its primary ticker update (above). This branch fires from
            // an unawaited background chain (loadBackgroundData) that can take a while
            // (Promise.all of 3 requests + a sequential debug fetch), so by the time it
            // resolves, an unrelated poll-driven full refresh (see pendingFullRefreshRef
            // -- set after every transaction) may have already applied a newer snapshot.
            // Without a snapshotRequestId here, updateTickersWithFilteredData's staleness
            // gate is skipped entirely (Number.isFinite(undefined) is false), so this
            // belated, pre-transaction-adjacent snapshot would unconditionally overwrite
            // that newer state -- exactly the "buy/sell looks right, then flips back"
            // symptom this account details page has otherwise guarded against everywhere
            // else it touches ticker state.
            updateTickersWithFilteredData(updatedTickers, accountId, {
              source: 'server',
              snapshotRequestId: tickerSnapshotRequestId
            });
          } else {
            logger.debug('All tickers have days_held values', filteredTickers.map(t => ({ symbol: t.symbol, days_held: t.days_held })));
          }

        } catch (err) {
          const isAbortError =
            err?.code === 'ECONNABORTED' ||
            err?.message === 'Request aborted' ||
            err?.name === 'CanceledError';

          if (isAbortError) {
            logger.debug('Background data loading aborted or canceled', {
              code: err?.code,
              message: err?.message
            });
          } else {
            logger.error('Error loading background data', err);
          }
          // Don't set error state for background data loading failures
        }
      };
      
      // Start background data loading
      loadBackgroundData();
      
      if (showErrors) {
        setError('');
      }
    } catch (err) {
      logger.error('Error fetching account details', err);
      if (showErrors) {
        logger.debug('Setting error state due to fetchAccountDetails failure');
        setError('Failed to load account details. Please try again.');
      } else {
        logger.debug('Suppressing error state for background refresh');
      }
      if (isStale()) return;
      setLoading(false);
      setTableLoading(false);
    }
  };
  
  const handleRefreshPrices = () => {
    const requestedAccountId = currentAccountIdRef.current;
    setRefreshLoading(true);
    setError(''); // Clear any existing errors when starting refresh
    // Always force update when manually refreshing, regardless of market hours
    updateTickerPrices(true)
      .then(response => {
        if (String(currentAccountIdRef.current) !== String(requestedAccountId)) {
          logger.debug('AccountDetails: Ignoring manual price refresh result for stale account', {
            requestedAccountId,
            currentAccountId: currentAccountIdRef.current
          });
          return;
        }

        const data = response.data;
        logger.debug('Price update response', data);
        
        if (data.market_closed !== undefined) {
          setMarketInfo({
            market_closed: data.market_closed,
            market_info: data.market_info || {}
          });
        }
        
        if (data && data.updated_tickers && data.updated_tickers.length > 0) {
          logger.debug('Directly updating ticker prices from API response');
          
          // Create a map of ticker IDs to updated ticker data for quick lookup
          const updatedTickersMap = {};
          data.updated_tickers.forEach(updatedTicker => {
            if (updatedTicker.ticker_id) {
              updatedTickersMap[updatedTicker.ticker_id] = updatedTicker;
            }
          });
          
          // Try to extract the actual database timestamp directly from tickers
          let mostRecentDate = null;
          for (const ticker of data.updated_tickers) {
            if (ticker.last_updated) {
              try {
                const tickerDate = new Date(ticker.last_updated);
                if (!isNaN(tickerDate) && tickerDate <= new Date()) {
                  if (!mostRecentDate || tickerDate > mostRecentDate) {
                    mostRecentDate = tickerDate;
                  }
                }
              } catch (e) {
                logger.error(`Error parsing ticker timestamp for ${ticker.symbol}`, e);
              }
            }
          }
          
          // Update the last refresh time
          if (mostRecentDate) {
            setLastRefreshTime(mostRecentDate);
          } else if (data.latest_update_time) {
            setLastRefreshTime(new Date(data.latest_update_time));
          } else {
            setLastRefreshTime(new Date());
          }
          
          // Apply partial metric updates against current state so stale closure snapshots
          // cannot overwrite newer fields from a prior full refresh.
          applyTickerMetricUpdates(updatedTickersMap, requestedAccountId, {
            source: 'server'
          });

          // Keep account summary values current without reloading and replacing the table snapshot.
          maybeRefreshAccountSummary();
          
          return;
        }
        
        // Fallback to the original method if direct updates aren't possible
        // Try to extract the actual database timestamp directly from tickers
        if (data.updated_tickers && data.updated_tickers.length > 0) {
          // Find the most recent ticker timestamp
          let mostRecentDate = null;
          let mostRecentTimeString = null;
          
          for (const ticker of data.updated_tickers) {
            if (ticker.last_updated) {
              try {
                const tickerDate = new Date(ticker.last_updated);
                if (!isNaN(tickerDate) && tickerDate <= new Date()) {
                  if (!mostRecentDate || tickerDate > mostRecentDate) {
                    mostRecentTimeString = ticker.last_updated;
                    mostRecentDate = tickerDate;
                    logger.debug(`Found valid DB timestamp from ticker ${ticker.symbol}: ${mostRecentTimeString}`);
                  }
                }
              } catch (e) {
                logger.error(`Error parsing ticker timestamp for ${ticker.symbol}`, e);
              }
            }
          }

          // Use the most recent ticker timestamp if found
          if (mostRecentDate) {
            logger.debug('Using actual database timestamp from ticker', mostRecentTimeString);
            setLastRefreshTime(mostRecentDate);

            // Refresh account details regardless of market status
            if (currentAccountIdRef.current === String(accountId)) {
              return fetchAccountDetails(false, { forceTickerReload: true }); // Don't show errors for this refresh either
            }
            return;
          }
        }

        // Fallback to latest_update_time if no valid ticker timestamp found
        if (data.latest_update_time) {
          logger.debug('Using server timestamp from response', data.latest_update_time);
          try {
            const serverTime = new Date(data.latest_update_time);
            logger.debug('Parsed server time', serverTime);

            // Validate the timestamp is valid
            if (!isNaN(serverTime.getTime())) {
              setLastRefreshTime(serverTime);
            } else {
              logger.error('Invalid server timestamp, using current time');
              setLastRefreshTime(new Date());
            }
          } catch (err) {
            logger.error('Error parsing server timestamp', err);
            setLastRefreshTime(new Date());
          }
        } else {
          logger.debug('No server timestamp available, using current time');
          setLastRefreshTime(new Date());
        }
        
        // Refresh account details regardless of market status
        if (currentAccountIdRef.current === String(accountId)) {
          return fetchAccountDetails(false, { forceTickerReload: true }); // Don't show errors for automatic refresh
        }
        return;
      })
      .catch(err => {
        if (String(currentAccountIdRef.current) !== String(requestedAccountId)) {
          logger.debug('AccountDetails: Ignoring manual price refresh error for stale account', {
            requestedAccountId,
            currentAccountId: currentAccountIdRef.current
          });
          return;
        }
        logger.error('Error updating prices', err);
        setError('Failed to update prices. Please try again later.');
        setLastRefreshTime(new Date()); // Still update the time on error for UX
      })
      .finally(() => {
        setRefreshLoading(false);
      });
  };
  
  const applyTickerMetricUpdates = (updatedTickersMap, sourceAccountId = currentAccountIdRef.current, options = {}) => {
    if (!updatedTickersMap || Object.keys(updatedTickersMap).length === 0) {
      logger.debug('[AccountDetails] No ticker metric updates to apply');
      return;
    }

    if (sourceAccountId !== undefined && sourceAccountId !== null) {
      const sourceIdStr = String(sourceAccountId);
      const currentIdStr = String(currentAccountIdRef.current);
      if (sourceIdStr !== currentIdStr) {
        logger.debug('[AccountDetails] Skipping partial ticker update for inactive account', {
          sourceAccountId: sourceIdStr,
          currentAccountId: currentIdStr
        });
        return;
      }
    }

    const effectiveAccountId = sourceAccountId ?? currentAccountIdRef.current;
    tickersAccountIdRef.current = effectiveAccountId;
    const sourceType = options.source || 'server';

    setTickers(prevTickers => {
      let updatedCount = 0;

      const nextTickers = prevTickers.map(existingTicker => {
        let update = updatedTickersMap[existingTicker.ticker_id];
        if (!update) {
          return existingTicker;
        }

        // The 'polling' source is the aggregated-across-accounts endpoint
        // (getAggregatedTickers / GET /api/tickers/aggregated), keyed here by
        // ticker_id -- the same id for every account that holds this symbol.
        // current_price/previous_close are legitimately symbol-level and
        // safe to trust from it, but current_value/today_gain_amount/
        // total_gain/total_gain_pct there are summed across every account
        // holding the symbol, not scoped to this one. Stripping them here
        // (rather than gating each read site below) lets the existing
        // "recompute from this account's own shares/cost_basis" fallback
        // further down fill them in correctly instead of silently trusting
        // a cross-account total. (today_gain_percentage is left alone: it's
        // a price ratio that's the same regardless of how many shares/
        // accounts are summed, so aggregation doesn't distort it.)
        if (sourceType === 'polling') {
          const { today_gain_amount, total_gain, total_gain_pct, current_value, ...safeUpdate } = update;
          update = safeUpdate;
        }

        // Check if shields were recently edited for this ticker
        const SHIELD_EDIT_PROTECTION_WINDOW = 20000; // 20 seconds
        const recentShieldEditTime = recentShieldEditsRef.current.get(existingTicker.ticker_id);
        const hasRecentShieldEdit = recentShieldEditTime &&
                                    (Date.now() - recentShieldEditTime) < SHIELD_EDIT_PROTECTION_WINDOW;

        // Check if rating was recently edited for this ticker
        const RATING_EDIT_PROTECTION_WINDOW = 20000; // 20 seconds
        const recentRatingEditTime = recentRatingEditsRef.current.get(existingTicker.ticker_id);
        const hasRecentRatingEdit = recentRatingEditTime &&
                                    (Date.now() - recentRatingEditTime) < RATING_EDIT_PROTECTION_WINDOW;

        // Detect price-only updates (has price fields but no meaningful risk data)
        const isPriceOnlyUpdate = (
          // Has price-related fields
          (Object.prototype.hasOwnProperty.call(update, 'current_price') ||
           Object.prototype.hasOwnProperty.call(update, 'effective_price') ||
           Object.prototype.hasOwnProperty.call(update, 'previous_close')) &&
          // Does NOT have meaningful risk data
          (!Object.prototype.hasOwnProperty.call(update, 'shield_risk_percentage') ||
           update.shield_risk_percentage === undefined ||
           update.shield_risk_percentage === null) &&
          (!Object.prototype.hasOwnProperty.call(update, 'shield_risk_amount') ||
           update.shield_risk_amount === undefined ||
           update.shield_risk_amount === null) &&
          (!Object.prototype.hasOwnProperty.call(update, 'armor') ||
           update.armor === undefined ||
           update.armor === null)
        );

        // If recent shield edit OR price-only update, skip risk field processing
        const shouldSkipRiskFields = hasRecentShieldEdit || isPriceOnlyUpdate;

        if (shouldSkipRiskFields && hasRecentShieldEdit) {
          logger.debug('[AccountDetails] Protecting risk data for recently edited shields', {
            tickerId: existingTicker.ticker_id,
            symbol: existingTicker.symbol,
            editAge: Date.now() - recentShieldEditTime
          });
        }

        if (shouldSkipRiskFields && isPriceOnlyUpdate && !hasRecentShieldEdit) {
          logger.debug('[AccountDetails] Skipping risk field updates for price-only update', {
            tickerId: existingTicker.ticker_id,
            symbol: existingTicker.symbol,
            updateKeys: Object.keys(update)
          });
        }

        const toFiniteNumber = (value) => {
          if (value === null || value === undefined || value === '') {
            return null;
          }
          const numeric = Number(value);
          return Number.isFinite(numeric) ? numeric : null;
        };

        const sharesValue = typeof existingTicker.shares === 'number'
          ? existingTicker.shares
          : parseFloat(existingTicker.shares) || 0;

        let draft = existingTicker;
        let tickerChanged = false;

        const ensureDraft = () => {
          if (!tickerChanged) {
            draft = { ...existingTicker };
            tickerChanged = true;
          }
        };

        const assignIfChanged = (key, value) => {
          if (value === undefined) {
            return;
          }
          if (!Object.is(existingTicker[key], value)) {
            ensureDraft();
            draft[key] = value;
          }
        };

        const effectivePriceFromUpdate = toFiniteNumber(update.effective_price);
        const currentPriceFromUpdate = toFiniteNumber(update.current_price);
        const manualPriceFromUpdate = toFiniteNumber(update.manual_price);
        const hasManualPriceUpdate = Object.prototype.hasOwnProperty.call(update, 'manual_price');

        const existingEffectivePrice = toFiniteNumber(existingTicker.effective_price);
        const existingCurrentPrice = toFiniteNumber(existingTicker.current_price);

        const effectivePrice = effectivePriceFromUpdate ?? currentPriceFromUpdate ?? existingEffectivePrice ?? existingCurrentPrice;

        if (effectivePriceFromUpdate !== null) {
          assignIfChanged('effective_price', effectivePriceFromUpdate);
        }

        if (Object.prototype.hasOwnProperty.call(update, 'current_price')) {
          assignIfChanged('current_price', currentPriceFromUpdate !== null ? currentPriceFromUpdate : update.current_price);
        } else if (effectivePriceFromUpdate !== null && effectivePriceFromUpdate !== existingCurrentPrice) {
          assignIfChanged('current_price', effectivePriceFromUpdate);
        }

        if (hasManualPriceUpdate) {
          assignIfChanged('manual_price', manualPriceFromUpdate !== null ? manualPriceFromUpdate : update.manual_price);
        }

        const hasPreviousCloseUpdate = Object.prototype.hasOwnProperty.call(update, 'previous_close') ||
          Object.prototype.hasOwnProperty.call(update, 'pre_close');
        const previousCloseFromUpdate = toFiniteNumber(update.previous_close ?? update.pre_close);
        const existingPreviousClose = toFiniteNumber(existingTicker.previous_close ?? existingTicker.pre_close);
        const previousCloseForCalc = previousCloseFromUpdate ?? existingPreviousClose;

        if (hasPreviousCloseUpdate) {
          assignIfChanged('previous_close', previousCloseFromUpdate);
          assignIfChanged('pre_close', previousCloseFromUpdate);
        }

        if (effectivePrice !== null && Number.isFinite(effectivePrice) && Number.isFinite(sharesValue)) {
          const currentValue = effectivePrice * sharesValue;
          if (Number.isFinite(currentValue)) {
            assignIfChanged('current_value', currentValue);
          }
        }

        const todayGainAmountFromUpdate = toFiniteNumber(update.today_gain_amount);
        const todayGainPctFromUpdate = toFiniteNumber(update.today_gain_percentage);

        let todayGainAmount = todayGainAmountFromUpdate;
        if (todayGainAmount === null && previousCloseForCalc !== null && previousCloseForCalc !== undefined && Number.isFinite(sharesValue) && effectivePrice !== null) {
          todayGainAmount = (effectivePrice - previousCloseForCalc) * sharesValue;
        }

        if (todayGainAmount !== null && Number.isFinite(todayGainAmount)) {
          assignIfChanged('today_gain_amount', normalizeNearZero(todayGainAmount));
        }

        // Compute today_gain_percentage from price and previous close (derivable from price data)
        const todayGainPctComputed = (previousCloseForCalc !== null && previousCloseForCalc > 0 && effectivePrice !== null && Number.isFinite(effectivePrice))
          ? ((effectivePrice - previousCloseForCalc) / previousCloseForCalc) * 100
          : null;
        const todayGainPctFinal = (todayGainPctFromUpdate !== null && Number.isFinite(todayGainPctFromUpdate))
          ? todayGainPctFromUpdate
          : todayGainPctComputed;
        if (todayGainPctFinal !== null && Number.isFinite(todayGainPctFinal)) {
          assignIfChanged('today_gain_percentage', normalizeNearZero(todayGainPctFinal));
        }

        // Handle gain fields from server response
        const gainAmountFields = ['total_gain', 'unrealized_gain', 'trade_gain', 'risk_gain', 'risk_gain_amount', 'trisk_gain'];
        const gainPercentageFields = ['total_gain_pct', 'trade_gain_pct'];

        gainAmountFields.forEach(field => {
          if (Object.prototype.hasOwnProperty.call(update, field)) {
            const numericValue = toFiniteNumber(update[field]);
            if (numericValue !== null) {
              assignIfChanged(field, numericValue);
            }
          }
        });

        gainPercentageFields.forEach(field => {
          if (Object.prototype.hasOwnProperty.call(update, field)) {
            const numericValue = toFiniteNumber(update[field]);
            if (numericValue !== null) {
              assignIfChanged(field, numericValue);
            }
          }
        });

        // Recompute cost-basis-derived gain fields from the new price when the server
        // did not provide them (price-only update). This keeps gain$/% columns live.
        if (effectivePrice !== null && Number.isFinite(effectivePrice) && Number.isFinite(sharesValue)) {
          const costBasisPerShare = toFiniteNumber(existingTicker.cost_basis);
          if (costBasisPerShare !== null && costBasisPerShare > 0) {
            const gainDollar = (effectivePrice - costBasisPerShare) * sharesValue;
            const gainPct = ((effectivePrice - costBasisPerShare) / costBasisPerShare) * 100;

            if (Number.isFinite(gainDollar)) {
              if (!Object.prototype.hasOwnProperty.call(update, 'total_gain')) {
                assignIfChanged('total_gain', normalizeNearZero(gainDollar));
              }
              if (!Object.prototype.hasOwnProperty.call(update, 'unrealized_gain')) {
                assignIfChanged('unrealized_gain', normalizeNearZero(gainDollar));
              }
              // current_trade_gain_loss is intentionally NOT approximated here: it
              // reflects the currently-open trade's own LIFO-tracked gain
              // (active_trade.total_gain_loss on the backend), which can differ from
              // this position's average cost basis (e.g. after partial sells/adds).
              // Overwriting it with a cost-basis approximation on every price tick
              // was clobbering the correct backend value between full refetches,
              // making the Trade$ column drift from the real trade.
            }
            if (Number.isFinite(gainPct)) {
              if (!Object.prototype.hasOwnProperty.call(update, 'total_gain_pct')) {
                assignIfChanged('total_gain_pct', normalizeNearZero(gainPct));
              }
              // current_trade_gain_pct: same reasoning as current_trade_gain_loss
              // above -- leave it at its last known-good (backend-computed) value
              // until the next full refetch instead of approximating from cost basis.
            }
          }
        }

        const riskPayload = {};
        const hasShieldPctProp = !shouldSkipRiskFields &&
                                 Object.prototype.hasOwnProperty.call(update, 'shield_risk_percentage');
        let appliedShieldPctUpdate = false;
        if (hasShieldPctProp) {
          const shieldPctRaw = update.shield_risk_percentage;
          const shieldPctNumeric = toFiniteNumber(shieldPctRaw);

          if (shieldPctRaw === null) {
            assignIfChanged('shield_risk_percentage', null);
            riskPayload.shield_risk_percentage = null;
            appliedShieldPctUpdate = true;
          } else if (shieldPctNumeric !== null) {
            assignIfChanged('shield_risk_percentage', shieldPctNumeric);
            riskPayload.shield_risk_percentage = shieldPctNumeric;
            appliedShieldPctUpdate = true;
          }
        }

        const hasShieldAmountProp = !shouldSkipRiskFields &&
                                    Object.prototype.hasOwnProperty.call(update, 'shield_risk_amount');
        let appliedShieldAmountUpdate = false;
        if (hasShieldAmountProp) {
          const shieldAmountRaw = update.shield_risk_amount;
          const shieldAmountNumeric = toFiniteNumber(shieldAmountRaw);

          if (shieldAmountRaw === null) {
            assignIfChanged('shield_risk_amount', null);
            riskPayload.shield_risk_amount = null;
            appliedShieldAmountUpdate = true;
          } else if (shieldAmountNumeric !== null) {
            assignIfChanged('shield_risk_amount', shieldAmountNumeric);
            riskPayload.shield_risk_amount = shieldAmountNumeric;
            appliedShieldAmountUpdate = true;
          }
        }

        if (!shouldSkipRiskFields && Object.prototype.hasOwnProperty.call(update, 'armor')) {
          assignIfChanged('armor', update.armor);
          riskPayload.armor = update.armor;
        }

        if (Object.prototype.hasOwnProperty.call(update, 'last_updated')) {
          assignIfChanged('last_updated', update.last_updated);
        }

        // Only update rating if explicitly provided and not recently edited by user
        if (Object.prototype.hasOwnProperty.call(update, 'rating') && !hasRecentRatingEdit) {
          // Only update if the value is non-null/non-undefined (don't overwrite with empty values)
          if (update.rating !== null && update.rating !== undefined) {
            assignIfChanged('rating', update.rating);
          }
        } else if (hasRecentRatingEdit) {
          logger.debug('[AccountDetails] Protecting recently edited rating from server update', {
            tickerId: existingTicker.ticker_id,
            symbol: existingTicker.symbol,
            editAge: Date.now() - recentRatingEditTime,
            existingRating: existingTicker.rating,
            serverRating: update.rating
          });
        }

        if (Object.keys(riskPayload).length > 0) {
          updateTickerRiskCache(existingTicker.ticker_id, riskPayload, { source: sourceType });
        }

        const cachedRisk = tickerRiskCacheRef.current.get(existingTicker.ticker_id);
        if (cachedRisk) {
          if ((!hasShieldPctProp || !appliedShieldPctUpdate) && Object.prototype.hasOwnProperty.call(cachedRisk, 'shield_risk_percentage')) {
            const cachedShieldPct = cachedRisk.shield_risk_percentage;
            // Only apply cached value if it's not null/undefined, to avoid overwriting valid existing values
            if (cachedShieldPct !== null && cachedShieldPct !== undefined) {
              assignIfChanged('shield_risk_percentage', cachedShieldPct);
            }
          }
          if ((!hasShieldAmountProp || !appliedShieldAmountUpdate) && Object.prototype.hasOwnProperty.call(cachedRisk, 'shield_risk_amount')) {
            const cachedShieldAmount = cachedRisk.shield_risk_amount;
            // Only apply cached value if it's not null/undefined, to avoid overwriting valid existing values
            if (cachedShieldAmount !== null && cachedShieldAmount !== undefined) {
              assignIfChanged('shield_risk_amount', cachedShieldAmount);
            }
          }
          if (!Object.prototype.hasOwnProperty.call(update, 'armor') && Object.prototype.hasOwnProperty.call(cachedRisk, 'armor')) {
            const cachedArmor = cachedRisk.armor;
            // Only apply cached armor if it's not null/undefined
            if (cachedArmor !== null && cachedArmor !== undefined) {
              assignIfChanged('armor', cachedArmor);
            }
          }
        }

        if (tickerChanged) {
          updatedCount += 1;
          return applyRiskCacheToTicker(draft);
        }

        return applyRiskCacheToTicker(existingTicker);
      });

      if (updatedCount === 0) {
        return prevTickers;
      }

      logger.debug('[AccountDetails] Applied partial ticker metric updates', {
        updatedCount,
        accountId: effectiveAccountId
      });

      return nextTickers;
    });
  };

  useEffect(() => {
    const unsubscribe = subscribeToTickerUpdates((data) => {
      if (data?.market_closed !== undefined && data?.market_info) {
        setMarketInfo({
          market_closed: data.market_closed,
          market_info: data.market_info
        });
      }

      if (data?.latest_update_time) {
        try {
          const updateTime = new Date(data.latest_update_time);
          if (!isNaN(updateTime) && updateTime <= new Date()) {
            setLastRefreshTime(updateTime);
          }
        } catch (error) {
          logger.debug('AccountDetails: Failed to parse direct ticker update timestamp', error);
        }
      }

      const incomingTickers = Array.isArray(data?.updated_tickers) ? data.updated_tickers : [];
      const currentTickers = tickersRef.current;
      if (incomingTickers.length === 0 || currentTickers.length === 0) {
        return;
      }

      const tickerIdBySymbol = new Map();
      currentTickers.forEach((ticker) => {
        if (!ticker) {
          return;
        }
        if (ticker.ticker_id !== undefined && ticker.ticker_id !== null) {
          tickerIdBySymbol.set(String(ticker.ticker_id), ticker.ticker_id);
        }
        if (ticker.symbol) {
          tickerIdBySymbol.set(String(ticker.symbol).toUpperCase(), ticker.ticker_id);
        }
      });

      const updatedTickersMap = {};
      incomingTickers.forEach((updatedTicker) => {
        if (!updatedTicker || typeof updatedTicker !== 'object') {
          return;
        }

        const mappedTickerId = updatedTicker.ticker_id
          ?? tickerIdBySymbol.get(String(updatedTicker.symbol || '').toUpperCase())
          ?? tickerIdBySymbol.get(String(updatedTicker.id ?? ''));

        if (mappedTickerId === undefined || mappedTickerId === null) {
          return;
        }

        updatedTickersMap[mappedTickerId] = {
          ...updatedTicker,
          ticker_id: mappedTickerId
        };
      });

      if (Object.keys(updatedTickersMap).length > 0) {
        applyTickerMetricUpdates(updatedTickersMap, currentAccountIdRef.current, {
          source: 'polling'
        });
      }
    }, { autoReactivate: false });

    return () => {
      unsubscribe();
    };
    // applyTickerMetricUpdates intentionally omitted: this is a polling subscription scoped
    // to accountId; re-subscribing whenever this large helper's closure changes would be more
    // disruptive than reading its current closure from inside the long-lived handler.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  // Also ensure any direct ticker update method filters properly
  const updateTickersWithFilteredData = (tickersData, sourceAccountId = currentAccountIdRef.current, options = {}) => {
    if (!tickersData) return;

    if (sourceAccountId !== undefined && sourceAccountId !== null) {
      const sourceIdStr = String(sourceAccountId);
      const currentIdStr = String(currentAccountIdRef.current);
      if (sourceIdStr !== currentIdStr) {
        logger.debug('[AccountDetails] Skipping ticker update for inactive account', {
          sourceAccountId: sourceIdStr,
          currentAccountId: currentIdStr
        });
        return;
      }
    }

    const effectiveAccountId = sourceAccountId ?? currentAccountIdRef.current;
    tickersAccountIdRef.current = effectiveAccountId;
    const sourceType = options.source || 'server';
    const snapshotRequestId = options.snapshotRequestId;

    if (Number.isFinite(snapshotRequestId)) {
      if (snapshotRequestId < latestTickerSnapshotAppliedRef.current) {
        logger.debug('[AccountDetails] Skipping stale ticker snapshot update', {
          accountId: effectiveAccountId,
          snapshotRequestId,
          latestAppliedSnapshotId: latestTickerSnapshotAppliedRef.current,
          source: sourceType
        });
        return;
      }
      latestTickerSnapshotAppliedRef.current = snapshotRequestId;
    }

    // Filter out tickers with negligible shares (tiny remainders from "sell all")
    const filteredTickers = tickersData.filter(ticker => {
      // Keep tickers without share information (like cash entries)
      if (ticker.shares === undefined) return true;

      // Filter out tickers with extremely small share values (less than MIN_POSITION_SHARES)
      return ticker.shares >= MIN_POSITION_SHARES;
    });

      // Mark any tickers filtered out locally so stale API responses don't reintroduce them immediately
      tickersData.forEach(ticker => {
        if (ticker?.shares !== undefined && ticker.shares < MIN_POSITION_SHARES) {
          const tickerLastUpdated = ticker.last_updated || ticker.updated_at || null;
          markTickerRecentlyRemoved(ticker.symbol || ticker.ticker_id, effectiveAccountId, tickerLastUpdated);
          removeTickerFromCache(effectiveAccountId, ticker.symbol || ticker.ticker_id);
        }
      });

    setTickers(prevTickers => {
      const previousTickerMap = new Map(prevTickers.map(t => [t.ticker_id, t]));
      let changesDetected = prevTickers.length !== filteredTickers.length;
      let changedCount = 0;

      cleanupRecentTickerRemovals();

      // Mark any previously-held ticker that's simply absent from this snapshot as
      // recently removed too. The dust-share loop above only catches positions the
      // API still returns with near-zero shares; a full sell instead deletes the
      // backend row outright, so the sold ticker never appears in tickersData at
      // all. Without this, a late-arriving/out-of-order fetch elsewhere (e.g. a
      // poll that was already in flight before the sell) has no guard stopping it
      // from reintroducing the sold ticker into the table.
      const incomingTickerIds = new Set(filteredTickers.map(t => t.ticker_id));
      for (const [tickerId, prevTicker] of previousTickerMap) {
        if (!incomingTickerIds.has(tickerId)) {
          markTickerRecentlyRemoved(
            prevTicker.symbol || tickerId,
            effectiveAccountId,
            prevTicker.last_updated || prevTicker.updated_at || null
          );
        }
      }

      const sanitizedTickers = [];

      for (const newTicker of filteredTickers) {
        const existingTicker = previousTickerMap.get(newTicker.ticker_id);

        if (!existingTicker) {
          const identifier = newTicker.symbol || newTicker.ticker_id;
          const incomingLastUpdated = newTicker.last_updated || newTicker.updated_at || null;
          if (wasTickerRecentlyRemoved(identifier, effectiveAccountId, incomingLastUpdated, snapshotRequestId)) {
            logger.debug('[AccountDetails] Skipping stale ticker snapshot for recently removed position', {
              accountId: effectiveAccountId,
              symbol: newTicker.symbol || identifier
            });
            changesDetected = true;
            continue;
          }
        }

        sanitizedTickers.push(newTicker);
      }

      if (sanitizedTickers.length !== filteredTickers.length) {
        changesDetected = true;
      }

      const tickersWithThemeOverrides = applyThemeOverridesToTickers(sanitizedTickers, effectiveAccountId);
      const tickersWithAllOverrides = applyBaseOverridesToTickers(tickersWithThemeOverrides, effectiveAccountId);

      const updatedTickers = tickersWithAllOverrides.map(newTicker => {
        const tickerWithNextPlanOverride = applyNextPlanOverrideIfPending(newTicker, effectiveAccountId);
        const existingTicker = previousTickerMap.get(tickerWithNextPlanOverride.ticker_id);

        const hasExistingTicker = Boolean(existingTicker);

        // Check if shields were recently edited for this ticker
        const SHIELD_EDIT_PROTECTION_WINDOW = 20000; // 20 seconds
        const recentShieldEditTime = recentShieldEditsRef.current.get(tickerWithNextPlanOverride.ticker_id);
        const hasRecentShieldEdit = recentShieldEditTime &&
                                    (Date.now() - recentShieldEditTime) < SHIELD_EDIT_PROTECTION_WINDOW;

        // Preserve shields if recently edited (even if set to null) or if specific local conditions met
        const preserveLocalRiskPct = (
          hasExistingTicker &&
          (hasRecentShieldEdit ||  // Protect ALL recent edits from stale server data (including setting to null)
           (sourceType === 'local' &&
            tickerWithNextPlanOverride.shield_risk_percentage === null &&
            existingTicker.shield_risk_percentage !== null &&
            existingTicker.shield_risk_percentage !== undefined))
        );
        const preserveLocalRiskAmount = (
          hasExistingTicker &&
          (hasRecentShieldEdit ||  // Protect ALL recent edits from stale server data (including setting to null)
           (sourceType === 'local' &&
            tickerWithNextPlanOverride.shield_risk_amount === null &&
            existingTicker.shield_risk_amount !== null &&
            existingTicker.shield_risk_amount !== undefined))
        );
        const preserveLocalArmor = (
          hasExistingTicker &&
          (hasRecentShieldEdit ||  // Protect ALL recent edits from stale server data (including setting to null)
           (sourceType === 'local' &&
            tickerWithNextPlanOverride.armor === null &&
            existingTicker.armor !== null &&
            existingTicker.armor !== undefined))
        );

        if (hasRecentShieldEdit && (preserveLocalRiskPct || preserveLocalRiskAmount || preserveLocalArmor)) {
          logger.debug('[AccountDetails] Protecting recently edited shields in full update', {
            tickerId: tickerWithNextPlanOverride.ticker_id,
            editAge: Date.now() - recentShieldEditTime,
            existingShieldPct: existingTicker.shield_risk_percentage,
            serverShieldPct: tickerWithNextPlanOverride.shield_risk_percentage,
            existingArmor: existingTicker.armor,
            serverArmor: tickerWithNextPlanOverride.armor
          });
        }

        // Check if rating was recently edited for this ticker
        const RATING_EDIT_PROTECTION_WINDOW = 20000; // 20 seconds
        const recentRatingEditTime = recentRatingEditsRef.current.get(tickerWithNextPlanOverride.ticker_id);
        const hasRecentRatingEdit = recentRatingEditTime &&
                                    (Date.now() - recentRatingEditTime) < RATING_EDIT_PROTECTION_WINDOW;

        // Preserve rating if:
        // 1. Server data doesn't include it or has null rating, OR
        // 2. Rating was recently edited by user (protect from stale server data)
        const preserveRating = (
          hasExistingTicker &&
          existingTicker.rating !== null &&
          existingTicker.rating !== undefined &&
          (hasRecentRatingEdit ||
           tickerWithNextPlanOverride.rating === null ||
           tickerWithNextPlanOverride.rating === undefined)
        );

        if (hasRecentRatingEdit && preserveRating) {
          logger.debug('[AccountDetails] Protecting recently edited rating in full update', {
            tickerId: tickerWithNextPlanOverride.ticker_id,
            editAge: Date.now() - recentRatingEditTime,
            existingRating: existingTicker.rating,
            serverRating: tickerWithNextPlanOverride.rating
          });
        }

        // Check if category was recently edited for this ticker (recorded in
        // handleTickerUpdate's array branch right when CategorySelect's edit lands).
        // sourceType !== 'local' excludes that very call from the guard -- otherwise
        // the edit's own snapshot would be rejected the instant it set the timestamp.
        const CATEGORY_EDIT_PROTECTION_WINDOW = 20000; // 20 seconds
        const recentCategoryEditTime = recentCategoryEditsRef.current.get(tickerWithNextPlanOverride.ticker_id);
        const hasRecentCategoryEdit = recentCategoryEditTime &&
                                      (Date.now() - recentCategoryEditTime) < CATEGORY_EDIT_PROTECTION_WINDOW;
        const preserveLocalCategory = hasExistingTicker && hasRecentCategoryEdit && sourceType !== 'local';

        if (preserveLocalCategory) {
          logger.debug('[AccountDetails] Protecting recently edited category in full update', {
            tickerId: tickerWithNextPlanOverride.ticker_id,
            editAge: Date.now() - recentCategoryEditTime,
            existingCategory: existingTicker.category,
            serverCategory: tickerWithNextPlanOverride.category
          });
        }

        updateTickerRiskCache(tickerWithNextPlanOverride.ticker_id, {
          shield_risk_percentage: preserveLocalRiskPct
            ? existingTicker.shield_risk_percentage
            : tickerWithNextPlanOverride.shield_risk_percentage,
          shield_risk_amount: preserveLocalRiskAmount
            ? existingTicker.shield_risk_amount
            : tickerWithNextPlanOverride.shield_risk_amount,
          armor: preserveLocalArmor
            ? existingTicker.armor
            : tickerWithNextPlanOverride.armor
        }, { source: sourceType });

        if (!existingTicker) {
          changedCount += 1;
          changesDetected = true;
          return applyRiskCacheToTicker(tickerWithNextPlanOverride);
        }

        const mergedTicker = {
          ...existingTicker,
          ...tickerWithNextPlanOverride,
          shield_risk_percentage: (
            tickerWithNextPlanOverride.shield_risk_percentage === undefined ||
            preserveLocalRiskPct
          ) ? existingTicker.shield_risk_percentage : tickerWithNextPlanOverride.shield_risk_percentage,
          shield_risk_amount: (
            tickerWithNextPlanOverride.shield_risk_amount === undefined ||
            preserveLocalRiskAmount
          ) ? existingTicker.shield_risk_amount : tickerWithNextPlanOverride.shield_risk_amount,
          armor: (
            tickerWithNextPlanOverride.armor === undefined ||
            preserveLocalArmor
          ) ? existingTicker.armor : tickerWithNextPlanOverride.armor,
          rating: preserveRating ? existingTicker.rating : tickerWithNextPlanOverride.rating,
          category: preserveLocalCategory ? existingTicker.category : tickerWithNextPlanOverride.category
        };

        const hasDifference = Object.keys(mergedTicker).some(key => !Object.is(mergedTicker[key], existingTicker[key]));

        if (hasDifference) {
          changedCount += 1;
          changesDetected = true;
          return applyRiskCacheToTicker(mergedTicker);
        }

        return applyRiskCacheToTicker(existingTicker);
      });

      if (!changesDetected) {
        const sameOrder = updatedTickers.length === prevTickers.length &&
          updatedTickers.every((ticker, index) => ticker === prevTickers[index]);

        if (sameOrder) {
          return prevTickers;
        }

        changesDetected = true;
      }

      logger.debug('[AccountDetails] Updated tickers with preserved shield data', {
        changedCount,
        total: updatedTickers.length
      });

      return updatedTickers;
    });

  };

  const syncAccountTickersForActiveAccount = async (sourceAccountId, tickersDataOverride = null) => {
    const targetAccountId = sourceAccountId ?? currentAccountIdRef.current;

    if (targetAccountId === undefined || targetAccountId === null) {
      logger.debug('AccountDetails: Skipping ticker sync - no active account id');
      return false;
    }

    const targetIdStr = String(targetAccountId);
    const currentIdStr = String(currentAccountIdRef.current);

    if (targetIdStr !== currentIdStr) {
      logger.debug('AccountDetails: Skipping ticker sync for inactive account', {
        targetAccountId: targetIdStr,
        currentAccountId: currentIdStr
      });
      return false;
    }

    try {
      const snapshotRequestId = getNextTickerSnapshotRequestId();
      let tickersData = tickersDataOverride;

      if (!Array.isArray(tickersData)) {
        const tickersResponse = await getAccountTickers(targetAccountId);
        tickersData = tickersResponse?.data;
      }

      if (Array.isArray(tickersData)) {
        updateTickersWithFilteredData(tickersData, targetAccountId, {
          source: 'server',
          snapshotRequestId
        });
        return true;
      }

      logger.debug('AccountDetails: Ticker sync response missing data payload', {
        targetAccountId: targetIdStr,
        hadOverride: Array.isArray(tickersDataOverride)
      });
    } catch (error) {
      logger.error('AccountDetails: Failed to sync tickers after transaction', error, {
        accountId: targetAccountId
      });
    }

    return false;
  };

  // Refresh only the account summary fields (like cash, today stats) on a throttle
  const maybeRefreshAccountSummary = async () => {
    try {
      const now = Date.now();
      // Throttle to at most once every 8 seconds
      if (now - lastAccountRefreshRef.current < 8000) {
        return;
      }
      lastAccountRefreshRef.current = now;

      const response = await getAccount(accountId);
      const latestAccount = response.data;
      // Preserve existing object shape if needed; update with fresh values
      if (currentAccountIdRef.current !== String(latestAccount?.account_id)) {
        // Stale: response for previous account; ignore
        return;
      }
      setAccount((prev) => ({ ...(prev || {}), ...latestAccount }));
    } catch (e) {
      // Non-blocking; ignore background errors
      // console.error('Background account refresh failed:', e);
    }
  };

  // Refresh this account's totals from the same aggregate endpoint
  // NavigationPanel uses, so the stats cards (Today's Gain, Total Gain)
  // match the nav panel exactly instead of drifting from the incrementally
  // merged `tickers` array between full page loads. Not throttled the way
  // maybeRefreshAccountSummary is -- this mirrors NavigationPanel's own
  // subscribeToTickerUpdates handler, which refetches unconditionally on
  // every tick that has ticker updates.
  const syncAccountAggregate = async () => {
    const requestedAccountId = currentAccountIdRef.current;
    try {
      const response = await getAllAccountsWithAggregate();
      if (currentAccountIdRef.current !== requestedAccountId) {
        return; // Stale: user navigated away before this resolved
      }
      const accountsList = Array.isArray(response?.data?.accounts) ? response.data.accounts : [];
      const matched = accountsList.find(a => String(a.account_id) === String(requestedAccountId));
      if (matched) {
        setAccountAggregate(matched);
      }
    } catch (error) {
      logger.debug('AccountDetails: Failed to sync account aggregate totals (non-critical)', error);
    }
  };

  const handleTickerUpdate = async (updatedTickers) => {
    try {
      logger.debug('handleTickerUpdate - received updatedTickers', updatedTickers);
      logger.debug('handleTickerUpdate - current tickers state', tickers);

      // Guard clause: if no updatedTickers provided, do nothing
      if (!updatedTickers) {
        logger.warn('handleTickerUpdate called without parameters - no action taken');
        return;
      }

      // Handle both single ticker updates (like rating changes) and array updates
      if (Array.isArray(updatedTickers)) {
        // Update the tickers in the local state using the filter utility
        const activeAccountId = (account && account.account_id) || accountId;
        const categoryEditTimestamp = Date.now();
        updatedTickers.forEach(ticker => {
          updateTickerRiskCache(ticker.ticker_id, {
            shield_risk_percentage: ticker.shield_risk_percentage,
            shield_risk_amount: ticker.shield_risk_amount,
            armor: ticker.armor
          }, { source: 'local' });

          // This array-form update is also how CategorySelect propagates a category
          // change (see TickerTable's handleCategoryUpdate). Record which ticker's
          // category actually changed so updateTickersWithFilteredData can protect it
          // below, the same way rating/shield edits are protected -- otherwise a
          // server refresh that was already in flight when the user saved the new
          // category can land afterward and silently revert it (visible in the
          // TreeMap, which redraws straight off this same tickers state).
          const existingTicker = tickersRef.current.find(t => t.ticker_id === ticker.ticker_id);
          const existingCategoryName = existingTicker?.category?.name ?? null;
          const nextCategoryName = ticker?.category?.name ?? null;
          if (existingTicker && existingCategoryName !== nextCategoryName) {
            recentCategoryEditsRef.current.set(ticker.ticker_id, categoryEditTimestamp);
          }
        });

        // Clean up old category edit entries (older than protection window)
        const CATEGORY_EDIT_RETENTION_MS = 20000;
        for (const [tid, timestamp] of recentCategoryEditsRef.current.entries()) {
          if (categoryEditTimestamp - timestamp > CATEGORY_EDIT_RETENTION_MS) {
            recentCategoryEditsRef.current.delete(tid);
          }
        }

        updateTickersWithFilteredData(updatedTickers, activeAccountId, {
          source: 'local',
          snapshotRequestId: getNextTickerSnapshotRequestId()
        });
      } else {
        // Single ticker update (e.g., rating change)
        const activeAccountId = (account && account.account_id) || accountId;
        if (String(activeAccountId) !== String(currentAccountIdRef.current)) {
          logger.debug('handleTickerUpdate - skipping single ticker update for inactive account', {
            sourceAccountId: activeAccountId,
            currentAccountId: currentAccountIdRef.current
          });
          return;
        }
        tickersAccountIdRef.current = activeAccountId;
        const [tickerWithThemeOverrides] = applyThemeOverridesToTickers([updatedTickers], activeAccountId);
        const [tickerWithAllOverrides] = applyBaseOverridesToTickers([tickerWithThemeOverrides || updatedTickers], activeAccountId);
        const normalizedTicker = tickerWithAllOverrides || tickerWithThemeOverrides || updatedTickers;

        // Track rating edits to protect them from stale server updates
        if (Object.prototype.hasOwnProperty.call(updatedTickers, 'rating')) {
          recentRatingEditsRef.current.set(normalizedTicker.ticker_id, Date.now());
          logger.debug('Recorded recent rating edit for ticker', normalizedTicker.ticker_id);

          // Clean up old rating edit entries (older than protection window)
          const RATING_EDIT_RETENTION_MS = 20000;
          const now = Date.now();
          for (const [tid, timestamp] of recentRatingEditsRef.current.entries()) {
            if (now - timestamp > RATING_EDIT_RETENTION_MS) {
              recentRatingEditsRef.current.delete(tid);
            }
          }
        }

        updateTickerRiskCache(normalizedTicker.ticker_id, {
          shield_risk_percentage: updatedTickers.shield_risk_percentage,
          shield_risk_amount: updatedTickers.shield_risk_amount,
          armor: updatedTickers.armor
        }, { source: 'local' });
        setTickers(prevTickers => {
          const updatedTickersArray = prevTickers.map(ticker =>
            ticker.ticker_id === normalizedTicker.ticker_id
              ? applyRiskCacheToTicker(normalizedTicker)
              : ticker
          );
          return updatedTickersArray;
        });
        if (
          Object.prototype.hasOwnProperty.call(updatedTickers, 'next_action') ||
          Object.prototype.hasOwnProperty.call(updatedTickers, 'next_size_pct')
        ) {
          recordNextPlanOverride(activeAccountId, {
            tickerId: normalizedTicker.ticker_id,
            symbol: normalizedTicker.symbol,
            next_action: normalizedTicker.next_action,
            next_size_pct: normalizedTicker.next_size_pct
          });
        }
      }
    } catch (err) {
      logger.error('Error updating ticker data', err);
    }
  };

  // Handle initiating a transaction from the ticker table
  const handleInitiateTransaction = (transactionData) => {
    setTransactionInitialData(transactionData);
    setShowTransactionModal(true);
  };

  // Handle sort changes from TickerTable
  const handleSortChange = (column, direction) => {
    setSortConfig({ key: column, direction });
  };

  // Handle ticker symbol click to show transaction history
  const handleTickerClick = (tickerSymbol, accountId) => {
    setSelectedTickerSymbol(tickerSymbol);
    setSelectedAccountId(accountId);
    setShowTickerHistoryModal(true);
  };

  // Helper function to safely calculate values when data is loading
  const safeCalculate = (calculationFn) => {
    if (loading || !account) {
      return 0;
    }
    return calculationFn();
  };

  // Add edit account state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editAccountValue, setEditAccountValue] = useState('');
  const [editDescriptionValue, setEditDescriptionValue] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  // Add edit account functions
  const handleEditAccount = () => {
    setEditAccountValue(account?.name || '');
    setEditDescriptionValue(account?.description || '');
    setShowEditModal(true);
  };
  
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    
    if (!editAccountValue.trim()) {
      setEditError('Account name is required');
      return;
    }
    
    setEditError('');
    setEditLoading(true);
    
    try {
      // Prepare update data with both name and description
      const updateData = {
        name: editAccountValue.trim(),
        description: editDescriptionValue.trim()
      };
      
      // Update account - pass accountId as first parameter
      await updateAccount(accountId, updateData);
      
      // Refresh account details
      await fetchAccountDetails();
      
      // Clear any edit errors on success
      setEditError('');
      
      // Close modal
      setShowEditModal(false);
    } catch (err) {
      logger.error('Error updating account', err);

      if (err.response && err.response.data && err.response.data.error) {
        setEditError(err.response.data.error);
      } else {
        setEditError('Failed to update account. Please try again.');
      }
    } finally {
      setEditLoading(false);
    }
  };

  const handleRefreshShields = async (targetTickerId = null) => {
    if (loading || shieldsRefreshing) return;
    setShieldsRefreshing(true);
    try {
      // Clear strategy badge cache AND shields cache when shields are refreshed
      // Shields are used to derive strategy values, so both caches must be invalidated
      const accountIdForShields = account?.account_id || accountId;
      if (accountIdForShields) {
        const strategyCacheKey = `strategyBadgesCache-${accountIdForShields}`;
        const shieldsCacheKey = `tickerShieldsCache-${accountIdForShields}`;
        try {
          localStorage.removeItem(strategyCacheKey);
          localStorage.removeItem(shieldsCacheKey);
          logger.debug('[AccountDetails] Cleared strategy and shields caches for shields refresh', {
            strategyCacheKey,
            shieldsCacheKey
          });
        } catch (error) {
          logger.warn('[AccountDetails] Failed to clear strategy/shields cache', error);
        }
      }

      // Only re-fetch full account data when running a global shields refresh.
      // For single ticker edits we already patch local state, so avoid reloading the table.
      let shouldRefreshAccountDetails = targetTickerId == null;

      // If no target ticker specified, trigger backend recalculation for the whole account first
      let bulkRecalculateSucceeded = false;
      if (!targetTickerId) {
        if (accountIdForShields) {
          try {
            await recalculateAccountShields(accountIdForShields);
            shouldRefreshAccountDetails = true;
            bulkRecalculateSucceeded = true;
          } catch (_) {
            // Non-blocking; UI will still refresh from getTickerShields
          }
        }
      }

      if (targetTickerId) {
        const accountIdForShields = account?.account_id || accountId;
        if (!accountIdForShields) return;
        const response = await getTickerShields(targetTickerId, accountIdForShields);
        const loadedShields = response.data.shields || [];
        const tickerInfo = response.data.ticker_info || {};
        let updatedArmor = 'No armor';
        let shieldRiskPercentage = null;
        let shieldRiskAmount = null;
        if (loadedShields.length > 0) {
          const totalPotentialPL = loadedShields.reduce((total, shield) => total + (shield.amount_from_cost || 0), 0);
          const costBasisPositionValue = (tickerInfo?.cost_basis || 0) * (tickerInfo?.shares || 0);
          const overallEffectPercentage = costBasisPositionValue > 0 ? (totalPotentialPL / costBasisPositionValue) * 100 : 0;
          updatedArmor = 'shields';
          shieldRiskPercentage = overallEffectPercentage;
          shieldRiskAmount = totalPotentialPL;
        }
        // Update only the matching ticker row in state
        tickersAccountIdRef.current = accountIdForShields;
        updateTickerRiskCache(targetTickerId, {
          shield_risk_percentage: shieldRiskPercentage,
          shield_risk_amount: shieldRiskAmount,
          armor: updatedArmor
        }, { source: 'local' });

        // Track that shields were just edited for this ticker
        recentShieldEditsRef.current.set(targetTickerId, Date.now());

        // Clean up old entries (older than 3 minutes)
        const SHIELD_EDIT_RETENTION_MS = 20000;
        const now = Date.now();
        for (const [tid, timestamp] of recentShieldEditsRef.current.entries()) {
          if (now - timestamp > SHIELD_EDIT_RETENTION_MS) {
            recentShieldEditsRef.current.delete(tid);
          }
        }

        setTickers(prev => prev.map(t => (
          t.ticker_id === targetTickerId
            ? applyRiskCacheToTicker({
              ...t,
              armor: updatedArmor,
              shield_risk_percentage: shieldRiskPercentage,
              shield_risk_amount: shieldRiskAmount
            })
            : t
        )));

        // Update only bottom line from backend
        try {
          const accountRes = await getAccount(accountIdForShields);
          const refreshedAccount = accountRes.data;
          if (String(currentAccountIdRef.current) !== String(refreshedAccount?.account_id)) {
            // Stale response for previous account; ignore
          } else {
            setAccount(prev => (
              prev
                ? { ...prev, bottom_line: refreshedAccount?.bottom_line ?? prev.bottom_line }
                : refreshedAccount
            ));
          }
        } catch (_) {
          // no-op: do not block on bottom line refresh
        }

      } else if (!bulkRecalculateSucceeded && Array.isArray(tickers) && tickers.length > 0) {
        // True fallback now: recalculateAccountShields() above already
        // recalculates every ticker's shields server-side in one call, and
        // shouldRefreshAccountDetails already triggers a full
        // fetchAccountDetails() below that picks up its results. This
        // per-ticker getTickerShields() fan-out (one HTTP request per
        // ticker) used to run unconditionally on every "refresh all"
        // click even when the bulk recalc above had just succeeded --
        // duplicating the work for values fetchAccountDetails() was about
        // to overwrite anyway. Only needed when the bulk call failed.
        const accountIdForShields = account?.account_id || accountId;
        const updatedTickers = await Promise.all(tickers.map(async (t) => {
          try {
            const res = await getTickerShields(t.ticker_id, accountIdForShields);
            const shields = res.data.shields || [];
            const info = res.data.ticker_info || {};
            if (shields.length === 0) {
              return { ...t, armor: 'No armor', shield_risk_percentage: null, shield_risk_amount: null };
            }
            const totalPL = shields.reduce((sum, s) => sum + (s.amount_from_cost || 0), 0);
            const costBasisValue = (info?.cost_basis || 0) * (info?.shares || 0);
            const effectPct = costBasisValue > 0 ? (totalPL / costBasisValue) * 100 : 0;
            return { ...t, armor: 'shields', shield_risk_percentage: effectPct, shield_risk_amount: totalPL };
          } catch {
            return t;
          }
        }));
        tickersAccountIdRef.current = accountIdForShields;
        const shieldUpdatesByTickerId = new Map(updatedTickers.map(ticker => [ticker.ticker_id, ticker]));
        updatedTickers.forEach(ticker => {
          updateTickerRiskCache(ticker.ticker_id, {
            shield_risk_percentage: ticker.shield_risk_percentage,
            shield_risk_amount: ticker.shield_risk_amount,
            armor: ticker.armor
          }, { source: 'local' });

          // Track shield update time for batch updates
          recentShieldEditsRef.current.set(ticker.ticker_id, Date.now());
        });

        // Clean up old entries (older than 3 minutes)
        const SHIELD_EDIT_RETENTION_MS = 20000;
        const now = Date.now();
        for (const [tid, timestamp] of recentShieldEditsRef.current.entries()) {
          if (now - timestamp > SHIELD_EDIT_RETENTION_MS) {
            recentShieldEditsRef.current.delete(tid);
          }
        }

        // Merge against the latest state rather than replacing it with the
        // `tickers` snapshot this per-ticker fan-out started from -- that
        // snapshot can be seconds stale by the time every getTickerShields()
        // call above resolves, so a plain replace would silently discard a
        // rating (or any other field) the user edited while it was running.
        // Only the freshly computed shield fields are overlaid here.
        setTickers(prevTickers => prevTickers.map(existingTicker => {
          const shieldUpdate = shieldUpdatesByTickerId.get(existingTicker.ticker_id);
          return shieldUpdate
            ? applyRiskCacheToTicker({
              ...existingTicker,
              armor: shieldUpdate.armor,
              shield_risk_percentage: shieldUpdate.shield_risk_percentage,
              shield_risk_amount: shieldUpdate.shield_risk_amount
            })
            : existingTicker;
        }));

        // Update only bottom line from backend
        try {
          const accountRes = await getAccount(accountIdForShields);
          const refreshedAccount = accountRes.data;
          if (String(currentAccountIdRef.current) !== String(refreshedAccount?.account_id)) {
            // Stale response for previous account; ignore
          } else {
            setAccount(prev => (
              prev
                ? { ...prev, bottom_line: refreshedAccount?.bottom_line ?? prev.bottom_line }
                : refreshedAccount
            ));
          }
        } catch (_) {
          // no-op: do not block on bottom line refresh
        }

        shouldRefreshAccountDetails = true;
      }

      if (shouldRefreshAccountDetails) {
        try {
          await fetchAccountDetails(false);
        } catch (refreshError) {
          logger.warn('handleRefreshShields - failed to refresh account details after shield update', refreshError);
        }
      }
    } catch (err) {
      // silently ignore for now
    } finally {
      setShieldsRefreshing(false);
    }
  };

  // Fetch orders for this account
  const fetchOrders = useCallback(async () => {
    if (!accountId) return;
    setOrdersLoading(true);
    try {
      const response = await getAccountOrders(accountId);
      logger.debug('[DEBUG] fetchOrders response', response.data);
      // Filter out fulfilled orders - they should not appear in the orders table
      const activeOrders = response.data.filter(order => order.status !== 'FULFILLED');
      setOrders(activeOrders);
      logger.debug('[DEBUG] orders state after setOrders', response.data);
    } catch (err) {
      setOrders([]);
      logger.debug('[DEBUG] fetchOrders error', err);
    } finally {
      setOrdersLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Order dialog handlers
  const handleShowOrderDialog = (ticker = null) => {
    if (ticker) {
      setOrderDialogInitial(null);
      setOrderDialogTicker(ticker);
    } else {
      setOrderDialogInitial(null);
      setOrderDialogTicker(null);
    }
    setOrderDialogError(''); // Clear any previous errors when opening dialog
    setShowOrderDialog(true);
  };
  const handleEditOrder = (order) => {
    setOrderDialogInitial(order);
    setOrderDialogError(''); // Clear any previous errors when editing order
    setShowOrderDialog(true);
  };
  const handleSaveOrder = async (orderData) => {
    // If editing, save immediately
    if (orderDialogInitial) {
      setOrderDialogLoading(true);
      try {
        const response = await updateOrder(orderDialogInitial.order_id, orderData);
        setShowOrderDialog(false);
        setOrderDialogInitial(null);

        // Update local state instead of refreshing
        if (response.data) {
          setOrders(prevOrders =>
            prevOrders.map(order =>
              order.order_id === orderDialogInitial.order_id
                ? response.data
                : order
            )
          );
        }

        // Note: We don't refresh the tickers table here because the order hasn't been committed yet.
        // The tickers table will be refreshed when the order is actually committed.

        try {
          await fetchOrders();
        } catch (ordersError) {
          logger.warn('handleSaveOrder - failed to refresh orders after editing order', ordersError, {
            orderId: orderDialogInitial.order_id
          });
        }
      } finally {
        setOrderDialogLoading(false);
      }
      return;
    }
    // If creating, save immediately (no review dialog)
    setOrderDialogLoading(true);
    try {
      const response = await createOrder(accountId, orderData);
      setShowOrderDialog(false);
      setOrderDialogInitial(null);

      // Add new order to local state instead of refreshing
      if (response.data) {
        setOrders(prevOrders => [...prevOrders, response.data]);
      }

      // Note: We don't refresh the tickers table here because the order hasn't been committed yet.
      // The tickers table will be refreshed when the order is actually committed.

      try {
        await fetchOrders();
      } catch (ordersError) {
        logger.warn('handleSaveOrder - failed to refresh orders after creating order', ordersError, {
          accountId,
          symbol: orderData?.symbol
        });
      }
    } catch (error) {
      logger.error('Error creating order', error);
      
      // Extract the user-friendly error message from the backend
      let errorMessage = 'Failed to create order. Please try again.';
      
      if (error.response && error.response.data && error.response.data.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Set the error message to be displayed in the OrderDialog
      setOrderDialogError(errorMessage);
      
      // Don't close the dialog so the user can see the error
    } finally {
      setOrderDialogLoading(false);
    }
  };
  const handleCommitOrder = async (order) => {
    try {
      await commitOrder(order.order_id);

      // Update local state instead of refreshing
          setOrders(prevOrders =>
            prevOrders.map(o =>
              o.order_id === order.order_id
                ? { ...o, status: 'COMMITTED' }
                : o
            )
          );

      const sourceAccountId = order?.account_id ?? order?.accountId ?? order?.sourceAccountId;
      const activeAccountId = sourceAccountId || account?.account_id || accountId;

      if (activeAccountId && String(activeAccountId) !== String(currentAccountIdRef.current)) {
        logger.debug('handleCommitOrder - skipping ticker refresh for inactive account', {
          sourceAccountId: activeAccountId,
          currentAccountId: currentAccountIdRef.current
        });
      } else {
        const targetAccountId = activeAccountId || accountId;
        try {
          const snapshotRequestId = getNextTickerSnapshotRequestId();
          const tickersResponse = await getAccountTickers(targetAccountId);
          if (tickersResponse?.data) {
            updateTickersWithFilteredData(tickersResponse.data, targetAccountId, {
              snapshotRequestId
            });
          }
        } catch (tickerError) {
          logger.error('handleCommitOrder - failed to refresh tickers after commit', tickerError, {
            orderId: order?.order_id,
            accountId: targetAccountId
          });
        }

        try {
          await maybeRefreshAccountSummary();
        } catch (summaryError) {
          logger.warn('handleCommitOrder - failed to refresh account summary after commit', summaryError, {
            orderId: order?.order_id,
            accountId: targetAccountId
          });
        }
      }
    } catch (error) {
      logger.error('Error committing order', error);
    }
  };

  const handleAdjustOrderSize = async (order, fraction) => {
    if (!order) {
      return;
    }

    const fractionValue = Number(fraction);
    if (!Number.isFinite(fractionValue) || fractionValue <= 0) {
      return;
    }

    const normalizedAction = String(order.action || '').toUpperCase();
    let newQuantity = null;

    if (normalizedAction === 'SELL') {
      const heldTicker = tickers.find(t => t.symbol === order.symbol);
      const rawShares = heldTicker
        ? (heldTicker.shares ?? heldTicker.current_shares ?? heldTicker.quantity ?? null)
        : null;
      const sharesOwned = parseNumericValue(rawShares);

      if (!Number.isFinite(sharesOwned) || sharesOwned < 1) {
        return;
      }

      const desiredQuantity = sharesOwned * fractionValue;
      const maxQuantity = Math.floor(sharesOwned);
      // Match OrderDialog rounding so adjust state stays in sync after edits.
      newQuantity = Math.max(1, Math.min(maxQuantity, Math.floor(desiredQuantity)));
    } else {
      const price = parseNumericValue(order.price);
      const standardSize = parseNumericValue(account?.standard_size);

      if (!Number.isFinite(price) || price <= 0 || !Number.isFinite(standardSize) || standardSize <= 0) {
        return;
      }

      newQuantity = Math.round((standardSize * fractionValue) / price);

      if (!Number.isFinite(newQuantity) || newQuantity < 1) {
        newQuantity = 1;
      }
    }

    // Avoid unnecessary API calls if quantity is unchanged
    if (Number(order.quantity) === newQuantity) {
      return;
    }

    setAdjustingOrderId(order.order_id);
    try {
      const response = await updateOrder(order.order_id, { quantity: newQuantity });
      if (response?.data) {
        setOrders(prevOrders =>
          prevOrders.map(o => (o.order_id === order.order_id ? response.data : o))
        );
      } else {
        setOrders(prevOrders =>
          prevOrders.map(o => (o.order_id === order.order_id ? { ...o, quantity: newQuantity } : o))
        );
      }
    } catch (error) {
      logger.error('Error adjusting order size', error, { orderId: order.order_id, fraction: fractionValue });
    } finally {
      setAdjustingOrderId(null);
    }
  };

  const getQuickTradePrice = (ticker) => {
    if (!ticker) {
      return null;
    }

    const candidates = [
      ticker.effective_price,
      ticker.current_price,
      ticker.price,
      ticker.last_price,
      ticker.manual_price,
      ticker.previous_close,
      ticker.pre_close,
      ticker.cost_basis
    ];

    for (const candidate of candidates) {
      const parsed = parseNumericValue(candidate);
      if (parsed && parsed > 0) {
        return parsed;
      }
    }

    return null;
  };

  const getTickerSymbol = (ticker) => {
    if (!ticker) {
      return '';
    }

    if (ticker.symbol) {
      return String(ticker.symbol).toUpperCase();
    }

    if (ticker.ticker) {
      return String(ticker.ticker).toUpperCase();
    }

    return '';
  };

  // Quick trade shortcut handlers
  const handleQuickBuy = async (ticker) => {
    let refreshRequested = false;
    try {
      // Calculate quantity based on standard size
      // Respect auto_standard_size flag: if true, always use automatic calculation
      const standardSize = (account?.auto_standard_size || !account?.standard_size)
        ? calculateDefaultStandardSize()
        : account.standard_size;

      const numericStandardSize = parseNumericValue(standardSize) || 0;

      logger.debug('Quick Buy - Standard Size Calculation', {
        accountId: account?.account_id,
        autoStandardSize: account?.auto_standard_size,
        manualStandardSize: account?.standard_size,
        calculatedStandardSize: numericStandardSize,
        ticker: ticker?.symbol
      });

      const currentPrice = getQuickTradePrice(ticker);

      if (!currentPrice) {
        const symbol = getTickerSymbol(ticker);
        logger.error('No price available for ticker', symbol);
        setQuickTradeStatus({
          variant: 'warning',
          message: `No tradable price is available for ${symbol}. Refresh prices or set a manual price before using quick buy.`
        });
        return;
      }

      const availableCash = parseNumericValue(account?.cash_balance ?? account?.cash ?? 0) || 0;

      let quantity = 0;
      if (numericStandardSize > 0 && currentPrice > 0) {
        quantity = Math.floor((numericStandardSize * 0.25) / currentPrice);
      }

      if (quantity <= 0 && availableCash >= currentPrice) {
        quantity = 1;
      }

      if (quantity > 0 && availableCash > 0 && (quantity * currentPrice) > availableCash) {
        quantity = Math.floor(availableCash / currentPrice);
      }

      if (quantity <= 0) {
        const symbol = getTickerSymbol(ticker);
        logger.error('Insufficient funds for purchase', {
          availableCash,
          currentPrice,
          standardSize: numericStandardSize,
          symbol
        });
        setQuickTradeStatus({
          variant: 'warning',
          message: `Quick buy skipped for ${symbol}. Not enough available cash to purchase at least one share.`
        });
        return;
      }

      const symbol = getTickerSymbol(ticker);
      setQuickTradeState({ type: 'BUY', symbol });

      const normalizedPrice = Number.isFinite(currentPrice)
        ? Number(currentPrice.toFixed(4))
        : currentPrice;

      const orderData = {
        symbol,
        quantity,
        action: 'BUY',
        type: 'LIMIT',
        price: normalizedPrice,
        order_date: new Date().toISOString()
      };

      const response = await createOrder(accountId, orderData);

      if (response?.data) {
        setOrders(prevOrders => [...prevOrders, response.data]);
        refreshRequested = true;
      } else {
        try {
          await fetchOrders();
        } catch (fetchError) {
          logger.warn('Unable to refresh orders after quick buy', fetchError, { symbol });
        }
        refreshRequested = true;
      }

      // Note: We don't refresh the tickers table here because the order hasn't been committed yet.
      // The tickers table will be refreshed when the order is actually committed.
      if (refreshRequested) {
        try {
          await fetchOrders();
        } catch (orderRefreshError) {
          logger.warn('Quick buy - failed to refresh orders after order creation', orderRefreshError, { symbol });
        }
      }

    } catch (error) {
      const symbol = getTickerSymbol(ticker);
      const errorMessage = error?.response?.data?.error || error.message || 'An unknown error occurred.';
      logger.error('Quick buy failed', error, { symbol });
      setQuickTradeStatus({
        variant: 'danger',
        message: `Unable to place quick buy for ${symbol}: ${errorMessage}`
      });
    } finally {
      setQuickTradeState(null);
    }
  };

  const handleQuickSell = async (ticker) => {
    let refreshRequested = false;
    try {
      // Get all shares owned
      const sharesOwnedRaw = parseNumericValue(ticker?.shares);
      const sharesOwned = sharesOwnedRaw && sharesOwnedRaw > 0 ? sharesOwnedRaw : 0;

      const symbol = getTickerSymbol(ticker);

      if (sharesOwned <= 0) {
        logger.error('No shares to sell for ticker', symbol);
        setQuickTradeStatus({
          variant: 'warning',
          message: `Quick sell skipped for ${symbol}. No shares are currently available.`
        });
        return;
      }

      const currentPrice = getQuickTradePrice(ticker);

      if (!currentPrice) {
        logger.error('No price available for ticker', symbol);
        setQuickTradeStatus({
          variant: 'warning',
          message: `No tradable price is available for ${symbol}. Refresh prices or set a manual price before using quick sell.`
        });
        return;
      }

      setQuickTradeState({ type: 'SELL', symbol });

      const normalizedQuantity = Number.isFinite(sharesOwned)
        ? Number(sharesOwned.toFixed(6))
        : sharesOwned;

      const normalizedPrice = Number.isFinite(currentPrice)
        ? Number(currentPrice.toFixed(4))
        : currentPrice;

      const orderData = {
        symbol,
        quantity: normalizedQuantity,
        action: 'SELL',
        type: 'LIMIT',
        price: normalizedPrice,
        order_date: new Date().toISOString()
      };

      const response = await createOrder(accountId, orderData);

      if (response?.data) {
        setOrders(prevOrders => [...prevOrders, response.data]);
        refreshRequested = true;
      } else {
        try {
          await fetchOrders();
        } catch (fetchError) {
          logger.warn('Unable to refresh orders after quick sell', fetchError, { symbol });
        }
        refreshRequested = true;
      }

      // Note: We don't refresh the tickers table here because the order hasn't been committed yet.
      // The tickers table will be refreshed when the order is actually committed.
      if (refreshRequested) {
        try {
          await fetchOrders();
        } catch (orderRefreshError) {
          logger.warn('Quick sell - failed to refresh orders after order creation', orderRefreshError, { symbol });
        }
      }

    } catch (error) {
      const symbol = getTickerSymbol(ticker);
      const errorMessage = error?.response?.data?.error || error.message || 'An unknown error occurred.';
      logger.error('Quick sell failed', error, { symbol });
      setQuickTradeStatus({
        variant: 'danger',
        message: `Unable to place quick sell for ${symbol}: ${errorMessage}`
      });
    } finally {
      setQuickTradeState(null);
    }
  };

  // Calculate available cash after non-deleted orders
  // BUY orders reserve cash (subtract); SELL orders add expected proceeds (add)
  const nonDeletedOrders = orders.filter(o => o.status !== 'DELETED' && o.status !== 'FULFILLED');
  const netCashImpact = nonDeletedOrders.reduce((sum, o) => {
    const total = Number(o.price) * Number(o.quantity);
    return sum + (String(o.action).toUpperCase() === 'SELL' ? total : -total);
  }, 0);
  const availableCashAfterOrders = (account?.cash || 0) + netCashImpact;

  // Handle cancelled order - directly cancel without confirmation
  async function openCancelledOrderModal(order) {
    const activeAccountId = (account && account.account_id) || accountId;
    const sourceAccountId = order?.account_id ?? order?.accountId ?? activeAccountId;
    const orderWithSource = order ? { ...order, sourceAccountId } : order;

    await deleteOrder(orderWithSource.order_id);

    // Update local state instead of refreshing
    setOrders(prevOrders =>
      prevOrders.filter(o => o.order_id !== orderWithSource.order_id)
    );

    // Reset Next plan to HOLD for the cancelled order's ticker locally
    if (orderWithSource && orderWithSource.symbol) {
      if (sourceAccountId && String(sourceAccountId) !== String(currentAccountIdRef.current)) {
        logger.debug('cancelOrder - skipping ticker update for inactive account', {
          sourceAccountId,
          currentAccountId: currentAccountIdRef.current
        });
      } else {
        const activeAccountId = sourceAccountId || ((account && account.account_id) || accountId);
        tickersAccountIdRef.current = activeAccountId;
        setTickers(prev => prev.map(t => (
          t.symbol === orderWithSource.symbol ? { ...t, next_action: 'HOLD', next_size_pct: null } : t
        )));
        recordNextPlanOverride(activeAccountId, {
          tickerId: null,
          symbol: orderWithSource.symbol,
          next_action: 'HOLD',
          next_size_pct: null
        });
      }
    }
  }

  async function confirmCancelledOrder() {
    const order = cancelledOrderObj;
    await deleteOrder(order.order_id);
    setShowCancelledModal(false);
    setCancelledOrderObj(null);

    // Update local state instead of refreshing
    setOrders(prevOrders =>
      prevOrders.filter(o => o.order_id !== order.order_id)
    );

    // Reset Next plan to HOLD for the cancelled order's ticker locally
    if (order && order.symbol) {
      const sourceAccountId = order?.account_id ?? order?.accountId ?? order?.sourceAccountId;
      if (sourceAccountId && String(sourceAccountId) !== String(currentAccountIdRef.current)) {
        logger.debug('confirmCancelledOrder - skipping ticker update for inactive account', {
          sourceAccountId,
          currentAccountId: currentAccountIdRef.current
        });
      } else {
        const activeAccountId = sourceAccountId || ((account && account.account_id) || accountId);
        tickersAccountIdRef.current = activeAccountId;
        setTickers(prev => prev.map(t => (
          t.symbol === order.symbol ? { ...t, next_action: 'HOLD', next_size_pct: null } : t
        )));
        recordNextPlanOverride(activeAccountId, {
          tickerId: null,
          symbol: order.symbol,
          next_action: 'HOLD',
          next_size_pct: null
        });
      }
    }
  }

  // Handle fulfilled order
  function handleFulfilledOrder(order) {
    // Set up transaction data prefilled with order details
    const transactionData = {
      ticker: { symbol: order.symbol },
      shares: order.quantity,
      price_per_share: order.price,
      type: String(order.action).toUpperCase(),
              transaction_date: getCurrentLocalDate() // Today's date in local time
    };
    setTransactionInitialData(transactionData);
    const activeAccountId = (account && account.account_id) || accountId;
    const sourceAccountId = order?.account_id ?? order?.accountId ?? activeAccountId;
    setFulfilledOrderObj(order ? { ...order, sourceAccountId } : order); // Store the order for later fulfillment
    setShowTransactionModal(true);
  }

  // Custom transaction complete handler that also fulfills the order
  const handleTransactionCompleteWithFulfill = async (transactionData) => {
    const normalizedTransactionType = String(transactionData?.transaction_type || transactionData?.type || '').toUpperCase();
    const isBuyOrSellTransaction = normalizedTransactionType === 'BUY' || normalizedTransactionType === 'SELL';
    let tickersSyncedForTransaction = false;
    let sourceAccountIdForSync = null;
    let hadFulfilledOrder = false;

    try {
      const sourceAccountId = fulfilledOrderObj?.account_id ?? fulfilledOrderObj?.accountId ?? fulfilledOrderObj?.sourceAccountId;
      sourceAccountIdForSync = sourceAccountId ?? null;
      hadFulfilledOrder = Boolean(fulfilledOrderObj);
      const fulfilledSymbol = fulfilledOrderObj?.symbol;
      // First, fulfill the order if we have one stored
      if (fulfilledOrderObj) {
        await fulfillOrder(fulfilledOrderObj.order_id);
        // Reset Next plan to HOLD for the fulfilled order's ticker locally
        if (fulfilledOrderObj.symbol) {
          if (!sourceAccountId || String(sourceAccountId) === String(currentAccountIdRef.current)) {
            const currentAccountId = sourceAccountId || currentAccountIdRef.current;
            tickersAccountIdRef.current = currentAccountId;
            const normalizedAction = String(fulfilledOrderObj.action || transactionData?.transaction_type || '').toUpperCase();
            const fulfilledQuantityRaw = fulfilledOrderObj.quantity ?? transactionData?.shares ?? transactionData?.quantity;
            const fulfilledQuantity = Number(fulfilledQuantityRaw);

            setTickers(prev => {
              const nextTickers = [];

              for (const ticker of prev) {
                if (ticker.symbol !== fulfilledOrderObj.symbol) {
                  nextTickers.push(ticker);
                  continue;
                }

                const updatedTicker = { ...ticker, next_action: 'HOLD', next_size_pct: null };

                if (normalizedAction === 'SELL' && Number.isFinite(fulfilledQuantity)) {
                  const rawShares = ticker.shares !== undefined ? ticker.shares : ticker.current_shares;
                  const currentShares = Number(rawShares ?? 0);

                  if (Number.isFinite(currentShares)) {
                    const remainingShares = currentShares - fulfilledQuantity;

                    if (remainingShares <= MIN_POSITION_SHARES) {
                      // Remove the row immediately when the position is effectively closed
                      markTickerRecentlyRemoved(fulfilledOrderObj.symbol, currentAccountId);
                      removeTickerFromCache(currentAccountId, fulfilledOrderObj.symbol);
                      continue;
                    }

                    const sanitizedShares = remainingShares > 0 ? remainingShares : 0;

                    if (Object.prototype.hasOwnProperty.call(updatedTicker, 'shares')) {
                      updatedTicker.shares = sanitizedShares;
                    }

                    if (Object.prototype.hasOwnProperty.call(updatedTicker, 'current_shares')) {
                      updatedTicker.current_shares = sanitizedShares;
                    }
                  }
                }

                nextTickers.push(updatedTicker);
              }

              return nextTickers;
            });
            recordNextPlanOverride(currentAccountId, {
              tickerId: fulfilledOrderObj?.ticker_id ?? null,
              symbol: fulfilledOrderObj.symbol,
              next_action: 'HOLD',
              next_size_pct: null
            });
          } else {
            logger.debug('handleTransactionCompleteWithFulfill - skipping ticker update for inactive account', {
              sourceAccountId,
              currentAccountId: currentAccountIdRef.current
            });
          }
        }
        setFulfilledOrderObj(null); // Clear the stored order
        
        // Update local state instead of refreshing
        setOrders(prevOrders => 
          prevOrders.filter(o => o.order_id !== fulfilledOrderObj.order_id)
        );

        // Refresh only the affected ticker row from backend to get all recalculated fields
        try {
          const affectedSymbol = fulfilledOrderObj.symbol;
          if (affectedSymbol) {
            if (sourceAccountId && String(sourceAccountId) !== String(currentAccountIdRef.current)) {
              logger.debug('handleTransactionCompleteWithFulfill - skipping refreshed ticker merge for inactive account', {
                sourceAccountId,
                currentAccountId: currentAccountIdRef.current
              });
            } else {
              const targetAccountId = sourceAccountId || accountId;
              const rowRefreshSnapshotRequestId = getNextTickerSnapshotRequestId();
              const tickersResponse = await getAccountTickers(targetAccountId);
              if (tickersResponse && Array.isArray(tickersResponse.data)) {
                const refreshedTicker = tickersResponse.data.find(t => t.symbol === affectedSymbol);
                const currentAccountId = sourceAccountId || ((account && account.account_id) || accountId);
                tickersAccountIdRef.current = currentAccountId;
                if (refreshedTicker) {
                  const sharesNum = Number(refreshedTicker.shares || 0);
                  const isTiny = sharesNum < MIN_POSITION_SHARES;
                  if (isTiny) {
                    // Remove the row if the position is effectively closed
                    markTickerRecentlyRemoved(affectedSymbol, targetAccountId);
                    removeTickerFromCache(targetAccountId, affectedSymbol);
                    setTickers(prev => prev.filter(t => {
                      if (t.symbol === affectedSymbol) {
                        tickerRiskCacheRef.current.delete(t.ticker_id);
                        return false;
                      }
                      return true;
                    }));
                  } else {
                    // Add or merge the refreshed ticker row
                    const riskUpdatePayload = {};
                    if (Object.prototype.hasOwnProperty.call(refreshedTicker, 'shield_risk_percentage')) {
                      riskUpdatePayload.shield_risk_percentage = refreshedTicker.shield_risk_percentage;
                    }
                    if (Object.prototype.hasOwnProperty.call(refreshedTicker, 'shield_risk_amount')) {
                      riskUpdatePayload.shield_risk_amount = refreshedTicker.shield_risk_amount;
                    }
                    if (Object.prototype.hasOwnProperty.call(refreshedTicker, 'armor')) {
                      riskUpdatePayload.armor = refreshedTicker.armor;
                    }
                    if (Object.keys(riskUpdatePayload).length > 0) {
                      updateTickerRiskCache(refreshedTicker.ticker_id, riskUpdatePayload, { source: 'server' });
                    }
                    const refreshedWithRisk = applyRiskCacheToTicker({ ...refreshedTicker });
                    setTickers(prev => {
                      const exists = prev.some(t => t.symbol === affectedSymbol);
                      if (exists) {
                        return prev.map(t => (
                          t.symbol === affectedSymbol
                            ? applyRiskCacheToTicker({ ...t, ...refreshedWithRisk })
                            : t
                        ));
                      }
                      // Insert new row -- but not if something (the optimistic
                      // removal a few lines above, or a concurrent full refresh)
                      // has already confirmed this symbol sold more recently than
                      // this fetch was issued. This targeted row-refresh doesn't
                      // go through updateTickersWithFilteredData's staleness
                      // guards, so without this check a stale response here could
                      // silently resurrect a ticker that was already sold.
                      if (wasTickerRecentlyRemoved(affectedSymbol, targetAccountId, null, rowRefreshSnapshotRequestId)) {
                        return prev;
                      }
                      return [...prev, applyRiskCacheToTicker({ ...refreshedWithRisk })];
                    });
                  }
                } else {
                  // Not found in backend response => fully removed; drop the row locally
                  markTickerRecentlyRemoved(affectedSymbol, targetAccountId);
                  removeTickerFromCache(targetAccountId, affectedSymbol);
                  setTickers(prev => prev.filter(t => {
                    if (t.symbol === affectedSymbol) {
                      tickerRiskCacheRef.current.delete(t.ticker_id);
                      return false;
                    }
                    return true;
                  }));
                }
              }
            }
          }
        } catch (rowRefreshError) {
          // Non-blocking: if row refresh fails, fall back to existing local state
          logger.error('Failed to refresh fulfilled ticker row', rowRefreshError);
        }
        const targetAccountId = sourceAccountId || accountId;
        const didSyncAfterFulfillment = await syncAccountTickersForActiveAccount(targetAccountId);
        tickersSyncedForTransaction = tickersSyncedForTransaction || didSyncAfterFulfillment;

        // Refresh account summary (cash, previous_day_balance, etc.) to keep Top Stats accurate
        try {
          const targetAccountId = sourceAccountId || accountId;
          const accountResponse = await getAccount(targetAccountId);
          if (accountResponse && accountResponse.data) {
            if (String(currentAccountIdRef.current) !== String(accountResponse.data?.account_id)) {
              // Stale response; ignore
            } else {
              setAccount(accountResponse.data);
            }
          }
        } catch (acctErr) {
          logger.error('Failed to refresh account summary after fulfillment', acctErr);
        }

        // Persist transaction notes to unified ticker notes
        try {
          const noteText = (transactionData && typeof transactionData.notes === 'string') ? transactionData.notes.trim() : '';
          if (noteText) {
            let tickerIdToNote = transactionData?.ticker_id;
            if (!tickerIdToNote && fulfilledSymbol) {
              const heldTicker = tickers.find(t => t.symbol === fulfilledSymbol);
              if (heldTicker && heldTicker.ticker_id) {
                tickerIdToNote = heldTicker.ticker_id;
              }
            }
            if (tickerIdToNote) {
              await addTickerNote(tickerIdToNote, noteText);
            }
          }
        } catch (noteErr) {
          logger.error('Failed to add unified ticker note from transaction notes', noteErr);
        }
      }
      
      // Avoid heavy table refresh if we just fulfilled an order (we already updated the row locally)
      if (!hadFulfilledOrder) {
        // We need to refresh account details because a transaction was completed (non-fulfillment flow)
        if (currentAccountIdRef.current === String(accountId)) {
          await fetchAccountDetails(false);
          tickersSyncedForTransaction = true;
        }
      }

      // Refresh the transactions table to show the new transaction
      await fetchAccountTransactions();

      if (isBuyOrSellTransaction && !tickersSyncedForTransaction) {
        const syncTargetAccountId = sourceAccountIdForSync || accountId;
        const didSyncAfterTransaction = await syncAccountTickersForActiveAccount(syncTargetAccountId);
        tickersSyncedForTransaction = tickersSyncedForTransaction || didSyncAfterTransaction;
      }

      // Notify other parts of the app (e.g., NavigationPanel) to refresh
      try {
        window.dispatchEvent(new CustomEvent('transactionCompleted', { detail: { accountId } }));
      } catch (e) {
        // no-op: event dispatching should not block flow
      }
    } catch (error) {
      logger.error('Error fulfilling order', error);
      // Still refresh account details even if fulfillment fails, unless we already updated locally
      if (!hadFulfilledOrder) {
        await fetchAccountDetails(false);
        tickersSyncedForTransaction = true;
      }
      // Still refresh transactions even if fulfillment fails
      await fetchAccountTransactions();
      if (isBuyOrSellTransaction && !tickersSyncedForTransaction) {
        const syncTargetAccountId = sourceAccountIdForSync || accountId;
        await syncAccountTickersForActiveAccount(syncTargetAccountId);
      }
      // Best-effort notify navigation even on error after local refresh
      try {
        window.dispatchEvent(new CustomEvent('transactionCompleted', { detail: { accountId } }));
      } catch (e) {
        // swallow
      }
    }

    // Set flag to force full ticker refresh on next polling cycle
    pendingFullRefreshRef.current = true;
  };

  // Cash modal state
  const [cashAction, setCashAction] = useState('deposit'); // 'deposit' | 'withdraw'
  const [cashAmount, setCashAmount] = useState('');
  const [cashDate, setCashDate] = useState(() => new Date().toISOString().slice(0,16)); // yyyy-MM-ddTHH:mm
  const [cashNotes, setCashNotes] = useState('');
  const [cashSubmitting, setCashSubmitting] = useState(false);

  const resetCashForm = () => {
    setCashAction('deposit');
    setCashAmount('');
    setCashDate(new Date().toISOString().slice(0,16));
    setCashNotes('');
    setCashSubmitting(false);
  };

  const handleSubmitCash = async () => {
    if (!accountId) return;
    const amt = Number(cashAmount);
    if (!amt || amt <= 0) return;
    setCashSubmitting(true);
    try {
      await createTransaction({
        account_id: accountId,
        transaction_type: 'CASH',
        type: cashAction, // 'deposit' | 'withdraw'
        amount: amt,
        transaction_date: new Date(cashDate).toISOString(),
        notes: cashNotes || ''
      });
      // Refresh account details and today's transactions
      await fetchAccountDetails(false);
      await fetchAccountTransactions();
      setShowCashModal(false);
      resetCashForm();
    } catch (e) {
      setCashSubmitting(false);
    }
  };

  // Sort orders when orders or sort configuration changes
  useEffect(() => {
    if (orders && orders.length > 0) {
      const sorted = [...orders].sort((a, b) => {
        let aValue, bValue;
        
        switch (orderSortColumn) {
          case 'symbol':
            aValue = a.symbol?.toLowerCase() || '';
            bValue = b.symbol?.toLowerCase() || '';
            break;
          case 'action':
            aValue = a.action?.toLowerCase() || '';
            bValue = b.action?.toLowerCase() || '';
            break;
          case 'quantity':
            aValue = Number(a.quantity) || 0;
            bValue = Number(b.quantity) || 0;
            break;
          case 'price':
            aValue = Number(a.price) || 0;
            bValue = Number(b.price) || 0;
            break;
          case 'total':
            aValue = (Number(a.price) * Number(a.quantity)) || 0;
            bValue = (Number(b.price) * Number(b.quantity)) || 0;
            break;
          case 'size':
            aValue = (Number(a.price) * Number(a.quantity)) || 0;
            bValue = (Number(b.price) * Number(b.quantity)) || 0;
            break;
          case 'status':
            aValue = a.status?.toLowerCase() || '';
            bValue = b.status?.toLowerCase() || '';
            break;
          case 'order_date':
            aValue = parseDateForSort(a.order_date);
            bValue = parseDateForSort(b.order_date);
            break;
          default:
            aValue = parseDateForSort(a.created_at);
            bValue = parseDateForSort(b.created_at);
            break;
        }
        
        if (aValue < bValue) {
          return orderSortDirection === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return orderSortDirection === 'asc' ? 1 : -1;
        }
        return 0;
      });
      
      setSortedOrders(sorted);
    } else {
      setSortedOrders([]);
    }
  }, [orders, orderSortColumn, orderSortDirection]);
  
  // Handle order table header click for sorting
  const handleOrderHeaderClick = (column) => {
    if (orderSortColumn === column) {
      setOrderSortDirection(orderSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderSortColumn(column);
      setOrderSortDirection('asc');
    }
  };
  
  // Render sort indicator for order table headers
  const renderOrderSortIndicator = (column) => {
    if (orderSortColumn !== column) {
      return '';
    }
    return (
      <span style={{ marginLeft: '4px', fontSize: '0.85em' }}>
        {orderSortDirection === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  const statsLoading = loading || !account;

  // Stable references for the callback props passed to TickerTable, so its
  // React.memo comparison isn't defeated by a fresh function identity on
  // every render (FRONTEND-005). See useStableCallback's docstring.
  const stableHandleTickerUpdate = useStableCallback(handleTickerUpdate);
  const stableSafelyUpdateColumnOrder = useStableCallback(safelyUpdateColumnOrder);
  const stableHandleInitiateTransaction = useStableCallback(handleInitiateTransaction);
  const stableHandleSortChange = useStableCallback(handleSortChange);
  const stableHandleRefreshShields = useStableCallback(handleRefreshShields);
  const stableHandleTickerClick = useStableCallback(handleTickerClick);
  const stableHandleShowOrderDialog = useStableCallback(handleShowOrderDialog);
  const stableHandleQuickBuy = useStableCallback(handleQuickBuy);
  const stableHandleQuickSell = useStableCallback(handleQuickSell);

  return (
    <Container className="mt-1">
      <style>{customStyles}</style>
      
      
      {/* Remove Breadcrumb and add title/description section */}
      <Row className="mb-2">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <h3 className="mb-0">
                {statsLoading ? (
                  <Placeholder animation="glow">
                    <Placeholder xs={6} />
                  </Placeholder>
                ) : (
                  <>
                    {account?.name}
                    <span 
                      className="ms-2 fs-6" 
                      style={{ cursor: 'pointer' }}
                      onClick={handleEditAccount}
                    >
                      <i className="bi bi-pencil-square"></i>
                    </span>
                  </>
                )}
              </h3>
              {statsLoading ? (
                <Placeholder animation="glow" className="ms-3">
                  <Placeholder xs={8} />
                </Placeholder>
              ) : (
                <p className="text-muted mb-0 ms-3">
                  {account?.description}
                </p>
              )}
            </div>
            <div className="d-flex align-items-center">
              <Button 
                variant="outline-primary"
                onClick={() => setShowPieChartModal(true)}
                className="me-2"
                size="sm"
              >
                <i className="bi bi-pie-chart"></i> Pie Chart
              </Button>
              <Button
                variant="outline-primary"
                onClick={showStrategyPresetModal}
                className="me-2"
                size="sm"
              >
                <i className="bi bi-list-stars"></i> Strategy
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => setShowPlanningModal(true)}
                className="me-2"
                size="sm"
                title="Trading planning calculator"
              >
                <i className="bi bi-calculator"></i> Planning
              </Button>
              <Button 
                variant="outline-primary"
                onClick={() => setShowStandardSize(true)}
                size="sm"
              >
                {standardSizeLabel}
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Balance and Gain Cards */}
      <Row className="mb-4">
        <Col className="d-flex flex-wrap">
          <div className="col-xl col-lg-4 col-md-6 col-sm-6 pb-3 px-1">
            <Card className="h-100 account-details-card">
              <Card.Body className="p-2 text-center">
                <Card.Title className="fs-6 mb-1">Total Balance</Card.Title>
                <div className="text-muted small" style={{height: statsLoading ? "24px" : "18px"}}></div>
                {statsLoading ? (
                  <Placeholder as={Card.Text} animation="glow" className="mb-0">
                    <Placeholder xs={8} className="fs-5" />
                  </Placeholder>
                ) : (
                  <Card.Text className="fs-5 mb-0 fw-bold">
                    {formatBalanceDisplay(safeCalculate(calculateTotalBalance))}
                  </Card.Text>
                )}
                {statsLoading ? (
                  <div style={{height: "25px"}}></div>
                ) : (
                  <>
                    <small className="text-muted d-block">
                      Cost Total: {formatBalanceDisplay(safeCalculate(calculateTotalCost))}
                    </small>
                    <small className="text-muted d-block">
                      {(() => {
                        const totalBalance = safeCalculate(calculateTotalBalance);
                        const cash = account?.cash || 0;
                        // Exposure = 100% - cash percentage
                        const exposurePct = totalBalance > 0 ? ((totalBalance - cash) / totalBalance) * 100 : 0;
                        return `(${exposurePct.toFixed(1)}%)`;
                      })()}
                    </small>
                  </>
                )}
              </Card.Body>
            </Card>
          </div>
          {/* Bottom Line Card - 2nd position */}
          <div className="col-xl col-lg-4 col-md-6 col-sm-6 pb-3 px-1">
            <Card className="h-100 account-details-card">
              <Card.Body className="p-2 text-center">
                <Card.Title className="fs-6 mb-1">
                  Bottom Line
                  <i 
                    className="bi bi-info-circle ms-1" 
                    style={{ fontSize: '0.8rem', cursor: 'help' }}
                    title="Bottom line = Cash + Sum of all shield values. Represents the worst-case scenario if all stop losses are triggered."
                  ></i>
                </Card.Title>
                <div className="text-muted small" style={{height: statsLoading ? "24px" : "18px"}}>If all stop losses hit</div>
                {statsLoading ? (
                  <Placeholder as={Card.Text} animation="glow" className="mb-0">
                    <Placeholder xs={8} className="fs-5" />
                  </Placeholder>
                ) : (
                  <>
                    <Card.Text className="fs-5 mb-0 fw-bold">
                      {formatBalanceDisplay(safeCalculate(calculateBottomLine))}
                    </Card.Text>
                    <span className="d-block small text-muted">
                      ({safeCalculate(calculateTotalBalance) > 0 ? ((safeCalculate(calculateBottomLine) / safeCalculate(calculateTotalBalance)) * 100).toFixed(2) : '0.00'}%)
                      {safeCalculate(calculateTotalBalance) > 0 && (
                        <span
                          style={{
                            color: (() => {
                              const pct = ((safeCalculate(calculateBottomLine) / safeCalculate(calculateTotalBalance)) * 100);
                              const effect = pct - 100;
                              if (effect < -3) return 'red';
                              if (effect > -3) return 'goldenrod';
                              return 'inherit';
                            })(),
                            fontSize: (() => {
                              const pct = ((safeCalculate(calculateBottomLine) / safeCalculate(calculateTotalBalance)) * 100);
                              const effect = pct - 100;
                              return effect < -3 ? '1.5em' : 'inherit';
                            })(),
                            marginLeft: 6,
                          }}
                        >
                          ({(() => {
                            const pct = ((safeCalculate(calculateBottomLine) / safeCalculate(calculateTotalBalance)) * 100);
                            const effect = pct - 100;
                            return `${effect >= 0 ? '+' : ''}${effect.toFixed(2)}%`;
                          })()})
                        </span>
                      )}
                    </span>
                  </>
                )}
                <div style={{height: statsLoading ? "25px" : "19px"}}></div>
              </Card.Body>
            </Card>
          </div>
          <div className="col-xl col-lg-4 col-md-6 col-sm-6 pb-3 px-1">
            <Card className="h-100 account-details-card">
              <Card.Body className="p-2 text-center">
                <Card.Title className="fs-6 mb-1">Cash Balance</Card.Title>
                <div className="text-muted small" style={{height: statsLoading ? "24px" : "18px"}}></div>
                {statsLoading ? (
                  <Placeholder as={Card.Text} animation="glow" className="mb-0">
                    <Placeholder xs={8} className="fs-5" />
                  </Placeholder>
                ) : (
                  <Card.Text className="fs-5 mb-0 fw-bold">
                    {formatBalanceDisplay(account.cash || 0)}
                  </Card.Text>
                )}
                {statsLoading ? (
                  <div style={{height: "25px"}}></div>
                ) : (
                  <span className="d-block small text-muted">
                    ({safeCalculate(calculateTotalBalance) > 0 ? ((account.cash || 0) / safeCalculate(calculateTotalBalance) * 100).toFixed(1) : 0}%)
                  </span>
                )}
              </Card.Body>
            </Card>
          </div>
          <div className="col-xl col-lg-4 col-md-6 col-sm-6 pb-3 px-1">
            <Card 
              className="h-100 account-details-card"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowEdgeHistoryModal(true)}
            >
              <Card.Body className="p-2 text-center">
                <Card.Title className="fs-6 mb-1">
                  Trading Edge
                  <i 
                    className="bi bi-info-circle ms-1" 
                    style={{ fontSize: '0.8rem', cursor: 'help' }}
                    title="Trading edge (expectancy) from CLOSED trades only (1st of current month to today). Shows raw expectancy and a secondary position-weighted expectancy that accounts for position size via standard size multiples."
                  ></i>
                </Card.Title>
                <div className="text-muted small" style={{height: statsLoading ? "24px" : "18px"}}>{getCalendarMonthLabel()}</div>
                <div className="text-muted small" style={{fontSize: '0.7rem', height: "16px"}}>Closed Trades Only</div>
                {statsLoading ? (
                  <Placeholder as={Card.Text} animation="glow" className="mb-0">
                    <Placeholder xs={6} className="fs-5" />
                  </Placeholder>
                ) : (
                  <OverlayTrigger
                    placement="top"
                    delay={{ show: 200, hide: 100 }}
                    overlay={
                      <Tooltip id="account-expectancy-tooltip">E = Σ(w·r) / Σw, where r = trade % return and w = capital ÷ standard size</Tooltip>
                    }
                    trigger={["hover", "focus", "click"]}
                  >
                    <div>
                      <Card.Text className={`fs-5 mb-0 fw-bold text-${(accountExpectancyWeighted || 0) >= 0 ? 'success' : 'danger'}`}>
                        {typeof accountExpectancyWeighted === 'number' ? `${accountExpectancyWeighted.toFixed(2)}%` : 'Not available'}
                      </Card.Text>
                    </div>
                  </OverlayTrigger>
                )}
                {!statsLoading && (
                  <small className={`d-block small text-${(accountExpectancy || 0) >= 0 ? 'success' : 'danger'}`}>
                    {(accountExpectancy || 0) >= 0 ? 'Profitable Strategy' : 'Losing Strategy'}
                  </small>
                )}
              </Card.Body>
            </Card>
          </div>
            <div className="col-xl col-lg-4 col-md-6 col-sm-6 pb-3 px-1">
              <Card 
                className="h-100 account-details-card"
              >
                <Card.Body className="p-2 text-center">
                  <Card.Title className="fs-6 mb-1">Today's Gain</Card.Title>
                  {(() => {
                    const hasRequiredData = account && account.previous_day_balance !== undefined && !statsLoading;
                    return hasRequiredData ? (
                      <small className="text-muted d-block">prev bal basis</small>
                    ) : (
                      <div className="text-muted small" style={{height: statsLoading ? "24px" : "18px"}}></div>
                    );
                  })()}
                  {(() => {
                    const hasRequiredData = account && account.previous_day_balance !== undefined && !statsLoading;
                    if (!hasRequiredData) {
                      return (
                        <Placeholder as={Card.Text} animation="glow" className="mb-0">
                          <Placeholder xs={7} className="fs-5" />
                        </Placeholder>
                      );
                    }
                    const todayGain = getTodayTotalGain();
                    const normalizedAmount = normalizeNearZero(todayGain.amount);
                    return (
                      <Card.Text className={`fs-5 mb-0 fw-bold text-${normalizedAmount >= 0 ? 'success' : 'danger'}`}>
                        {formatBalanceDisplay(normalizedAmount)}
                      </Card.Text>
                    );
                  })()}
                  {(() => {
                    const hasRequiredData = account && account.previous_day_balance !== undefined && !statsLoading;
                    if (!hasRequiredData) {
                      return <div style={{height: statsLoading ? "25px" : "19px"}}></div>;
                    }
                    const todayGain = getTodayTotalGain();
                    const normalizedAmount = normalizeNearZero(todayGain.amount);
                    const normalizedPct = normalizeNearZero(todayGain.percentage, 0.005);
                    return (
                      <OverlayTrigger
                        placement="top"
                        delay={{ show: 200, hide: 100 }}
                        overlay={
                          <Tooltip id="today-gain-tooltip">
                            {`Today's Gain: ${formatBalanceDisplay(normalizedAmount)}`}
                          </Tooltip>
                        }
                        trigger={["hover", "focus", "click"]}
                      >
                        <span className={`d-block small text-${normalizedPct >= 0 ? 'success' : 'danger'}`} style={{ cursor: 'pointer' }}>
                          ({normalizedPct < 0 ? '-' : ''}{Math.abs(normalizedPct).toFixed(2)}%)
                        </span>
                      </OverlayTrigger>
                    );
                  })()}
                </Card.Body>
              </Card>
            </div>
            <div className="col-xl col-lg-6 col-md-6 col-sm-6 pb-3 px-1">
              <Card 
                className="h-100 account-details-card"
              >
                <Card.Body className="p-2 text-center">
              <Card.Title className="fs-6 mb-1">Total Gain</Card.Title>
                  {statsLoading ? (
                    <div className="text-muted small" style={{height: "24px"}}></div>
                  ) : (
                <small className="text-muted d-block">tickers total</small>
                  )}
                  {statsLoading ? (
                    <Placeholder as={Card.Text} animation="glow" className="mb-0">
                      <Placeholder xs={7} className="fs-5" />
                    </Placeholder>
                  ) : (
                (() => {
                  const { amount } = holdingsTotalGain;
                  return (
                    <Card.Text className={`fs-5 mb-0 fw-bold text-${amount >= 0 ? 'success' : 'danger'}`}>
                      {formatBalanceDisplay(amount)}
                    </Card.Text>
                  );
                })()
                  )}
                  {statsLoading ? (
                    <div style={{height: "25px"}}></div>
                  ) : (
                (() => {
                  const { amount, percentage } = holdingsTotalGain;
                  return (
                    <OverlayTrigger
                      placement="top"
                      delay={{ show: 200, hide: 100 }}
                      overlay={
                        <Tooltip id="total-gain-percent-tooltip">
                          Total Gain/Total Cost
                        </Tooltip>
                      }
                      trigger={["hover", "focus", "click"]}
                    >
                      <span className={`d-block small text-${amount >= 0 ? 'success' : 'danger'}`} style={{ cursor: 'pointer' }}>
                        ({percentage.toFixed(2)}%)
                      </span>
                    </OverlayTrigger>
                  );
                })()
                  )}
                </Card.Body>
              </Card>
            </div>
            <div className="col-xl col-lg-6 col-md-6 col-sm-6 pb-3 px-1">
              <Card 
                className="h-100 account-details-card"
              >
                <Card.Body className="p-2 text-center">
                  <Card.Title className="fs-6 mb-1">Year-to-Date</Card.Title>
                  {statsLoading ? (
                    <div className="text-muted small" style={{height: "24px"}}></div>
                  ) : account.ytd_start_date ? (
                    <small className="text-muted d-block">since {new Date(account.ytd_start_date).toLocaleDateString()}</small>
                  ) : (
                    <div className="text-muted small" style={{height: "18px"}}></div>
                  )}
                  {statsLoading ? (
                    <Placeholder as={Card.Text} animation="glow" className="mb-0">
                      <Placeholder xs={7} className="fs-5" />
                    </Placeholder>
                  ) : (
                    <Card.Text className={`fs-5 mb-0 fw-bold text-${(account.ytd_gain_amount || 0) >= 0 ? 'success' : 'danger'}`}>
                      {formatBalanceDisplay(account.ytd_gain_amount || 0)}
                    </Card.Text>
                  )}
                  {statsLoading ? (
                    <div style={{height: "25px"}}></div>
                  ) : (
                    <span className={`d-block small text-${(account.ytd_gain_amount || 0) >= 0 ? 'success' : 'danger'}`}>
                      ({(account.ytd_gain_percentage || 0).toFixed(2)}%)
                    </span>
                  )}
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>

      {error && (
        <Alert variant="danger">{error}</Alert>
      )}

      {/* Action Buttons Row */}
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <Button
                variant="outline-primary"
                onClick={() => setShowBalanceHistoryModal(true)}
                className="me-2"
                size="sm"
              >
                <i className="bi bi-graph-up"></i> Balance
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => setShowPerformanceModal(true)}
                className="me-2"
                size="sm"
              >
                <i className="bi bi-speedometer2"></i> Performance
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => setShowTreeMapModal(prev => !prev)}
                className="me-2"
                size="sm"
              >
                <i className="bi bi-diagram-3"></i> Tree Map
              </Button>
              <Button 
                variant="outline-primary"
                onClick={() => {
                  // Navigate to trades page with current account pre-selected
                  navigate('/trades', { state: { preSelectedAccount: accountId } });
                }}
                className="me-2"
                size="sm"
              >
                <i className="bi bi-graph-up"></i> Trades
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => navigate('/trades', { state: { preSelectedAccount: accountId, filterStatus: 'CLOSED', filterNeedsPost: true } })}
                className="me-2"
                size="sm"
                title="Closed trades missing post analysis"
              >
                Post({postAnalysisTodoCount})
              </Button>
            </div>
            <div>
              {/* Refresh Shields - recalculates and updates DB and UI */}
              <Button 
                variant="outline-primary"
                onClick={() => handleRefreshShields()}
                className="me-2"
                size="sm"
                disabled={loading}
                title="Recalculate and refresh all shields for this account"
              >
                <i className={`bi bi-arrow-repeat me-1 ${shieldsRefreshing ? 'spin' : ''}`}></i>
                Shield
              </Button>
              <Button 
                variant="outline-success"
                onClick={handleRefreshPrices}
                className="me-2"
                size="sm"
                disabled={refreshLoading}
                title="Refresh ticker prices from external API"
              >
                <FaSyncAlt className={refreshLoading ? 'spin' : ''} /> {refreshLoading ? 'Updating...' : 'Prices'}
              </Button>
              <Button 
                variant="outline-primary"
                onClick={handleShowOrderDialog}
                className="me-2"
                size="sm"
              >
                + Order
              </Button>
              <Button 
                variant="outline-primary"
                onClick={() => setShowCashModal(true)}
                className="me-2"
                size="sm"
              >
                Cash
              </Button>
              {/* Reset Columns Button */}
              <Button 
                variant="outline-primary"
                size="sm"
                className="text-nowrap me-2"
                disabled={loading}
                onClick={handleResetToDefaults}
              >
                Reset Cols
              </Button>
              {visibleColumns && (
                <ColumnVisibilityDropdown
                  visibleColumns={visibleColumns}
                  columns={ACCOUNT_DETAIL_TOGGLE_COLUMNS}
                  getLabel={formatAccountColumnLabel}
                  onToggleColumn={(column, newValue) => {
                    setVisibleColumns(prev => ({
                      ...prev,
                      [column]: newValue
                    }));
                  }}
                  idPrefix="header-column-toggle"
                  disabled={loading}
                >
                  <Dropdown.Divider />
                  <div className="text-muted small">
                    <div>• Click column headers to sort</div>
                    <div>• Drag column headers to reorder</div>
                  </div>
                  <Dropdown.Divider />
                  <div className="d-grid gap-1">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="w-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        logger.debug('Set as Default button clicked');
                        handleSaveAsDefaults();
                      }}
                    >
                      Set as Default
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="w-100"
                      onClick={handleResetToDefaults}
                    >
                      Reset Columns
                    </Button>
                  </div>
                </ColumnVisibilityDropdown>
              )}
            </div>
          </div>
        </Col>
      </Row>
      
      {!loading && standardSizeSuggestion && (
        <Alert variant="info" className="mb-3 d-flex justify-content-between align-items-center">
          <div>
            <strong>Position sizing suggestion:</strong> Your account doesn't have a standard size set.
            We recommend using {formatBalanceDisplay(calculateDefaultStandardSize())}
            (1/10 of your total account value, rounded to the nearest $1,000) as your standard size.
          </div>
          <Button variant="primary" size="sm" onClick={() => setShowStandardSize(true)}>
            Set Standard Size
          </Button>
        </Alert>
      )}

      {/* Use TickerTable with tableLoading prop instead of a separate loading spinner */}
      <div className="account-tickers-table-container">
        <style>{`
          /* Set CSS custom properties for weather theme colors */
          .account-tickers-table-container {
            --weather-bg-color: ${weatherColors.background};
            --weather-border-color: ${weatherColors.border};
            --weather-text-color: ${weatherColors.text};
          }
          
          /* Weather theme theming for tickers table headers - using CSS variables and maximum specificity */
          .account-tickers-table-container * {
            --bg-secondary: var(--weather-bg-color) !important;
          }
          
          /* Ultra high specificity to override TickerTable.css .ticker-table th rules */
          html body div.account-tickers-table-container .ticker-table.tickers-table.account-tickers-table thead tr th,
          html body div.account-tickers-table-container .ticker-table.tickers-table thead tr th,
          html body div.account-tickers-table-container .ticker-table.account-tickers-table thead tr th,
          html body div.account-tickers-table-container table.ticker-table thead tr th,
          html body div.account-tickers-table-container .ticker-table thead tr th,
          html body .account-tickers-table-container .ticker-table th,
          html body .account-tickers-table-container table th,
          html body .account-tickers-table-container thead th {
            background-color: var(--weather-bg-color) !important;
            background: var(--weather-bg-color) !important;
            border-color: var(--weather-border-color) !important;
            color: var(--weather-text-color) !important;
            border-bottom-color: var(--weather-border-color) !important;
            border-bottom: 2px solid var(--weather-border-color) !important;
            border-top-color: var(--weather-border-color) !important;
            border-left-color: var(--weather-border-color) !important;
            border-right-color: var(--weather-border-color) !important;
          }
          
          /* Direct override of exact TickerTable.css selector */
          .account-tickers-table-container .ticker-table th {
            background-color: var(--weather-bg-color) !important;
            background: var(--weather-bg-color) !important;
            border-color: var(--weather-border-color) !important;
            color: var(--weather-text-color) !important;
            border-bottom: 2px solid var(--weather-border-color) !important;
          }
        `}</style>
        {showTreeMapModal && (
          <div className="mb-3">
            <TreeMapContainer
              key={treemapPageKey}
              inline
              show={showTreeMapModal}
              onHide={() => setShowTreeMapModal(false)}
              tickers={tickers}
              cash={account?.cash || 0}
              previousBalance={account?.previous_day_balance}
              preBalLoading={statsLoading}
              lastRefreshTime={lastRefreshTime}
              refreshInterval={refreshInterval}
              autoRefreshEnabled={autoRefreshEnabled}
              onRefresh={handleRefreshPrices}
              marketInfo={marketInfo}
              pageKey={treemapPageKey}
            />
          </div>
        )}
        <TickerTable
          key={accountId}
          tickers={tickers}
          showAccountDetails={false}
          accountStandardSize={account?.standard_size}
          accountId={accountId}
          currentAccountId={accountId}
          onTickerUpdate={stableHandleTickerUpdate}
          hideColumnsDropdown={true}
          visibleColumnsFromParent={visibleColumns}
          columnOrder={columnOrder}
          onColumnOrderChange={stableSafelyUpdateColumnOrder}
          accountCash={account?.cash || 0}
          onInitiateTransaction={stableHandleInitiateTransaction}
          accountTotalValue={safeCalculate(calculateTotalBalance)}
          sortConfig={sortConfig}
          onSortChange={stableHandleSortChange}
          tableLoading={tableLoading}
          onShieldUpdate={stableHandleRefreshShields}
          onTickerClick={stableHandleTickerClick}
          onShowOrderDialog={stableHandleShowOrderDialog}
          onQuickBuy={stableHandleQuickBuy}
          onQuickSell={stableHandleQuickSell}
          className="account-tickers-table"
          notesFloating={true}
          compactActions={true}
          quickTradeState={quickTradeState}
          columnWidthOverrides={TICKER_TABLE_COLUMN_WIDTH_OVERRIDES}
        />
      </div>

      {quickTradeStatus && (
        <Alert
          variant={quickTradeStatus.variant}
          className="mb-3"
          dismissible
          onClose={() => setQuickTradeStatus(null)}
        >
          {quickTradeStatus.message}
        </Alert>
      )}

      {/* Orders Table */}
      {(ordersLoading || orders.length > 0) && (
        <div className="d-flex justify-content-between align-items-center mb-1 ms-1">
          <div className="d-flex align-items-center">
            <h5 className="mb-0">Orders</h5>
            {orders.length > 0 && (
              <span className="ms-2" style={{ fontSize: '1.1em', fontWeight: 700 }}>
                {(() => {
                  const totalAccountValue = safeCalculate(calculateTotalBalance);
                  const buyTotal = orders
                    .filter(o => String(o.action).toUpperCase() === 'BUY')
                    .reduce((sum, o) => sum + (Number(o.price) * Number(o.quantity)), 0);
                  const sellTotal = orders
                    .filter(o => String(o.action).toUpperCase() === 'SELL')
                    .reduce((sum, o) => sum + (Number(o.price) * Number(o.quantity)), 0);
                  const buyPct = totalAccountValue > 0 ? Math.round((buyTotal / totalAccountValue) * 100) : 0;
                  const sellPct = totalAccountValue > 0 ? Math.round((sellTotal / totalAccountValue) * 100) : 0;
                  return (
                    <>
                      <span style={{ color: '#198754' }}>
                        BUY: ${buyTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        <span style={{ color: '#6c757d', fontSize: '0.8em', fontWeight: 400, marginLeft: 4 }}>({buyPct}%)</span>
                      </span>
                      <span className="ms-3" style={{ color: '#dc3545' }}>
                        SELL: ${sellTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        <span style={{ color: '#6c757d', fontSize: '0.8em', fontWeight: 400, marginLeft: 4 }}>({sellPct}%)</span>
                      </span>
                    </>
                  );
                })()}
              </span>
            )}
          </div>
          <div className="d-flex align-items-center">
            {orders.length > 0 && (
              <span className="align-middle me-3" style={{ fontSize: '1.5em', fontWeight: 700, color: '#007bff' }}>
                {(() => {
                  const totalAccountValue = safeCalculate(calculateTotalBalance);
                  const percent = totalAccountValue > 0 ? Math.round((availableCashAfterOrders / totalAccountValue) * 100) : 0;
                  return <>
                    ${availableCashAfterOrders.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    <span style={{ color: '#6c757d', fontSize: '0.7em', fontWeight: 400, marginLeft: 4 }}>({percent}%)</span>
                  </>;
                })()}
              </span>
            )}
            <Button 
              variant="outline-primary"
              onClick={handleShowOrderDialog}
              size="sm"
            >
              + Order
            </Button>
          </div>
        </div>
      )}
      {ordersLoading ? (
        <div className="text-center my-4"><Spinner animation="border" size="sm" /></div>
      ) : orders.length > 0 ? (
        <div className="table-responsive">
          {/* Orders Table Custom Styles */}
          <style>{`
            .orders-table-quantity {
              color: #0d6efd;
              font-weight: bold;
            }
            .orders-table-price {
              color: #198754;
              font-weight: bold;
            }
            [data-theme="dark"] .orders-table-quantity {
              color: #0d6efd !important;
            }
            [data-theme="dark"] .orders-table-price {
              color: #198754 !important;
            }
            /* Orders table hover effects - similar to tickers table */
            .orders-table tbody tr {
              background-color: var(--card-bg) !important;
              border-bottom: 1px solid var(--border-color) !important;
              transition: all 0.3s ease-in-out !important;
            }

             /* Subtle action tint */
             .orders-table tbody tr.order-row-buy {
               background-color: #edf7ed !important; /* subtle green */
             }
             .orders-table tbody tr.order-row-sell {
               background-color: #fff1f1 !important; /* subtle red */
             }
             .orders-table tbody tr.order-row-commit {
               background-color: ${weatherColors.background} !important;
             }
             /* Apply tint on cells to ensure visibility over default cell backgrounds */
             .orders-table tbody tr.order-row-buy td { background-color: #edf7ed !important; }
             .orders-table tbody tr.order-row-sell td { background-color: #fff1f1 !important; }
             .orders-table tbody tr.order-row-commit td { background-color: ${weatherColors.background} !important; }
             [data-theme="dark"] .orders-table tbody tr.order-row-buy {
               background-color: rgba(25, 135, 84, 0.15) !important; /* bootstrap success tint */
             }
             [data-theme="dark"] .orders-table tbody tr.order-row-sell {
               background-color: rgba(220, 53, 69, 0.15) !important; /* bootstrap danger tint */
             }
             [data-theme="dark"] .orders-table tbody tr.order-row-commit {
               background-color: ${weatherColors.background} !important;
             }
             [data-theme="dark"] .orders-table tbody tr.order-row-buy td { background-color: rgba(25, 135, 84, 0.15) !important; }
             [data-theme="dark"] .orders-table tbody tr.order-row-sell td { background-color: rgba(220, 53, 69, 0.15) !important; }
             [data-theme="dark"] .orders-table tbody tr.order-row-commit td { background-color: ${weatherColors.background} !important; }
            
            .orders-table tbody tr:hover {
              background-color: var(--hover-bg) !important;
              transform: translateY(-2px) !important;
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
              border-left: 3px solid #0d6efd !important;
              border-right: 3px solid #0d6efd !important;
            }
            
            [data-theme="light"] .orders-table tbody tr:hover {
              background-color: #b8daff !important;
              box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3) !important;
              border-left-color: #0d6efd !important;
              border-right-color: #0d6efd !important;
              border-top: 2px solid #0d6efd !important;
              border-bottom: 2px solid #0d6efd !important;
            }
            
            [data-theme="dark"] .orders-table tbody tr:hover {
              background-color: #1e3a8a !important;
              box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3) !important;
              border-left-color: #3b82f6 !important;
              border-right-color: #3b82f6 !important;
            }
            
            .orders-table tbody td {
              padding: 4px 6px !important;
              border-bottom: 1px solid var(--border-color) !important;
              vertical-align: middle !important;
              transition: all 0.3s ease-in-out !important;
            }
            
            .orders-table tbody tr:hover td {
              color: var(--text-primary) !important;
            }
            
            [data-theme="light"] .orders-table tbody tr {
              background-color: #ffffff !important;
            }
            /* Ensure action tint overrides base theme row color */
            [data-theme="light"] .orders-table tbody tr.order-row-buy { background-color: #edf7ed !important; }
            [data-theme="light"] .orders-table tbody tr.order-row-sell { background-color: #fff1f1 !important; }
            [data-theme="light"] .orders-table tbody tr.order-row-buy td { background-color: #edf7ed !important; }
            [data-theme="light"] .orders-table tbody tr.order-row-sell td { background-color: #fff1f1 !important; }
            [data-theme="light"] .orders-table tbody tr.order-row-commit { background-color: ${weatherColors.background} !important; }
            [data-theme="light"] .orders-table tbody tr.order-row-commit td { background-color: ${weatherColors.background} !important; }
            
            [data-theme="light"] .orders-table tbody td {
              border-bottom: 1px solid #e5e7eb !important;
            }
            
            [data-theme="light"] .orders-table tbody tr:hover td {
              color: #0d6efd !important;
            }
            
            [data-theme="dark"] .orders-table tbody tr {
              background-color: #1f2937 !important;
            }
            /* Ensure action tint overrides base theme row color */
            [data-theme="dark"] .orders-table tbody tr.order-row-buy { background-color: rgba(25, 135, 84, 0.15) !important; }
            [data-theme="dark"] .orders-table tbody tr.order-row-sell { background-color: rgba(220, 53, 69, 0.15) !important; }
            [data-theme="dark"] .orders-table tbody tr.order-row-buy td { background-color: rgba(25, 135, 84, 0.15) !important; }
            [data-theme="dark"] .orders-table tbody tr.order-row-sell td { background-color: rgba(220, 53, 69, 0.15) !important; }
            [data-theme="dark"] .orders-table tbody tr.order-row-commit { background-color: ${weatherColors.background} !important; }
            [data-theme="dark"] .orders-table tbody tr.order-row-commit td { background-color: ${weatherColors.background} !important; }

            /* Preserve tint on hover for action rows */
            .orders-table tbody tr.order-row-buy:hover { background-color: #e6f3e6 !important; }
            .orders-table tbody tr.order-row-sell:hover { background-color: #ffe8e8 !important; }
            .orders-table tbody tr.order-row-commit:hover { background-color: ${weatherColors.background} !important; }
            .orders-table tbody tr.order-row-buy:hover td { background-color: #e6f3e6 !important; }
            .orders-table tbody tr.order-row-sell:hover td { background-color: #ffe8e8 !important; }
            .orders-table tbody tr.order-row-commit:hover td { background-color: ${weatherColors.background} !important; }
            [data-theme="dark"] .orders-table tbody tr.order-row-buy:hover { background-color: rgba(25, 135, 84, 0.22) !important; }
            [data-theme="dark"] .orders-table tbody tr.order-row-sell:hover { background-color: rgba(220, 53, 69, 0.22) !important; }
            [data-theme="dark"] .orders-table tbody tr.order-row-commit:hover { background-color: ${weatherColors.background} !important; }
            [data-theme="dark"] .orders-table tbody tr.order-row-buy:hover td { background-color: rgba(25, 135, 84, 0.22) !important; }
            [data-theme="dark"] .orders-table tbody tr.order-row-sell:hover td { background-color: rgba(220, 53, 69, 0.22) !important; }
            [data-theme="dark"] .orders-table tbody tr.order-row-commit:hover td { background-color: ${weatherColors.background} !important; }
            
            [data-theme="dark"] .orders-table tbody td {
              border-bottom: 1px solid #374151 !important;
            }
            
            [data-theme="dark"] .orders-table tbody tr:hover td {
              color: #93c5fd !important;
            }
            
            /* Prevent scrollbar from appearing on hover */
            .table-responsive:hover {
              overflow: hidden !important;
            }

            .orders-table .orders-action-col {
              min-width: 4.4rem;
              width: 4.4rem;
            }

            .orders-table .orders-date-col {
              min-width: 6rem;
              width: 6rem;
            }

            .orders-table .orders-actions-col {
              max-width: 11rem;
              width: 11rem;
            }

            .orders-table .adjust-button {
              min-width: 1.85rem;
              font-weight: 700 !important;
              text-transform: uppercase;
              letter-spacing: 0.01em;
              border-width: 2px !important;
              transition: transform 0.2s ease, box-shadow 0.2s ease !important;
              font-size: 0.75rem;
            }

            .orders-table .adjust-button.adjust-button-buy {
              box-shadow: 0 2px 6px rgba(25, 135, 84, 0.25);
            }

            .orders-table .adjust-button.adjust-button-sell {
              box-shadow: 0 2px 6px rgba(13, 110, 253, 0.25);
            }

            .orders-table .adjust-button.adjust-button-muted {
              opacity: 0.55 !important;
              box-shadow: none !important;
            }

            .orders-table .adjust-button.adjust-button-muted:not(:disabled):hover {
              transform: none !important;
              box-shadow: none !important;
            }

            .orders-table .adjust-button:not(:disabled):hover,
            .orders-table .adjust-button.adjust-button-buy:not(:disabled):hover {
              transform: translateY(-1px);
              box-shadow: 0 4px 10px rgba(25, 135, 84, 0.35);
            }

            .orders-table .adjust-button.adjust-button-sell:not(:disabled):hover {
              box-shadow: 0 4px 10px rgba(13, 110, 253, 0.35);
            }

            .orders-table .btn-group-sm > .btn.adjust-button {
              padding: 0.2rem 0.45rem !important;
            }

            .orders-table .orders-adjust-col {
              width: 6rem;
              min-width: 6rem;
            }

            .orders-table .adjust-button:disabled {
              opacity: 0.6 !important;
              box-shadow: none !important;
            }
           
            /* Exclude total row from hover effects */
            .orders-table tbody tr:last-child {
              background: #f8f9fa !important;
              color: #212529 !important;
              font-weight: bold !important;
              font-size: 1rem !important;
              transition: none !important;
            }

            .orders-table tbody tr:last-child:hover {
              background: #f8f9fa !important;
              color: #212529 !important;
              font-size: 1rem !important;
              transform: none !important;
              box-shadow: none !important;
              font-weight: bold !important;
              border-left: none !important;
              border-right: none !important;
            }

            .orders-table tbody tr:last-child td {
              transition: none !important;
              color: #212529 !important;
              font-size: 1rem !important;
            }

            .orders-table tbody tr:last-child:hover td {
              color: #212529 !important;
              font-weight: bold !important;
              font-size: 1rem !important;
              transform: none !important;
            }

            /* Weather theme theming for orders table headers */
            .orders-table thead th {
              background-color: ${weatherColors.background} !important;
              border-color: ${weatherColors.border} !important;
              color: ${weatherColors.text} !important;
              border-bottom: 2px solid ${weatherColors.border} !important;
            }
          `}</style>
          <table className="table table-sm table-bordered mb-0 orders-table">
            <thead>
              <tr>
                <th 
                  style={{ cursor: 'pointer' }} 
                  onClick={() => handleOrderHeaderClick('symbol')}
                  className="user-select-none"
                >
                  Symbol {renderOrderSortIndicator('symbol')}
                </th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleOrderHeaderClick('action')}
                  className="user-select-none text-end align-middle orders-action-col"
                >
                  Actn {renderOrderSortIndicator('action')}
                </th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleOrderHeaderClick('quantity')}
                  className="user-select-none text-end"
                >
                  Shrs {renderOrderSortIndicator('quantity')}
                </th>
                <th className="text-center align-middle orders-adjust-col">Adjust</th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleOrderHeaderClick('price')}
                  className="user-select-none text-end"
                >
                  Price {renderOrderSortIndicator('price')}
                </th>
                <th 
                  style={{ cursor: 'pointer' }} 
                  onClick={() => handleOrderHeaderClick('total')}
                  className="user-select-none text-end"
                >
                  Total {renderOrderSortIndicator('total')}
                </th>
                <th className="user-select-none text-end">Gain $</th>
                <th className="user-select-none text-end">Gain %</th>
                <th 
                  style={{ cursor: 'pointer' }} 
                  onClick={() => handleOrderHeaderClick('size')}
                  className="user-select-none text-end align-middle"
                >
                  Size {renderOrderSortIndicator('size')}
                </th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleOrderHeaderClick('order_date')}
                  className="user-select-none text-end orders-date-col"
                >
                  Date {renderOrderSortIndicator('order_date')}
                </th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleOrderHeaderClick('status')}
                  className="user-select-none text-end"
                >
                  Status {renderOrderSortIndicator('status')}
                </th>
                <th className="text-center align-middle orders-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedOrders.map(order => {
                const total = Number(order.price) * Number(order.quantity);
                const size = account?.standard_size ? (total / account.standard_size) : null;
                const normalizedAction = String(order.action).toUpperCase();
                const isSellOrder = normalizedAction === 'SELL';
                const isCommitted = order.status === 'COMMITTED' || order.status === 'COMMIT';
                const rowClass = isCommitted
                  ? 'order-row-commit'
                  : (isSellOrder ? 'order-row-sell' : 'order-row-buy');
                const isAdjusting = adjustingOrderId === order.order_id;
                const heldTicker = isSellOrder ? tickers.find(t => t.symbol === order.symbol) : null;
                const heldShares = isSellOrder
                  ? parseNumericValue(
                      heldTicker?.shares ?? heldTicker?.current_shares ?? heldTicker?.quantity ?? null
                    )
                  : null;
                const numericStandardSize = parseNumericValue(account?.standard_size);
                const orderPrice = parseNumericValue(order.price);
                const canAdjust = isSellOrder
                  ? Number.isFinite(heldShares) && heldShares >= 1
                  : Number.isFinite(numericStandardSize) && numericStandardSize > 0 && Number.isFinite(orderPrice) && orderPrice > 0;
                const adjustButtonVariant = isSellOrder ? 'primary' : 'success';
                const mutedAdjustButtonVariant = 'outline-secondary';
                const activeAdjustButtonClass = isSellOrder ? 'adjust-button-sell' : 'adjust-button-buy';
                const getAdjustedQuantityForFraction = (fraction) => {
                  const fractionValue = Number(fraction);
                  if (!Number.isFinite(fractionValue) || fractionValue <= 0) {
                    return null;
                  }

                  if (isSellOrder) {
                    if (!Number.isFinite(heldShares) || heldShares < 1) {
                      return null;
                    }
                    const desiredQuantity = heldShares * fractionValue;
                    const maxQuantity = Math.floor(heldShares);
                    return Math.max(1, Math.min(maxQuantity, Math.floor(desiredQuantity)));
                  }

                  if (!Number.isFinite(numericStandardSize) || numericStandardSize <= 0 || !Number.isFinite(orderPrice) || orderPrice <= 0) {
                    return null;
                  }

                  let adjustedQuantity = Math.round((numericStandardSize * fractionValue) / orderPrice);
                  if (!Number.isFinite(adjustedQuantity) || adjustedQuantity < 1) {
                    adjustedQuantity = 1;
                  }
                  return adjustedQuantity;
                };
                const isFractionSelected = (fraction) => {
                  const adjustedQuantity = getAdjustedQuantityForFraction(fraction);
                  if (!Number.isFinite(adjustedQuantity)) {
                    return false;
                  }
                  const orderQuantity = Number(order.quantity);
                  if (!Number.isFinite(orderQuantity)) {
                    return false;
                  }
                  const tolerance = adjustedQuantity * 0.01;
                  return Math.abs(orderQuantity - adjustedQuantity) <= tolerance;
                };
                const adjustOptions = [
                  {
                    fraction: 1,
                    label: '1',
                    title: isSellOrder ? 'Adjust to full size' : 'Adjust to full standard size'
                  },
                  {
                    fraction: 0.5,
                    label: '1/2',
                    title: isSellOrder ? 'Adjust to 1/2 size' : 'Adjust to 1/2 standard size'
                  },
                  {
                    fraction: 0.25,
                    label: '1/4',
                    title: isSellOrder ? 'Adjust to 1/4 size' : 'Adjust to 1/4 standard size'
                  },
                  {
                    fraction: 0.125,
                    label: '1/8',
                    title: isSellOrder ? 'Adjust to 1/8 size' : 'Adjust to 1/8 standard size'
                  },
                  {
                    fraction: 0.0625,
                    label: '1/16',
                    title: isSellOrder ? 'Adjust to 1/16 size' : 'Adjust to 1/16 standard size'
                  },
                  {
                    fraction: 0.03125,
                    label: '1/32',
                    title: isSellOrder ? 'Adjust to 1/32 size' : 'Adjust to 1/32 standard size'
                  }
                ];
                // Compute Gain/Loss for SELL using current held cost_basis if available from tickers list
                let gainAmountCell = '-';
                let gainPctCell = '-';
                if (String(order.action).toUpperCase() === 'SELL') {
                  const held = tickers.find(t => t.symbol === order.symbol);
                  const cb = held?.cost_basis;
                  if (cb != null) {
                    const perShare = Number(order.price) - Number(cb);
                    const totalGL = perShare * Number(order.quantity);
                    const color = totalGL >= 0 ? 'text-success' : 'text-danger';
                    gainAmountCell = (
                      <span className={`fw-bold ${color}`}>
                        {`${totalGL >= 0 ? '' : '-'}$${Math.abs(totalGL).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      </span>
                    );
                    const totalCost = Number(cb) * Number(order.quantity);
                    if (totalCost > 0) {
                      const pct = (totalGL / totalCost) * 100;
                      gainPctCell = (
                        <span className={`fw-bold ${color}`}>
                          {`${pct >= 0 ? '' : '-'}${Math.abs(pct).toFixed(2)}%`}
                        </span>
                      );
                    }
                  }
                }
                return (
                  <tr key={order.order_id} className={rowClass}>
                    <td className="align-middle">{order.symbol}</td>
                    <td className="text-end align-middle orders-action-col">{order.action}</td>
                    <td className="text-end align-middle orders-table-quantity">{order.quantity}</td>
                    <td className="text-center align-middle orders-adjust-col">
                      <div className="btn-group btn-group-sm" role="group" aria-label="Adjust order size">
                        {adjustOptions.map((option, index) => {
                          const isSelected = isFractionSelected(option.fraction);
                          return (
                            <Button
                              key={option.fraction}
                              variant={isSelected ? adjustButtonVariant : mutedAdjustButtonVariant}
                              size="sm"
                              className={`${index < adjustOptions.length - 1 ? 'me-1 ' : ''}adjust-button ${isSelected ? activeAdjustButtonClass : 'adjust-button-muted'}`}
                              disabled={!canAdjust || isAdjusting}
                              onClick={() => handleAdjustOrderSize(order, option.fraction)}
                              title={option.title}
                            >
                              {isAdjusting ? '...' : option.label}
                            </Button>
                          );
                        })}
                      </div>
                    </td>
                    <td className="text-end align-middle orders-table-price">${Number(order.price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="text-end align-middle">${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="text-end align-middle">{gainAmountCell}</td>
                    <td className="text-end align-middle">{gainPctCell}</td>
                    <td className="text-end align-middle" style={{ color: '#0d6efd', fontWeight: 'bold' }}>{size !== null ? size.toFixed(2) : '-'}</td>
                    <td className="text-end align-middle orders-date-col">{order.order_date ? new Date(order.order_date).toLocaleDateString(undefined, { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }) : ''}</td>
                    <td className={"text-end align-middle" + (isCommitted ? ' text-success' : '')}>{order.status}</td>
                    <td className="text-center align-middle orders-actions-col">
                      <Button variant="outline-primary" size="sm" className="me-1" onClick={() => handleEditOrder(order)} title="Modify Order">
                        <i className="bi bi-pencil-square"></i>
                      </Button>
                      <Button variant="outline-success" size="sm" className="me-1" onClick={() => handleCommitOrder(order)} disabled={order.status !== 'PENDING'} title="Commit Order">
                        <i className="bi bi-check-circle"></i>
                      </Button>
                      <Button variant="outline-info" size="sm" className="me-1" onClick={() => handleFulfilledOrder(order)} disabled={!isCommitted} title="Mark as Fulfilled">
                        <i className="bi bi-check-square"></i>
                      </Button>
                      <Button variant="outline-warning" size="sm" className="me-1" onClick={() => openCancelledOrderModal(order)} title="Cancel Order">
                        <i className="bi bi-x-circle"></i>
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {/* Total row for Orders table */}
              {sortedOrders.length > 0 && (
                <tr style={{ background: '#f8f9fa', color: '#212529', fontWeight: 'bold' }}>
                  <td className="text-start align-middle">TOTAL ({sortedOrders.length})</td>
                  <td className="text-end align-middle"></td>
                  <td className="text-end align-middle"></td>
                  <td className="text-center align-middle"></td>
                  <td className="text-end align-middle"></td>
                  <td className="text-end align-middle">
                    {(() => {
                      const net = sortedOrders.reduce((sum, order) => {
                        const amt = Number(order.price) * Number(order.quantity);
                        return sum + (String(order.action).toUpperCase() === 'SELL' ? amt : -amt);
                      }, 0);
                      const sign = net < 0 ? '-' : '';
                      const absVal = Math.abs(net).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                      return `${sign}$${absVal}`;
                    })()}
                  </td>
                  <td className="text-end align-middle"></td>
                  <td className="text-end align-middle"></td>
                  <td className="text-end align-middle"></td>
                  <td className="text-end align-middle"></td>
                  <td className="text-end align-middle"></td>
                  <td className="text-center align-middle"></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Account Transactions Table */}
      <div className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">Transactions Today</h5>
          <div className="d-flex align-items-center">
            <span className="text-muted small me-3">
              Showing {filteredAccountTransactions.length}
              {filteredAccountTransactions.length !== accountTransactions.length
                ? ` of ${accountTransactions.length}`
                : ''}{' '}
              transaction{filteredAccountTransactions.length !== 1 ? 's' : ''}
            </span>
            <ButtonGroup className="me-2" size="sm" aria-label="Filter transactions by type">
              {['BUY', 'SELL', 'BOTH'].map(type => (
                <Button
                  key={type}
                  variant={transactionTypeFilter === type ? 'primary' : 'outline-primary'}
                  onClick={() => setTransactionTypeFilter(type)}
                  active={transactionTypeFilter === type}
                >
                  {type === 'BOTH' ? 'Both' : type.charAt(0) + type.slice(1).toLowerCase()}
                </Button>
              ))}
            </ButtonGroup>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={toggleTransactionsTodayVisibility}
              aria-expanded={showTransactionsToday}
              aria-controls="account-transactions-collapse"
            >
              {showTransactionsToday ? 'Hide' : 'Show'}
            </Button>
          </div>
        </div>
        <Collapse in={showTransactionsToday}>
          <div id="account-transactions-collapse">
            <div className="table-responsive">
              <style>{`
                /* Account transactions table styling - consistent with other tables */
                .account-transactions-table {
                  background-color: var(--card-bg) !important;
                  border: 2px solid var(--border-color) !important;
                  border-radius: 8px !important;
                  box-shadow: 0 4px 16px var(--shadow-color) !important;
                  overflow: hidden !important;
                  margin-bottom: 0 !important;
                }

                .account-transactions-table thead th {
                  background-color: var(--bg-secondary) !important;
                  border-bottom: 2px solid var(--border-color) !important;
                  font-weight: 600 !important;
                  color: var(--text-primary) !important;
                  padding: 6px 6px !important;
                  transition: background-color 0.2s ease-in-out;
                }

                .account-transactions-table thead th:hover {
                  background-color: var(--hover-bg) !important;
                  cursor: pointer;
                }

                [data-theme="light"] .account-transactions-table thead th {
                  background-color: #f8f9fa !important;
                  border-bottom: 2px solid #d1d5db !important;
                  color: #374151 !important;
                }

                [data-theme="light"] .account-transactions-table thead th:hover {
                  background-color: #e9ecef !important;
                }

                [data-theme="dark"] .account-transactions-table thead th {
                  background-color: #374151 !important;
                  border-bottom: 2px solid #4b5563 !important;
                  color: #f9fafb !important;
                }

                [data-theme="dark"] .account-transactions-table thead th:hover {
                  background-color: #4b5563 !important;
                }

                .account-transactions-table tbody tr {
                  border-bottom: 1px solid var(--border-color) !important;
                  transition: all 0.3s ease-in-out !important;
                  font-size: 0.95rem !important;
                }

                .account-transactions-table tbody tr:hover {
                  background-color: var(--hover-bg) !important;
                  transform: translateY(-2px) !important;
                  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
                  font-weight: 500 !important;
                  border-left: 3px solid #0d6efd !important;
                  border-right: 3px solid #0d6efd !important;
                }

                [data-theme="light"] .account-transactions-table tbody tr:hover {
                  background-color: #e3f2fd !important;
                  box-shadow: 0 4px 12px rgba(13, 110, 253, 0.2) !important;
                  border-left-color: #0d6efd !important;
                  border-right-color: #0d6efd !important;
                }

                [data-theme="dark"] .account-transactions-table tbody tr:hover {
                  background-color: #1e3a8a !important;
                  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3) !important;
                  border-left-color: #3b82f6 !important;
                  border-right-color: #3b82f6 !important;
                }

                .account-transactions-table tbody td {
                  padding: 4px 6px !important;
                  border-bottom: 1px solid var(--border-color) !important;
                  vertical-align: middle !important;
                  transition: all 0.3s ease-in-out !important;
                  word-wrap: break-word !important;
                  overflow-wrap: break-word !important;
                }

                .account-transactions-table tbody td:last-child {
                  width: 35% !important;
                  max-width: 35% !important;
                  min-width: 35% !important;
                }
            
                .account-transactions-table tbody tr:hover td {
                  color: var(--text-primary) !important;
                  font-weight: 500 !important;
                  transform: scale(1.02) !important;
                }

                [data-theme="light"] .account-transactions-table tbody td {
                  border-bottom: 1px solid #e5e7eb !important;
                }

                [data-theme="light"] .account-transactions-table tbody tr:hover td {
                  color: #1e40af !important;
                }

                [data-theme="dark"] .account-transactions-table tbody td {
                  border-bottom: 1px solid #374151 !important;
                }

                [data-theme="dark"] .account-transactions-table tbody tr:hover td {
                  color: #93c5fd !important;
                }

                /* Preserve semantic gain/loss colors when hovering rows */
                .account-transactions-table tbody tr:hover td.text-success,
                .account-transactions-table tbody tr:hover td .text-success,
                [data-theme="light"] .account-transactions-table tbody tr:hover td.text-success,
                [data-theme="light"] .account-transactions-table tbody tr:hover td .text-success,
                [data-theme="dark"] .account-transactions-table tbody tr:hover td.text-success,
                [data-theme="dark"] .account-transactions-table tbody tr:hover td .text-success {
                  color: #198754 !important;
                  font-weight: 700 !important;
                }

                .account-transactions-table tbody tr:hover td.text-danger,
                .account-transactions-table tbody tr:hover td .text-danger,
                [data-theme="light"] .account-transactions-table tbody tr:hover td.text-danger,
                [data-theme="light"] .account-transactions-table tbody tr:hover td .text-danger,
                [data-theme="dark"] .account-transactions-table tbody tr:hover td.text-danger,
                [data-theme="dark"] .account-transactions-table tbody tr:hover td .text-danger {
                  color: #dc3545 !important;
                  font-weight: 700 !important;
                }

                /* Set CSS custom properties for weather theme colors */
                .table-responsive {
                  --weather-bg-color: ${weatherColors.background};
                  --weather-border-color: ${weatherColors.border};
                  --weather-text-color: ${weatherColors.text};
                }

                /* Override CSS variables that might be used by existing styles */
                .table-responsive * {
                  --bg-secondary: var(--weather-bg-color) !important;
                }

                /* Weather theme theming for transactions table headers - using CSS variables and maximum specificity */
                html body div.table-responsive [data-theme="light"] .account-transactions-table thead th,
                html body div.table-responsive [data-theme="dark"] .account-transactions-table thead th,
                html body div.table-responsive .account-transactions-table thead th,
                html body .account-transactions-table thead th,
                html body table.account-transactions-table thead th {
                  background-color: var(--weather-bg-color) !important;
                  background: var(--weather-bg-color) !important;
                  border-color: var(--weather-border-color) !important;
                  color: var(--weather-text-color) !important;
                  border-bottom-color: var(--weather-border-color) !important;
                  border-bottom: 2px solid var(--weather-border-color) !important;
                  border-top-color: var(--weather-border-color) !important;
                  border-left-color: var(--weather-border-color) !important;
                  border-right-color: var(--weather-border-color) !important;
                }
              `}</style>
              {accountTransactionsLoading ? (
                <div className="text-center my-4"><Spinner animation="border" size="sm" /></div>
              ) : filteredAccountTransactions.length > 0 ? (
                <table className="table table-sm table-bordered mb-0 account-transactions-table" style={{ tableLayout: 'fixed' }}>
                  <thead>
                    <tr>
                      <th className="py-2" style={{ width: '17%' }} onClick={() => handleAccountTxSort('date')}>Date {renderAccountTxSortIndicator('date')}</th>
                      <th className="py-2" style={{ width: '5.65%' }} onClick={() => handleAccountTxSort('type')}>Type {renderAccountTxSortIndicator('type')}</th>
                      <th className="py-2" style={{ width: '9%' }} onClick={() => handleAccountTxSort('symbol')}>Sym {renderAccountTxSortIndicator('symbol')}</th>
                      <th className="py-2 text-end" style={{ width: '5.65%' }} onClick={() => handleAccountTxSort('shares')}>Shares {renderAccountTxSortIndicator('shares')}</th>
                      <th className="py-2 text-end" style={{ width: '9%' }} onClick={() => handleAccountTxSort('price')}>Price {renderAccountTxSortIndicator('price')}</th>
                      <th className="py-2 text-end" style={{ width: '12.5%' }} onClick={() => handleAccountTxSort('total')}>Total {renderAccountTxSortIndicator('total')}</th>
                      <th className="py-2 text-end" style={{ width: '10%' }} onClick={() => handleAccountTxSort('gain')}>Gain $ {renderAccountTxSortIndicator('gain')}</th>
                      <th className="py-2 text-end" style={{ width: '10%' }} onClick={() => handleAccountTxSort('gain_pct')}>Gain % {renderAccountTxSortIndicator('gain_pct')}</th>
                      <th className="py-2" style={{ width: '20.4%' }} onClick={() => handleAccountTxSort('notes')}>Notes {renderAccountTxSortIndicator('notes')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedAccountTransactions().map(transaction => {
                      const isCashTransaction = transaction.transaction_type === 'CASH';
                      const sharesValue = transaction.numericShares != null
                        ? transaction.numericShares
                        : (transaction.shares != null && Number.isFinite(Number(transaction.shares)) ? Number(transaction.shares) : 0);
                      const priceValue = transaction.numericPrice != null
                        ? transaction.numericPrice
                        : (transaction.price_per_share != null && Number.isFinite(Number(transaction.price_per_share))
                          ? Number(transaction.price_per_share)
                          : 0);
                      const cashAmountClass = isCashTransaction
                        ? (sharesValue >= 0 ? 'text-success fw-bold' : 'text-danger fw-bold')
                        : '';
                      const badgeVariant = isCashTransaction
                        ? (sharesValue >= 0 ? 'success' : 'danger')
                        : transaction.transaction_type === 'BUY'
                          ? 'success'
                          : transaction.transaction_type === 'SELL'
                            ? 'danger'
                            : 'info';
                      const badgeLabel = isCashTransaction
                        ? (sharesValue >= 0 ? 'DEPOSIT' : 'WITHDRAW')
                        : transaction.transaction_type;

                      return (
                        <tr key={transaction.transaction_id}>
                          <td className="py-1">{formatTransactionDate(transaction.normalizedDate || transaction.raw_date || transaction.transaction_date)}</td>
                          <td className="py-1">
                            <Badge bg={badgeVariant}>
                              {badgeLabel}
                            </Badge>
                          </td>
                          <td className="py-1">{isCashTransaction ? <span className="text-muted">Cash</span> : <span>{transaction.ticker_symbol}</span>}</td>
                          <td className={`py-1 text-end ${cashAmountClass}`}>
                            {isCashTransaction ? (
                              formatTransactionCurrency(sharesValue)
                            ) : (
                              Number.isFinite(sharesValue)
                                ? (Number.isInteger(sharesValue) ? sharesValue : sharesValue.toFixed(2))
                                : '—'
                            )}
                          </td>
                          <td className="py-1 text-end">{isCashTransaction ? '—' : formatTransactionCurrency(priceValue)}</td>
                          <td className={`py-1 text-end ${cashAmountClass}`}>
                            {formatTransactionCurrency(isCashTransaction ? sharesValue : sharesValue * priceValue)}
                          </td>
                        {(() => {
                          // Only SELL transactions have realized gain
                          if (transaction.transaction_type !== 'SELL') {
                            return (
                              <>
                                <td className="py-1 text-end text-muted">—</td>
                                <td className="py-1 text-end text-muted">—</td>
                              </>
                            );
                          }
                          // Prefer backend-provided realized gain; fallback to local calc if missing
                          const backendAmt = transaction.realized_gain_amount;
                          const backendPct = transaction.realized_gain_percent;
                          let gainAmt = (backendAmt !== null && backendAmt !== undefined) ? Number(backendAmt) : null;
                          let gainPct = (backendPct !== null && backendPct !== undefined) ? Number(backendPct) : null;
                          if (gainAmt === null || gainPct === null) {
                            const t = tickers.find(tt => tt.symbol === transaction.ticker_symbol);
                            const cb = t && t.cost_basis != null ? Number(t.cost_basis) : null;
                            if (cb == null) {
                              return (
                                <>
                                  <td className="py-1 text-end text-muted">—</td>
                                  <td className="py-1 text-end text-muted">—</td>
                                </>
                              );
                            }
                            const price = Number.isFinite(priceValue) ? priceValue : Number(transaction.price_per_share);
                            const shares = Number.isFinite(sharesValue) ? sharesValue : Number(transaction.shares);
                            const perShare = price - cb;
                            gainAmt = perShare * shares;
                            gainPct = cb > 0 ? (perShare / cb) * 100 : 0;
                          }
                          const amtCls = gainAmt >= 0 ? 'text-success fw-bold' : 'text-danger fw-bold';
                          const pctCls = gainPct >= 0 ? 'text-success fw-bold' : 'text-danger fw-bold';
                          return (
                            <>
                              <td className={`py-1 text-end ${amtCls}`}>{formatTransactionCurrency(gainAmt)}</td>
                              <td className={`py-1 text-end ${pctCls}`}>{`${gainPct >= 0 ? '+' : ''}${Number(gainPct).toFixed(2)}%`}</td>
                            </>
                          );
                        })()}
                        <td className="py-1">{(() => {
                          const cleanNotes = (transaction.notes || '').split('\n').filter(ln => !ln.startsWith('[RG]')).join('\n').trim();
                          if (!cleanNotes) {
                            return <span className="text-muted">—</span>;
                          }

                          return (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip
                                  id={`tooltip-notes-${transaction.transaction_id}`}
                                  style={{ maxWidth: '300px', wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}
                                >
                                  {cleanNotes}
                                </Tooltip>
                              }
                            >
                              <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{cleanNotes}</span>
                            </OverlayTrigger>
                          );
                        })()}</td>
                      </tr>
                    );
                  })}
                    {/* Totals Row */}
                    {filteredAccountTransactions && filteredAccountTransactions.length > 0 && (
                      <tr style={{ borderTop: '2px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
                        <td className="py-2 fw-bold">Total({filteredAccountTransactions.length})</td>
                        <td className="py-2"></td>
                        <td className="py-2"></td>
                        <td className="py-2"></td>
                        <td className="py-2"></td>
                        <td className="py-2 text-end fw-bold">
                          {formatTransactionCurrency(
                            filteredAccountTransactions.reduce((sum, tx) => {
                              const sharesVal = tx.numericShares != null
                                ? tx.numericShares
                                : (tx.shares != null && Number.isFinite(Number(tx.shares)) ? Number(tx.shares) : 0);
                              const priceVal = tx.numericPrice != null
                                ? tx.numericPrice
                                : (tx.price_per_share != null && Number.isFinite(Number(tx.price_per_share)) ? Number(tx.price_per_share) : 0);
                              const txTotal = tx.transaction_type === 'CASH' ? sharesVal : sharesVal * priceVal;
                              return sum + (Number.isFinite(txTotal) ? txTotal : 0);
                            }, 0)
                          )}
                        </td>
                        <td className="py-2 text-end fw-bold">
                          {(() => {
                          const totalGain = filteredAccountTransactions.reduce((sum, tx) => {
                            if (tx.transaction_type !== 'SELL') return sum;
                            const backendAmt = tx.realized_gain_amount;
                            let gainAmt = (backendAmt !== null && backendAmt !== undefined) ? Number(backendAmt) : null;
                            if (gainAmt === null) {
                              const t = tickers.find(tt => tt.symbol === tx.ticker_symbol);
                              const cb = t && t.cost_basis != null ? Number(t.cost_basis) : null;
                              if (cb == null) return sum;
                              const price = tx.numericPrice != null ? tx.numericPrice : Number(tx.price_per_share);
                              const shares = tx.numericShares != null ? tx.numericShares : Number(tx.shares);
                              gainAmt = (price - cb) * shares;
                            }
                            return sum + gainAmt;
                          }, 0);
                            const totalGainClass = totalGain >= 0 ? 'text-success' : 'text-danger';
                            return <span className={totalGainClass}>{formatTransactionCurrency(totalGain)}</span>;
                          })()}
                        </td>
                        <td className="py-2 text-end fw-bold">
                          {(() => {
                            let totalInvested = 0;
                            let totalGainAmount = 0;
                            filteredAccountTransactions.forEach(tx => {
                              if (tx.transaction_type === 'SELL') {
                                const backendAmt = tx.realized_gain_amount;
                                let gainAmt = (backendAmt !== null && backendAmt !== undefined) ? Number(backendAmt) : null;
                                if (gainAmt === null) {
                                  const t = tickers.find(tt => tt.symbol === tx.ticker_symbol);
                                  const cb = t && t.cost_basis != null ? Number(t.cost_basis) : null;
                                  if (cb != null) {
                                    const price = tx.numericPrice != null ? tx.numericPrice : Number(tx.price_per_share);
                                    const shares = tx.numericShares != null ? tx.numericShares : Number(tx.shares);
                                    gainAmt = (price - cb) * shares;
                                    totalInvested += cb * shares;
                                  }
                                } else {
                                  // If we have backend gain, calculate invested amount from sell price - gain
                                  const shares = tx.numericShares != null ? tx.numericShares : Number(tx.shares);
                                  const price = tx.numericPrice != null ? tx.numericPrice : Number(tx.price_per_share);
                                  const sellValue = shares * price;
                                  totalInvested += sellValue - gainAmt;
                                }
                                if (gainAmt !== null) totalGainAmount += gainAmt;
                              }
                            });
                            const avgGainPct = totalInvested > 0 ? (totalGainAmount / totalInvested) * 100 : 0;
                            const pctClass = avgGainPct >= 0 ? 'text-success' : 'text-danger';
                            return <span className={pctClass}>{`${avgGainPct >= 0 ? '+' : ''}${avgGainPct.toFixed(2)}%`}</span>;
                          })()}
                        </td>
                        <td className="py-2"></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              ) : accountTransactions.length > 0 ? (
                <div className="text-center py-4"><p className="text-muted">No transactions match the selected filter.</p></div>
              ) : (
                <div className="text-center py-4"><p className="text-muted">No transactions found for today.</p></div>
              )}
            </div>
          </div>
        </Collapse>
      </div>
      
      {/* Empty state when no tickers */}
      {!tableLoading && tickers.length === 0 && (
        <div className="text-center my-5">
          <p className="text-muted">No securities in this account yet.</p>
        </div>
      )}

      {/* TransactionModal with initialData prop */}
      <TransactionModal
        show={showTransactionModal}
        onHide={() => {
          setShowTransactionModal(false);
          setTransactionInitialData(null); // Clear initial data when modal is closed
          setFulfilledOrderObj(null); // Clear fulfilled order when modal is closed
        }}
        accountId={accountId}
        currentCash={account?.cash || 0}
        availableTickers={availableTickers}
        setAvailableTickers={setAvailableTickers}
        onTransactionComplete={handleTransactionCompleteWithFulfill}
        accountTickers={tickers}
        initialData={transactionInitialData}
      />
      {/* Cash Modal */}
      <Modal show={showCashModal} onHide={() => { setShowCashModal(false); resetCashForm(); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Cash</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Action</label>
            <div>
              <div className="btn-group" role="group">
                <button type="button" className={`btn btn-sm ${cashAction === 'deposit' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setCashAction('deposit')} disabled={cashSubmitting}>Deposit</button>
                <button type="button" className={`btn btn-sm ${cashAction === 'withdraw' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setCashAction('withdraw')} disabled={cashSubmitting}>Withdraw</button>
              </div>
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label">Amount</label>
            <input type="number" min="0" step="0.01" className="form-control" value={cashAmount} onChange={(e) => setCashAmount(e.target.value)} disabled={cashSubmitting} />
          </div>
          <div className="mb-3">
            <label className="form-label">Date</label>
            <input type="datetime-local" className="form-control" value={cashDate} onChange={(e) => setCashDate(e.target.value)} disabled={cashSubmitting} />
          </div>
          <div className="mb-3">
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows="2" value={cashNotes} onChange={(e) => setCashNotes(e.target.value)} disabled={cashSubmitting} />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowCashModal(false); resetCashForm(); }} disabled={cashSubmitting}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmitCash} disabled={cashSubmitting || !cashAmount || Number(cashAmount) <= 0}>
            {cashSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Pie Chart Modal */}
      <PieChartModal
        show={showPieChartModal}
        onHide={() => setShowPieChartModal(false)}
        tickers={tickers}
        cash={account?.cash || 0}
      />
      
      {/* Balance History Modal */}
      <BalanceHistoryModal
        show={showBalanceHistoryModal}
        onHide={() => setShowBalanceHistoryModal(false)}
        accountId={accountId}
        accountName={account?.name}
        portfolioName={account?.portfolio_name}
        currentBalance={calculateTotalBalance()}
        isBalanceLoading={tableLoading}
      />

      {/* Performance Modal */}
      <PerformanceChartModal
        show={showPerformanceModal}
        onHide={() => setShowPerformanceModal(false)}
        accountId={accountId}
        accountName={account?.name}
        portfolioName={account?.portfolio_name}
        currentBalance={calculateTotalBalance()}
        isBalanceLoading={tableLoading}
      />

      {/* Standard Size Modal */}
      <StandardSizeModal
        show={showStandardSize}
        onHide={() => setShowStandardSize(false)}
        accountId={accountId}
        currentStandardSize={account?.standard_size}
        defaultStandardSize={calculateDefaultStandardSize()}
        autoStandardSize={account?.auto_standard_size}
        onTickerUpdate={handleTickerUpdate}
        onStandardSizeUpdate={handleStandardSizeUpdate}
      />

      {/* Ticker Transaction History Modal */}
      <TickerTransactionHistoryModal
        show={showTickerHistoryModal}
        onHide={() => {
          setShowTickerHistoryModal(false);
          setSelectedTickerSymbol(null);
          setSelectedAccountId(null);
        }}
        tickerSymbol={selectedTickerSymbol}
        accountId={selectedAccountId}
        accountName={account?.name}
      />

      {/* Fixed position feedback message */}
      {historySaveMessage && (
        <div 
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1050,
            minWidth: '300px',
            maxWidth: '500px'
          }}
        >
          <Alert 
            variant={historySaveMessage.type} 
            className="py-2 shadow" 
            dismissible 
            onClose={() => setHistorySaveMessage('')}>
            {historySaveMessage.text}
          </Alert>
        </div>
      )}

      {/* Modify Edit Account Modal to include both name and description */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            Edit Account Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editError && (
            <Alert variant="danger">{editError}</Alert>
          )}
          <Form onSubmit={handleSaveEdit}>
            <Form.Group className="mb-3" controlId="editAccountName">
              <Form.Label>Account Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter account name"
                value={editAccountValue}
                onChange={(e) => setEditAccountValue(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="editAccountDescription">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter account description"
                value={editDescriptionValue}
                onChange={(e) => setEditDescriptionValue(e.target.value)}
              />
            </Form.Group>
            <div className="d-grid gap-2">
              <Button 
                variant="primary" 
                type="submit"
                disabled={editLoading}
              >
                {editLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Order Dialog */}
      <OrderDialog
        show={showOrderDialog}
        onHide={() => {
          setShowOrderDialog(false);
          setOrderDialogTicker(null); // Clear ticker when dialog is closed
          setOrderDialogError(''); // Clear error when dialog is closed
        }}
        onSave={handleSaveOrder}
        account={account}
        initialData={orderDialogInitial}
        loading={orderDialogLoading}
        availableCash={availableCashAfterOrders}
        tickers={tickers}
        availableTickers={availableTickers}
        ticker={orderDialogTicker?.symbol || ''} // Pass the ticker symbol for pre-filling
        preloadedTicker={orderDialogTicker} // Pass the full ticker object to preload current price
        defaultAction={orderDialogTicker?.forceSell ? 'SELL' : 'BUY'}
        error={orderDialogError} // Pass error message to display
      />



      {/* Cancelled Order Modal */}
      <Modal show={showCancelledModal} onHide={() => { setShowCancelledModal(false); setCancelledOrderObj(null); }} centered>
        <Modal.Header closeButton><Modal.Title>Cancel Order{cancelledOrderObj && cancelledOrderObj.symbol ? ` - ${cancelledOrderObj.symbol}` : ''}</Modal.Title></Modal.Header>
        <Modal.Body>
          {cancelledOrderObj && (
            <>
              <div style={{ fontWeight: 'bold' }}>
                {`${cancelledOrderObj.symbol}: ${cancelledOrderObj.quantity} x $${Number(cancelledOrderObj.price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/share = $${(Number(cancelledOrderObj.price) * Number(cancelledOrderObj.quantity)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              </div>
              <div>Are you sure to cancel this order?</div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowCancelledModal(false); setCancelledOrderObj(null); }}>Cancel</Button>
          <Button variant="danger" onClick={confirmCancelledOrder}>Cancel</Button>
        </Modal.Footer>
      </Modal>

      {/* Edge History Modal */}
      <EdgeHistoryModal
        show={showEdgeHistoryModal}
        onHide={() => setShowEdgeHistoryModal(false)}
        accountId={accountId}
        accountName={account?.name || account?.account_name}
        timePeriod={edgeHistoryTimePeriod}
        onTimePeriodChange={setEdgeHistoryTimePeriod}
        accounts={[{ account_id: accountId, name: account?.name || account?.account_name }]}
        onAccountChange={(newAccountId) => {
          // For Account Details page, we only have one account, so this is mostly for consistency
          logger.debug('Account changed to', newAccountId);
        }}
      />

      {/* Planning Modal */}
      <PlanningModal
        show={showPlanningModal}
        onHide={() => setShowPlanningModal(false)}
        account={account}
        tradingStats={tradingStats}
        defaultStatsPeriod={DEFAULT_TRADING_STATS_MONTHS}
        fetchTradingStats={fetchTradingStatsForMonths}
      />

      {/* Save as Default Confirmation Modal */}
      <Modal show={showSaveDefaultModal} onHide={() => setShowSaveDefaultModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Save Column Configuration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Save your current column configuration as your personal default?</p>
          <p className="mb-2">This will apply to all accounts and includes:</p>
          <ul className="mb-0">
            <li>{Object.values(visibleColumns).filter(Boolean).length} visible columns</li>
            <li>Current column order</li>
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSaveDefaultModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmSaveAsDefaults}>
            Save as Default
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default AccountDetails; 
